/**
 * Agent KB Manager Modal
 *
 * Provides a modal interface for managing agents:
 * - KB assignments (which knowledge bases the agent can access)
 * - Memory fields (structured data collection from users)
 * - Settings (system prompt, initial prompt, narrator mode)
 */

import { getAuthToken } from './auth.js';
import { showToast } from './toast.js';
import { escapeHtml } from './html-escape.js';
import { confirm } from './confirm-dialog.js';
import { fetchAllModels, getHeaders } from './api.js';
import { loadTasks, showTaskEditor, handleSaveTask, hideTaskEditor, hideTaskRuns } from './agent-tasks.js';
import { loadLoops, showLoopEditor, handleSaveLoop, hideLoopEditor, stopLoopsPolling } from './agent-loops.js';
import { openModelManagerForSelection } from './model-manager.js';
import { isContainerMode, state } from './state.js';

/**
 * Show a styled toast notification
 */

// State
let agents = [];
let allKBs = [];
let selectedAgent = null;
let assignedKBs = [];
let memoryFields = [];
let memoryValues = {};  // Stored values for memory fields
let agentSettings = {};
let currentView = 'list';  // 'list' | 'detail'
let currentTab = 'kbs';    // 'kbs' | 'memory' | 'settings'
let modalOpen = false;
let editingField = null;   // Field being edited (null for new)
let _autoSaveTimer = null;  // Debounce timer for auto-save
let _savePromise = null;    // Promise of the currently in-flight saveSettings() call (null when idle)
let agentImportFile = null;    // File for agent import dialog

// Database state
let dbTables = [];            // Schema tables from agent memory_schema
let dbSelectedTable = null;   // Currently viewing table key
let dbTableRows = [];         // Rows for selected table
let dbEnabled = false;        // Whether agent_db tool is enabled
let editingTable = null;      // Table being edited (null for new)
let editingDbRow = null;      // Row being edited (null for new)
// Which table that row belongs to. Not always the table on screen: a
// reference cell can open the row it points AT, in the table it lives in,
// without leaving the current one. Saving used the currently-viewed table,
// so editing a related row that way would have written the contact's fields
// into whichever table you happened to be looking at.
let editingDbTableKey = null;
let dbConfirmCallback = null; // Callback for confirm dialog
let dbCurrentPage = 1;        // Current page for row pagination
let importFile = null;        // File selected for import
let dbPageSize = 25;          // Rows per page (user-selectable)
let dbSortColumn = null;      // Column key to sort by (null = default order)
let dbSortDirection = null;   // 'asc' | 'desc' | null
// Horizontal scroll position of the data table, kept across re-renders.
// The table is rebuilt wholesale (innerHTML) on every edit, sort and page
// change, which resets scrollLeft to 0. On a wide table that threw the
// operator back to column one every time they saved a row -- so editing a
// value in the last column meant scrolling right again for every single edit.
// Keyed by table so switching tables does not inherit a meaningless offset.
let dbScrollLeft = Object.create(null);

// DOM elements cache
const elements = {};

/**
 * Initialize the Agent KB Manager
 */
export function initAgentKBManager() {
    createModalHTML();
    cacheElements();
    setupEventListeners();
}

/**
 * Create the modal HTML structure
 */
function createModalHTML() {
    const modalHTML = `
        <div id="agent-kb-manager-overlay" class="modal-overlay"></div>
        <div id="agent-kb-manager-panel" class="kb-manager-panel">
            <div class="kb-manager-header">
                <div class="kb-manager-nav">
                    <button id="agent-kb-back-btn" class="kb-back-btn hidden">← Back</button>
                    <h2 id="agent-kb-title">Manage Agents</h2>
                </div>
                <div class="kb-window-btns">
                    <button id="agent-kb-maximize" class="kb-window-btn" title="Maximize" aria-label="Maximize">
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                            <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                        </svg>
                    </button>
                    <button id="agent-kb-minimize" class="kb-window-btn" title="Restore to normal size" aria-label="Restore to normal size" disabled>
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
                            <line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
                        </svg>
                    </button>
                    <button id="agent-kb-close" class="modal-close">&times;</button>
                </div>
            </div>

            <!-- List View -->
            <div id="agent-kb-list-view" class="kb-view">
                <div class="kb-manager-toolbar">
                    <input type="text" id="agent-kb-search" class="kb-search" placeholder="Search agents...">
                    <button id="import-agent-btn" class="kb-import-btn" title="Import Agent">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        <span class="btn-label">Import</span>
                    </button>
                    <button id="create-agent-btn" class="kb-create-btn" title="New Agent">+</button>
                </div>
                <div id="agent-kb-list" class="kb-list"></div>
            </div>

            <!-- Detail View -->
            <div id="agent-kb-detail-view" class="kb-view hidden">
                <!-- Tabs -->
                <div class="agent-detail-tabs">
                    <button class="agent-tab active" data-tab="kbs">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
                        </svg>
                        Knowledge Bases
                    </button>
                    <button class="agent-tab" data-tab="memory">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M12 6v6l4 2"/>
                        </svg>
                        Memory
                    </button>
                    <button class="agent-tab" data-tab="settings">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="3"/>
                            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
                        </svg>
                        Settings
                    </button>
                    <button class="agent-tab" data-tab="tasks">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        Tasks
                    </button>
                    <button class="agent-tab" data-tab="loops">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 12a9 9 0 1 0 3-6.7"/>
                            <polyline points="3 4 3 10 9 10"/>
                        </svg>
                        Loops
                    </button>
                </div>

                <!-- KB Tab Content -->
                <div id="agent-tab-kbs" class="agent-tab-content">
                    <div class="kb-assignment-section">
                        <h3>Assigned KBs</h3>
                        <div id="agent-assigned-kbs" class="kb-assignment-list"></div>
                    </div>
                    <div class="kb-assignment-section">
                        <h3>Available KBs</h3>
                        <div id="agent-available-kbs" class="kb-assignment-list"></div>
                    </div>
                </div>

                <!-- Memory Tab Content -->
                <div id="agent-tab-memory" class="agent-tab-content hidden">
                    <!-- System Prompt Accordion -->
                    <div class="settings-accordion" id="system-prompt-accordion">
                        <button type="button" class="settings-accordion-toggle">
                            <span class="settings-accordion-title">System Prompt</span>
                            <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                 fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                        <div class="settings-accordion-body">
                            <div class="memory-system-prompt-section">
                                <p class="memory-fields-intro-text">Define the agent's personality and behavior</p>
                                <textarea id="setting-system-prompt" placeholder="You are a helpful assistant..." rows="6"></textarea>
                                <span id="system-prompt-status" class="system-prompt-status"></span>
                                <div class="skill-import-row">
                                    <button type="button" id="skill-import-btn" class="skill-import-btn" title="Import from SKILL.md">
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                                        </svg>
                                        Import Skill
                                    </button>
                                    <input type="file" id="skill-import-file" accept=".md" hidden>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Memory Fields Accordion -->
                    <div class="settings-accordion" id="memory-fields-accordion">
                        <button type="button" class="settings-accordion-toggle">
                            <span class="settings-accordion-title">Memory Fields</span>
                            <span class="settings-accordion-subtitle" id="memory-fields-accordion-subtitle"></span>
                            <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                 fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                        <div class="settings-accordion-body">
                            <div class="memory-fields-header">
                                <div class="memory-fields-intro">
                                    <p>Define structured fields the agent extracts from conversation and persists across sessions. Use for user profiling, preference tracking, or injecting dynamic context into prompts.</p>
                                </div>
                                <button id="add-field-btn" class="kb-create-btn">+ Add Field</button>
                            </div>
                            <div id="memory-fields-list" class="memory-fields-list"></div>
                        </div>
                    </div>

                    <!-- Agent Database Accordion -->
                    <div class="settings-accordion" id="db-accordion">
                        <button type="button" class="settings-accordion-toggle">
                            <span class="settings-accordion-title">Agent Database Tool</span>
                            <span class="settings-accordion-subtitle" id="db-accordion-subtitle"></span>
                            <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                 fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                        <div class="settings-accordion-body">
                            <div class="db-container">
                                <label class="setting-toggle compact">
                                    <input type="checkbox" id="db-enable-toggle">
                                    <span class="toggle-slider"></span>
                                    <span class="toggle-label">
                                        <strong>Enable Database</strong>
                                        <small>Give agent structured data storage via the agent_db tool</small>
                                    </span>
                                </label>
                                <div class="db-default-on-row" id="db-default-on-row" style="display:none">
                                    <span class="config-label">Default on in chat menu</span>
                                    <label class="config-toggle">
                                        <input type="checkbox" id="db-default-on" checked>
                                        <span class="config-toggle-slider"></span>
                                    </label>
                                </div>
                                <div id="db-content" class="db-content hidden">
                                    <div id="db-table-list" class="db-table-list"></div>
                                    <div class="db-action-buttons">
                                        <button id="db-create-table-btn" class="db-add-table-btn">+ Table</button>
                                        <button id="db-import-recording-btn" class="db-add-table-btn db-import-recording-btn">+ Recording</button>
                                    </div>
                                    <div id="db-table-detail" class="db-table-detail hidden"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Settings Tab Content -->
                <div id="agent-tab-settings" class="agent-tab-content hidden">
                    <div class="agent-settings-form">
                        <!-- General Settings Accordion -->
                        <div class="settings-accordion" id="general-settings-accordion">
                            <button type="button" class="settings-accordion-toggle">
                                <span class="settings-accordion-title">General Settings</span>
                                <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                     fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            <div class="settings-accordion-body">
                                <div class="general-settings-grid">
                                    <label class="setting-toggle compact">
                                        <input type="checkbox" id="setting-initial-prompt">
                                        <span class="toggle-slider"></span>
                                        <span class="toggle-label">
                                            <strong>Initial Prompt</strong>
                                            <small>Generate a context-aware opening message when loaded</small>
                                        </span>
                                    </label>

                                    <label class="setting-toggle compact sub-setting" id="autoplay-initial-prompt-row">
                                        <input type="checkbox" id="setting-autoplay-initial-prompt">
                                        <span class="toggle-slider"></span>
                                        <span class="toggle-label">
                                            <strong>Auto-Play Initial Prompt</strong>
                                            <small>Read the opening message aloud (preloads audio before streaming)</small>
                                        </span>
                                    </label>

                                    <div class="general-setting-item" id="gs-temperature">
                                        <div class="general-setting-header">
                                            <div class="general-setting-info">
                                                <strong>Temperature</strong>
                                                <small>Controls randomness. Lower = precise, higher = creative.</small>
                                            </div>
                                            <label class="override-toggle">
                                                <input type="checkbox" id="gs-temperature-override" class="override-checkbox">
                                                <span class="override-slider"></span>
                                            </label>
                                        </div>
                                        <div class="general-setting-control">
                                            <input type="range" id="gs-temperature-slider" min="0" max="2" step="0.1" value="0.7">
                                            <span class="slider-value" id="gs-temperature-value">0.7</span>
                                        </div>
                                        <div class="slider-labels"><span>Precise</span><span>Creative</span></div>
                                        <div class="override-default-label" id="gs-temperature-default">Using admin default</div>
                                    </div>

                                    <div class="general-setting-item" id="gs-memory">
                                        <div class="general-setting-header">
                                            <div class="general-setting-info">
                                                <strong>Memory</strong>
                                                <small>Off: no context. Basic: recent messages. Full: messages + fact extraction.</small>
                                            </div>
                                            <label class="override-toggle">
                                                <input type="checkbox" id="gs-memory-override" class="override-checkbox">
                                                <span class="override-slider"></span>
                                            </label>
                                        </div>
                                        <div class="general-setting-control">
                                            <input type="range" id="gs-memory-slider" min="0" max="2" step="1" value="2">
                                            <span class="slider-value" id="gs-memory-value">Full</span>
                                        </div>
                                        <div class="slider-labels"><span>Off</span><span>Basic</span><span>Full</span></div>
                                        <div class="override-default-label" id="gs-memory-default">Using admin default</div>
                                    </div>

                                    <div class="general-setting-item" id="gs-fidelity">
                                        <div class="general-setting-header">
                                            <div class="general-setting-info">
                                                <strong>Fidelity</strong>
                                                <small>How strictly the LLM sticks to KB sources. Open allows general knowledge.</small>
                                            </div>
                                            <label class="override-toggle">
                                                <input type="checkbox" id="gs-fidelity-override" class="override-checkbox">
                                                <span class="override-slider"></span>
                                            </label>
                                        </div>
                                        <div class="general-setting-control">
                                            <input type="range" id="gs-fidelity-slider" min="0" max="1" step="0.1" value="0.7">
                                            <span class="slider-value" id="gs-fidelity-value">0.7</span>
                                        </div>
                                        <div class="slider-labels"><span>Open</span><span>Strict</span></div>
                                        <div class="override-default-label" id="gs-fidelity-default">Using admin default</div>
                                    </div>

                                    <div class="general-setting-item" id="gs-kb-threshold">
                                        <div class="general-setting-header">
                                            <div class="general-setting-info">
                                                <strong>KB Threshold</strong>
                                                <small>Minimum similarity score for KB results. Higher = stricter matching.</small>
                                            </div>
                                            <label class="override-toggle">
                                                <input type="checkbox" id="gs-kb-threshold-override" class="override-checkbox">
                                                <span class="override-slider"></span>
                                            </label>
                                        </div>
                                        <div class="general-setting-control">
                                            <input type="range" id="gs-kb-threshold-slider" min="0" max="1" step="0.05" value="0.45">
                                            <span class="slider-value" id="gs-kb-threshold-value">0.45</span>
                                        </div>
                                        <div class="slider-labels"><span>Broad</span><span>Strict</span></div>
                                        <div class="override-default-label" id="gs-kb-threshold-default">Using admin default (0.45)</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Teaching Accordion -->
                        <div class="settings-accordion" id="teaching-accordion">
                            <button type="button" class="settings-accordion-toggle">
                                <span class="settings-accordion-title">Narration</span>
                                <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                     fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            <div class="settings-accordion-body">
                                <div class="tool-settings-grid">
                                    <label class="setting-toggle">
                                        <input type="checkbox" id="setting-narrator-mode">
                                        <span class="toggle-slider"></span>
                                        <span class="toggle-label">
                                            <strong>Narrator Mode</strong>
                                            <small>Present KB content sequentially chunk by chunk</small>
                                        </span>
                                    </label>
                                    <label class="setting-toggle">
                                        <input type="checkbox" id="setting-narrator-verbatim">
                                        <span class="toggle-slider"></span>
                                        <span class="toggle-label">
                                            <strong>Verbatim</strong>
                                            <small>Output raw chunk text without LLM narration</small>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Agent Tools Accordion -->
                        <div class="settings-accordion" id="tools-accordion">
                            <button type="button" class="settings-accordion-toggle">
                                <span class="settings-accordion-title">Agent Tools</span>
                                <span class="settings-accordion-subtitle" id="tools-accordion-subtitle">0 enabled</span>
                                <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                     fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            <div class="settings-accordion-body">
                                <div class="tool-cards-grid" id="tool-cards-grid">
                                    <div class="tool-card" data-tool="vision" id="tool-card-vision">
                                        <input type="checkbox" id="tool-vision" value="vision" class="tool-card-checkbox">
                                        <span class="tool-llm-badge" title="Uses a local vision model">LLM</span>
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Vision</div>
                                        <div class="tool-card-desc">Auto-describe pasted images with local vision model</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="text_cleanup" id="tool-card-text-cleanup">
                                        <input type="checkbox" id="tool-text-cleanup" value="text_cleanup" class="tool-card-checkbox">
                                        <span class="tool-llm-badge" title="Uses a local text cleanup model">LLM</span>
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M4 7h7M4 12h11M4 17h7"/><polyline points="15 14 17 16 21 12"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Proofread</div>
                                        <div class="tool-card-desc">Auto-fix spelling, grammar & voice errors</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="doc_create" id="tool-card-doccreate">
                                        <input type="checkbox" id="tool-doccreate" value="doc_create" class="tool-card-checkbox">
                                        <span class="tool-llm-badge" title="Uses a local intent detection model">LLM</span>
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Doc Create</div>
                                        <div class="tool-card-desc">Auto-create downloadable documents from chat responses</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="calculator" id="tool-card-calculator">
                                        <input type="checkbox" id="tool-calculator" value="calculator" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <rect x="4" y="2" width="16" height="20" rx="2"/><rect x="7" y="5" width="10" height="5" rx="1"/><circle cx="7.5" cy="14" r=".5" fill="currentColor"/><circle cx="12" cy="14" r=".5" fill="currentColor"/><circle cx="16.5" cy="14" r=".5" fill="currentColor"/><circle cx="7.5" cy="18" r=".5" fill="currentColor"/><circle cx="12" cy="18" r=".5" fill="currentColor"/><circle cx="16.5" cy="18" r=".5" fill="currentColor"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Calculator</div>
                                        <div class="tool-card-desc">Evaluate math expressions (pattern-matched)</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="datetime" id="tool-card-datetime">
                                        <input type="checkbox" id="tool-datetime" value="datetime" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Date/Time</div>
                                        <div class="tool-card-desc">Get current date and time (pattern-matched)</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="python_exec" id="tool-card-python">
                                        <input type="checkbox" id="tool-python" value="python_exec" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><line x1="14" y1="4" x2="10" y2="20"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Python</div>
                                        <div class="tool-card-desc">Run Python code in sandbox (LLM-decided)</div>
                                        <span class="tool-risk-badge risk-code">code</span>
                                    </div>
                                    <div class="tool-card" data-tool="shell_exec" id="tool-card-shell">
                                        <input type="checkbox" id="tool-shell" value="shell_exec" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <rect x="2" y="3" width="20" height="18" rx="2"/><polyline points="7 9 10 12 7 15"/><line x1="13" y1="15" x2="17" y2="15"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Shell</div>
                                        <div class="tool-card-desc">Run shell commands in sandbox (LLM-decided)</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="file_ops" id="tool-card-fileops">
                                        <input type="checkbox" id="tool-fileops" value="file_ops" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">File Ops</div>
                                        <div class="tool-card-desc">Read/write files in allowed directories</div>
                                        <span class="tool-risk-badge risk-write">write</span>
                                    </div>
                                    <div class="tool-card" data-tool="browser_check" id="tool-card-browsercheck">
                                        <input type="checkbox" id="tool-browsercheck" value="browser_check" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/><path d="m9 8 3 3 4-5"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Browser Check</div>
                                        <div class="tool-card-desc">Load a web app headlessly, report errors, test interactions</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="web_fetch" id="tool-card-webfetch">
                                        <input type="checkbox" id="tool-webfetch" value="web_fetch" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Web Fetch</div>
                                        <div class="tool-card-desc">Fetch content from URLs (LLM-decided)</div>
                                        <span class="tool-risk-badge risk-external">external</span>
                                    </div>
                                    <div class="tool-card" data-tool="http_request" id="tool-card-httprequest">
                                        <input type="checkbox" id="tool-httprequest" value="http_request" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">HTTP Request</div>
                                        <div class="tool-card-desc">Call any REST API (GET/POST/PUT/DELETE)</div>
                                        <span class="tool-risk-badge risk-external">external</span>
                                    </div>
                                    <div class="tool-card" data-tool="email_send" id="tool-card-emailsend">
                                        <input type="checkbox" id="tool-emailsend" value="email_send" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Email Send</div>
                                        <div class="tool-card-desc">Send emails via SMTP</div>
                                        <span class="tool-risk-badge risk-external">external</span>
                                    </div>
                                    <div class="tool-card" data-tool="email_fetch" id="tool-card-emailfetch">
                                        <input type="checkbox" id="tool-emailfetch" value="email_fetch" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/><circle cx="17.5" cy="17.5" r="3.5"/><line x1="20" y1="20" x2="22.5" y2="22.5"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Email Fetch</div>
                                        <div class="tool-card-desc">Read &amp; search email via IMAP</div>
                                        <span class="tool-risk-badge risk-external">external</span>
                                    </div>
                                    <div class="tool-card" data-tool="image_generate" id="tool-card-imagegen">
                                        <input type="checkbox" id="tool-imagegen" value="image_generate" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Image Gen</div>
                                        <div class="tool-card-desc">Generate images from text prompts</div>
                                        <span class="tool-risk-badge risk-external">external</span>
                                    </div>
                                    <div class="tool-card" data-tool="scholar" id="tool-card-scholar">
                                        <input type="checkbox" id="tool-scholar" value="scholar" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Scholar</div>
                                        <div class="tool-card-desc">Search academic papers, patents, legal docs & data</div>
                                        <span class="tool-risk-badge risk-external">external</span>
                                    </div>
                                    <div class="tool-card" data-tool="web_search" id="tool-card-websearch">
                                        <input type="checkbox" id="tool-websearch" value="web_search" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Web Search</div>
                                        <div class="tool-card-desc">Smart web search with optimized queries</div>
                                        <span class="tool-risk-badge risk-external">external</span>
                                    </div>
                                    <div class="tool-card" data-tool="rag_search" id="tool-card-ragsearch">
                                        <input type="checkbox" id="tool-ragsearch" value="rag_search" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/><circle cx="13" cy="10" r="3"/><line x1="15.5" y1="12.5" x2="17" y2="14"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Doc Search</div>
                                        <div class="tool-card-desc">Smart KB search with optimized queries</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="kb_embed" id="tool-card-kbembed">
                                        <input type="checkbox" id="tool-kbembed" value="kb_embed" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M12 3v12"/><path d="M8 11l4 4 4-4"/><rect x="4" y="17" width="16" height="4" rx="1"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">KB Embed</div>
                                        <div class="tool-card-desc">Embed conversation content into knowledge bases</div>
                                        <span class="tool-risk-badge risk-write">write</span>
                                    </div>
                                    <div class="tool-card" data-tool="recall" id="tool-card-recall">
                                        <input type="checkbox" id="tool-recall" value="recall" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 4 3 10 9 10"/><path d="M12 7v5l3 2"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Recall</div>
                                        <div class="tool-card-desc">Pull facts &amp; past responses from earlier conversations with this agent</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="synthesize" id="tool-card-synthesize">
                                        <input type="checkbox" id="tool-synthesize" value="synthesize" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M4 5h6"/><path d="M4 12h6"/><path d="M4 19h6"/><path d="M10 5c5 0 4 7 9 7"/><path d="M10 19c5 0 4-7 9-7"/><circle cx="20" cy="12" r="2"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Synthesize</div>
                                        <div class="tool-card-desc">Compile past conversations into one organized document (multi-minute)</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                    <div class="tool-card" data-tool="brain_query" id="tool-card-brainquery">
                                        <input type="checkbox" id="tool-brainquery" value="brain_query" class="tool-card-checkbox">
                                        <div class="tool-card-icon">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                                <ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5"/><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"/>
                                            </svg>
                                        </div>
                                        <div class="tool-card-name">Brains</div>
                                        <div class="tool-card-desc">Read-only SQL over the tables, memory fields and reminders of this agent and every agent it runs</div>
                                        <span class="tool-risk-badge risk-safe">safe</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Tool Settings Accordion -->
                        <div class="settings-accordion" id="tool-settings-accordion">
                            <button type="button" class="settings-accordion-toggle">
                                <span class="settings-accordion-title">Tool Settings</span>
                                <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                     fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            <div class="settings-accordion-body">
                                <div class="tool-settings-grid">
                                    <div class="tool-setting-item">
                                        <div class="tool-setting-header">
                                            <strong>Max Steps</strong>
                                            <small>Maximum tool calls per prompt (ReAct loop iterations)</small>
                                        </div>
                                        <div class="max-iterations-control">
                                            <input type="range" id="max-iterations-slider" min="1" max="20" value="5" step="1">
                                            <span id="max-iterations-value" class="slider-value">5</span>
                                        </div>
                                    </div>
                                    <div class="tool-setting-item">
                                        <div class="tool-setting-header">
                                            <strong>Tool Approval</strong>
                                            <small>Require user approval before running certain tools</small>
                                        </div>
                                        <select id="hitl-mode-select" class="tool-setting-select">
                                            <option value="none">None (auto-execute)</option>
                                            <option value="write_only">Approve writes & code</option>
                                            <option value="all">Approve all tools</option>
                                        </select>
                                    </div>
                                    <div class="tool-setting-item">
                                        <div class="tool-setting-header">
                                            <strong>Planning</strong>
                                            <small>LLM outlines its approach before executing tools</small>
                                        </div>
                                        <label class="setting-toggle compact">
                                            <input type="checkbox" id="setting-use-planning">
                                            <span class="toggle-slider"></span>
                                            <span class="toggle-label">
                                                <strong>Plan before executing</strong>
                                            </span>
                                        </label>
                                        <label class="setting-toggle compact" id="planning-default-on-row" style="display:none; margin-top: 6px; padding-left: 12px;">
                                            <input type="checkbox" id="setting-planning-default-on" checked>
                                            <span class="toggle-slider"></span>
                                            <span class="toggle-label">Default on in menu</span>
                                        </label>
                                    </div>
                                    <div class="tool-setting-item">
                                        <div class="tool-setting-header">
                                            <strong>Token Budget</strong>
                                            <small>Maximum tokens per tool loop turn (0 = unlimited)</small>
                                        </div>
                                        <div class="token-budget-control">
                                            <input type="range" id="token-budget-slider" min="1" max="26" step="1" value="26">
                                            <span class="slider-value" id="token-budget-value">&infin;</span>
                                            <button type="button" class="token-budget-custom-btn" id="token-budget-custom-btn" title="Set custom value">Custom</button>
                                        </div>
                                        <div class="token-budget-custom-row hidden" id="token-budget-custom-row">
                                            <input type="number" id="token-budget-custom-input" min="0" max="500000" step="1000" placeholder="e.g. 50000">
                                            <span class="token-budget-custom-suffix">tokens</span>
                                            <button type="button" class="token-budget-custom-apply" id="token-budget-custom-apply" title="Apply">
                                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            </button>
                                            <button type="button" class="token-budget-custom-cancel" id="token-budget-custom-cancel" title="Cancel">
                                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                            </button>
                                        </div>
                                        <input type="hidden" id="token-budget-input" value="0">
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Tool Chains -->
                        <div class="settings-accordion" id="tool-chains-accordion">
                            <button type="button" class="settings-accordion-toggle">
                                <span class="settings-accordion-title">Tool Chains</span>
                                <span class="settings-accordion-subtitle" id="tool-chains-subtitle">0 chains</span>
                                <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                     fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            <div class="settings-accordion-body">
                                <div class="tool-chains-section">
                                    <p class="tool-chains-desc">Define multi-step tool sequences that appear as a single tool to the LLM.</p>
                                    <div id="tool-chains-list"></div>
                                    <button type="button" class="tool-chain-add-btn" id="add-chain-btn">
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                        Add Chain
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Meta Agent -->
                        <div class="settings-accordion" id="meta-agent-accordion">
                            <button type="button" class="settings-accordion-toggle">
                                <span class="settings-accordion-title">Meta Agent</span>
                                <span class="settings-accordion-subtitle" id="meta-agent-subtitle"></span>
                                <svg class="settings-accordion-chevron" viewBox="0 0 24 24" width="16" height="16"
                                     fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            <div class="settings-accordion-body">
                                <div class="meta-section-block">
                                    <div class="tool-setting-header">
                                        <strong>Routing line</strong>
                                        <small>How a meta agent's router (Laya) sees THIS agent. A few words on what sets it apart; with a dozen specialists Laya reads only ~60 characters of each. Empty = the description.</small>
                                    </div>
                                    <input type="text" id="setting-routing-summary" maxlength="80" placeholder="e.g. builds world NPC notables (not PCs); Forge tasks" autocomplete="off">
                                </div>
                                <label class="setting-toggle compact">
                                    <input type="checkbox" id="setting-is-meta">
                                    <span class="toggle-slider"></span>
                                    <span class="toggle-label">
                                        <strong>Meta agent</strong>
                                        <small>Can delegate tasks to subordinate agents via the invoke_agent tool</small>
                                    </span>
                                </label>
                                <div id="meta-subordinates-section" style="display:none;">
                                    <div class="meta-section-block">
                                        <div class="tool-setting-header">
                                            <strong>Subordinate agents</strong>
                                            <small>Which agents this meta agent may delegate to. Their descriptions guide the routing — keep them sharp.</small>
                                        </div>
                                        <div id="meta-subordinates-list" class="meta-subordinates-list"></div>
                                    </div>
                                    <div class="meta-section-block">
                                        <div class="tool-setting-header">
                                            <strong>Spawn budgets</strong>
                                            <small>Hard caps enforced in code, per chat turn</small>
                                        </div>
                                        <div class="meta-budget-grid">
                                            <div class="meta-budget-item">
                                                <label for="meta-max-spawns">Max delegations</label>
                                                <input type="number" id="meta-max-spawns" min="1" value="3">
                                            </div>
                                            <div class="meta-budget-item">
                                                <label for="meta-max-spawn-tokens">Token budget <span>(0 = unlimited)</span></label>
                                                <input type="number" id="meta-max-spawn-tokens" min="0" value="0">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Settings Status -->
                        <div class="settings-actions">
                            <span id="settings-status" class="settings-status"></span>
                        </div>
                    </div>
                </div>

                <!-- Tasks Tab Content -->
                <div id="agent-tab-tasks" class="agent-tab-content hidden">
                    <div class="tasks-toolbar">
                        <button id="create-task-btn" class="kb-create-btn">+ New Task</button>
                    </div>
                    <div id="agent-tasks-list" class="tasks-list"></div>

                    <!-- Task Editor (inline, hidden by default) -->
                    <div id="task-editor" class="task-editor hidden">
                        <div class="task-editor-header">
                            <h3 id="task-editor-title">New Task</h3>
                            <button class="field-editor-close" id="task-editor-close">&times;</button>
                        </div>
                        <div class="task-editor-form">
                            <div class="field-row">
                                <label for="task-name">Task Name</label>
                                <input type="text" id="task-name" placeholder="e.g., Daily Summary" autocomplete="off">
                            </div>
                            <div class="field-row">
                                <label for="task-prompt">Prompt</label>
                                <textarea id="task-prompt" placeholder="What should the agent do?" rows="4"></textarea>
                            </div>
                            <div class="field-row">
                                <label>Model</label>
                                <div class="task-model-selector">
                                    <button type="button" id="task-model-btn" class="task-model-btn">Select Model</button>
                                    <span id="task-model-display" class="task-model-display"></span>
                                    <button type="button" id="task-model-clear" class="task-model-clear"></button>
                                    <input type="hidden" id="task-model" value="">
                                </div>
                            </div>
                            <div class="field-row">
                                <label>Schedule</label>
                                <div class="task-schedule-radios">
                                    <label class="task-radio"><input type="radio" name="task-schedule" value="once" checked> One-time</label>
                                    <label class="task-radio"><input type="radio" name="task-schedule" value="recurring"> Recurring</label>
                                </div>
                            </div>
                            <div class="field-row" id="task-run-at-row">
                                <label for="task-run-at">Run At</label>
                                <input type="datetime-local" id="task-run-at">
                            </div>
                            <div class="field-row hidden" id="task-interval-row">
                                <label>Interval</label>
                                <div class="task-interval-presets">
                                    <button type="button" class="task-interval-btn" data-seconds="300">5m</button>
                                    <button type="button" class="task-interval-btn" data-seconds="900">15m</button>
                                    <button type="button" class="task-interval-btn" data-seconds="3600">1h</button>
                                    <button type="button" class="task-interval-btn" data-seconds="21600">6h</button>
                                    <button type="button" class="task-interval-btn" data-seconds="86400">24h</button>
                                    <button type="button" class="task-interval-btn" data-seconds="604800">7d</button>
                                </div>
                                <div class="task-custom-interval">
                                    <input type="number" id="task-custom-interval" placeholder="Custom" min="1">
                                    <select id="task-custom-unit">
                                        <option value="60">minutes</option>
                                        <option value="3600">hours</option>
                                        <option value="86400">days</option>
                                    </select>
                                </div>
                            </div>
                            <div class="field-row">
                                <label>Search Level</label>
                                <div class="task-search-level">
                                    <button type="button" class="task-search-btn active" data-depth="off">Off</button>
                                    <button type="button" class="task-search-btn" data-depth="surface">Surface</button>
                                    <button type="button" class="task-search-btn" data-depth="shallow">Shallow</button>
                                    <button type="button" class="task-search-btn" data-depth="deep">Deep</button>
                                </div>
                            </div>
                            <div class="field-row">
                                <label>Tools & Capabilities</label>
                                <div id="task-chips-container" class="task-chips-container"></div>
                            </div>
                            <div class="field-row">
                                <label class="setting-toggle compact">
                                    <input type="checkbox" id="task-continue-session">
                                    <span class="toggle-slider"></span>
                                    <span class="toggle-label">
                                        <strong>Continue Session</strong>
                                        <small>Reuse the same conversation across runs</small>
                                    </span>
                                </label>
                            </div>
                        </div>
                        <div class="task-editor-footer">
                            <span class="task-editor-error" id="task-editor-error"></span>
                            <button id="task-editor-cancel" class="btn-secondary">Cancel</button>
                            <button id="task-editor-save" class="kb-create-btn">Save Task</button>
                        </div>
                    </div>

                    <!-- Task Run History (inline, hidden by default) -->
                    <div id="task-runs-panel" class="task-runs-panel hidden">
                        <div class="task-runs-header">
                            <button class="task-runs-back" id="task-runs-back">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
                                Back
                            </button>
                            <h3 id="task-runs-title">Run History</h3>
                        </div>
                        <div id="task-runs-list" class="task-runs-list"></div>
                    </div>
                </div>

                <!-- Loops Tab Content -->
                <div id="agent-tab-loops" class="agent-tab-content hidden">
                    <div class="tasks-toolbar" id="loops-toolbar">
                        <button id="create-loop-btn" class="kb-create-btn">+ New Loop</button>
                    </div>
                    <div id="agent-loops-list" class="tasks-list"></div>

                    <!-- Loop Editor (inline, hidden by default) -->
                    <div id="loop-editor" class="task-editor hidden">
                        <div class="task-editor-header">
                            <h3 id="loop-editor-title">New Loop</h3>
                            <button class="field-editor-close" id="loop-editor-close">&times;</button>
                        </div>
                        <div class="task-editor-form">
                            <div class="field-row">
                                <label for="loop-name">Loop Name</label>
                                <input type="text" id="loop-name" placeholder="e.g., Modularize main.js" autocomplete="off">
                            </div>
                            <div class="field-row">
                                <label for="loop-mission">Mission</label>
                                <textarea id="loop-mission" placeholder="The overall goal and what 'done' means. This stays fixed while the loop runs." rows="5"></textarea>
                            </div>
                            <div class="field-row">
                                <label for="loop-plan">Initial Plan (optional)</label>
                                <textarea id="loop-plan" placeholder="Optional starting plan. The loop maintains and updates this as it works." rows="3"></textarea>
                            </div>
                            <div class="field-row">
                                <label for="loop-verify-command">Verify Command (optional)</label>
                                <input type="text" id="loop-verify-command" placeholder="e.g., npm test — run after each iteration; exit 0 = pass" autocomplete="off">
                            </div>
                            <div class="field-row">
                                <label for="loop-working-dir">Working Directory (optional)</label>
                                <input type="text" id="loop-working-dir" placeholder="Directory the verify command runs in" autocomplete="off">
                            </div>
                            <div class="field-row">
                                <label>Worker Model</label>
                                <div class="task-model-selector">
                                    <button type="button" id="loop-model-btn" class="task-model-btn">Select Model</button>
                                    <span id="loop-model-display" class="task-model-display"></span>
                                    <button type="button" id="loop-model-clear" class="task-model-clear"></button>
                                    <input type="hidden" id="loop-model" value="">
                                </div>
                            </div>
                            <div class="field-row">
                                <label>Check Model <small>(reviews each iteration; defaults to worker model)</small></label>
                                <div class="task-model-selector">
                                    <button type="button" id="loop-check-model-btn" class="task-model-btn">Select Model</button>
                                    <span id="loop-check-model-display" class="task-model-display"></span>
                                    <button type="button" id="loop-check-model-clear" class="task-model-clear"></button>
                                    <input type="hidden" id="loop-check-model" value="">
                                </div>
                            </div>
                            <div class="field-row">
                                <label for="loop-max-iterations">Max Iterations</label>
                                <input type="number" id="loop-max-iterations" min="1" value="25">
                            </div>
                            <div class="field-row">
                                <label for="loop-max-tokens">Token Budget <small>(0 = unlimited)</small></label>
                                <input type="number" id="loop-max-tokens" min="0" value="0">
                            </div>
                            <div class="field-row">
                                <label for="loop-max-wall-minutes">Time Budget, minutes <small>(0 = unlimited)</small></label>
                                <input type="number" id="loop-max-wall-minutes" min="0" value="0">
                            </div>
                            <div class="field-row">
                                <label for="loop-pause-every-n">Pause for review every N iterations <small>(0 = never)</small></label>
                                <input type="number" id="loop-pause-every-n" min="0" value="0">
                            </div>
                            <div class="field-row">
                                <label for="loop-done-threshold">Consecutive green verifies to finish</label>
                                <input type="number" id="loop-done-threshold" min="1" value="2">
                            </div>
                        </div>
                        <div class="task-editor-footer">
                            <span class="task-editor-error" id="loop-editor-error"></span>
                            <button id="loop-editor-cancel" class="btn-secondary">Cancel</button>
                            <button id="loop-editor-save" class="kb-create-btn">Save Loop</button>
                        </div>
                    </div>

                    <!-- Live journal feed for the selected loop -->
                    <div id="loop-feed" class="loop-feed hidden">
                        <div class="loop-feed-header">
                            <span id="loop-feed-title" class="loop-feed-title"></span>
                            <span id="loop-feed-status" class="loop-feed-status"></span>
                        </div>
                        <div id="loop-feed-plan"></div>
                        <div id="loop-feed-list" class="loop-feed-list"></div>
                    </div>
                </div>
            </div>

            <!-- Copy Agent Dialog -->
            <div id="agent-copy-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content">
                    <h3 id="agent-copy-title">Copy Agent</h3>
                    <input type="text" id="copy-agent-name" placeholder="New agent name">
                    <label class="copy-data-toggle">
                        <input type="checkbox" id="copy-include-data">
                        <span>Include data (database rows and memories)</span>
                    </label>
                    <div class="kb-dialog-actions">
                        <button id="agent-copy-cancel" class="btn-secondary">Cancel</button>
                        <button id="agent-copy-confirm" class="kb-create-btn">Copy</button>
                    </div>
                </div>
            </div>

            <!-- Import Agent Dialog -->
            <div id="agent-import-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content field-editor">
                    <div class="field-editor-header">
                        <h3>Import Agent</h3>
                        <button class="field-editor-close" id="agent-import-close">&times;</button>
                    </div>
                    <div class="db-import-body">
                        <div class="db-import-dropzone" id="agent-import-dropzone">
                            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            <p>Drop agent .zip file here</p>
                            <span>or click to browse</span>
                            <input type="file" id="agent-import-file-input" accept=".zip" hidden>
                        </div>
                        <div id="agent-import-file-info" class="db-import-file-info hidden">
                            <span id="agent-import-file-name"></span>
                            <button class="field-action-btn" id="agent-import-file-clear" title="Remove">&times;</button>
                        </div>
                        <input type="text" id="agent-import-name" placeholder="Name override (optional)" style="margin-top: 0.75rem;">
                        <label class="copy-data-toggle">
                            <input type="checkbox" id="agent-import-data" checked>
                            <span>Include data (database rows and memories)</span>
                        </label>
                        <label class="copy-data-toggle">
                            <input type="checkbox" id="agent-import-kbs" checked>
                            <span>Include knowledge bases</span>
                        </label>
                    </div>
                    <div class="field-editor-footer">
                        <span class="field-editor-error" id="agent-import-error"></span>
                        <button id="agent-import-cancel" class="btn-secondary">Cancel</button>
                        <button id="agent-import-submit" class="kb-create-btn" disabled>Import</button>
                    </div>
                </div>
            </div>

            <!-- Create Agent Dialog -->
            <div id="agent-create-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content">
                    <h3>Create New Agent</h3>
                    <input type="text" id="new-agent-name" placeholder="Agent name (e.g., My Agent)">
                    <input type="text" id="new-agent-description" placeholder="Description (optional)">
                    <div class="kb-dialog-actions">
                        <button id="agent-create-cancel" class="btn-secondary">Cancel</button>
                        <button id="agent-create-confirm" class="kb-create-btn">Create</button>
                    </div>
                </div>
            </div>

            <!-- Field Editor Dialog -->
            <div id="field-editor-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content field-editor">
                    <div class="field-editor-header">
                        <h3 id="field-editor-title">Add Field</h3>
                        <button class="field-editor-close" id="field-editor-close">&times;</button>
                    </div>

                    <div class="field-editor-form">
                        <div class="field-row-pair">
                            <div class="field-row">
                                <label for="field-key">Field Key</label>
                                <input type="text" id="field-key" placeholder="e.g., user_name" autocomplete="off">
                                <span class="field-hint">Unique identifier (snake_case)</span>
                            </div>
                            <div class="field-row">
                                <label for="field-label">Display Label</label>
                                <input type="text" id="field-label" placeholder="e.g., Your Name" autocomplete="off">
                                <span class="field-hint">What users will see</span>
                            </div>
                        </div>

                        <div class="field-row">
                            <label class="setting-toggle compact">
                                <input type="checkbox" id="field-required">
                                <span class="toggle-slider"></span>
                                <span class="toggle-label">
                                    <strong>Ask on first session</strong>
                                    <small>Prompt user for this value when they first chat</small>
                                </span>
                            </label>
                        </div>

                        <div class="field-row">
                            <label class="setting-toggle compact">
                                <input type="checkbox" id="field-reminder">
                                <span class="toggle-slider"></span>
                                <span class="toggle-label">
                                    <strong>Inject as reminder</strong>
                                    <small>Inserted right before each user turn — high-attention slot for behavioral rules that must hold across long conversations</small>
                                </span>
                            </label>
                        </div>

                        <div class="field-row">
                            <label for="field-default">Default Value</label>
                            <textarea id="field-default" placeholder="Optional default value..." rows="3"></textarea>
                        </div>

                    </div>

                    <div class="field-editor-footer">
                        <span class="field-editor-error" id="field-editor-error"></span>
                        <button id="field-editor-cancel" class="btn-secondary">Cancel</button>
                        <button id="field-editor-save" class="kb-create-btn">Save Field</button>
                    </div>
                </div>
            </div>

            <!-- Table Editor Dialog -->
            <div id="table-editor-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content field-editor db-table-editor">
                    <div class="field-editor-header">
                        <h3 id="table-editor-title">Create Table</h3>
                        <button class="field-editor-close" id="table-editor-close">&times;</button>
                    </div>
                    <div class="field-editor-form">
                        <div class="field-row-pair">
                            <div class="field-row">
                                <label for="table-key">Table Key</label>
                                <input type="text" id="table-key" placeholder="e.g., contacts" autocomplete="off">
                                <span class="field-hint">Unique identifier (snake_case)</span>
                            </div>
                            <div class="field-row">
                                <label for="table-label">Display Name</label>
                                <input type="text" id="table-label" placeholder="e.g., Contact List" autocomplete="off">
                                <span class="field-hint">Human-readable label</span>
                            </div>
                        </div>
                        <div class="field-row">
                            <label>Columns</label>
                            <div id="table-columns-list" class="db-column-list"></div>
                            <button type="button" id="table-add-column-btn" class="btn-secondary db-add-column-btn">+ Add Column</button>
                        </div>
                    </div>
                    <div class="field-editor-footer">
                        <span class="field-editor-error" id="table-editor-error"></span>
                        <button id="table-editor-cancel" class="btn-secondary">Cancel</button>
                        <button id="table-editor-save" class="kb-create-btn">Save Table</button>
                    </div>
                </div>
            </div>

            <!-- Row Editor Dialog -->
            <div id="row-editor-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content field-editor db-row-editor">
                    <div class="field-editor-header">
                        <h3 id="row-editor-title">Add Row</h3>
                        <button class="field-editor-close" id="row-editor-close">&times;</button>
                    </div>
                    <div class="field-editor-form" id="row-editor-form"></div>
                    <div class="field-editor-footer">
                        <span class="field-editor-error" id="row-editor-error"></span>
                        <button id="row-editor-cancel" class="btn-secondary">Cancel</button>
                        <button id="row-editor-save" class="kb-create-btn">Save Row</button>
                    </div>
                </div>
            </div>

            <!-- Delete Confirm Dialog -->
            <div id="db-confirm-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content db-confirm-content">
                    <h3 id="db-confirm-title">Confirm Delete</h3>
                    <p id="db-confirm-message"></p>
                    <div class="field-editor-footer">
                        <button id="db-confirm-cancel" class="btn-secondary">Cancel</button>
                        <button id="db-confirm-ok" class="kb-create-btn db-confirm-danger">Delete</button>
                    </div>
                </div>
            </div>

            <!-- DB Import Dialog -->
            <div id="db-import-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content field-editor db-import-editor">
                    <div class="field-editor-header">
                        <h3>Import Data</h3>
                        <button class="field-editor-close" id="import-dialog-close">&times;</button>
                    </div>
                    <div class="db-import-body">
                        <div class="db-import-dropzone" id="import-dropzone">
                            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            <p>Drop CSV or XLSX file here</p>
                            <span>or click to browse</span>
                            <input type="file" id="import-file-input" accept=".csv,.xlsx" hidden>
                        </div>
                        <div id="import-file-info" class="db-import-file-info hidden">
                            <span id="import-file-name"></span>
                            <button class="field-action-btn" id="import-file-clear" title="Remove">&times;</button>
                        </div>
                        <div class="db-import-mode">
                            <label><input type="radio" name="import-mode" value="append" checked> Append to existing rows</label>
                            <label><input type="radio" name="import-mode" value="replace"> Replace all existing rows</label>
                        </div>
                        <div id="import-replace-warning" class="db-import-warning hidden">
                            This will delete all existing rows before importing.
                        </div>
                    </div>
                    <div class="field-editor-footer">
                        <span class="field-editor-error" id="import-error"></span>
                        <button id="import-dialog-cancel" class="btn-secondary">Cancel</button>
                        <button id="import-dialog-submit" class="kb-create-btn" disabled>Import</button>
                    </div>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Cache DOM elements
 */
/**
 * Grow the manager to nearly the whole viewport, or put it back.
 *
 * Each button is lit only when it has somewhere to go: minimize is dead
 * until the panel has actually been maximized, and maximize is dead once it
 * is, so the pair always says what state the window is in. A control that
 * looks available and does nothing is how people conclude a UI is broken.
 *
 * The state lives on the element, so closing and reopening the manager
 * returns it the way you left it rather than quietly undoing your choice.
 */
function setPanelMaximized(on) {
    const panel = elements.panel;
    if (!panel) return;
    panel.classList.toggle('maximized', !!on);
    if (elements.maximizeBtn) elements.maximizeBtn.disabled = !!on;
    if (elements.minimizeBtn) elements.minimizeBtn.disabled = !on;
}

function cacheElements() {
    elements.overlay = document.getElementById('agent-kb-manager-overlay');
    elements.panel = document.getElementById('agent-kb-manager-panel');
    elements.closeBtn = document.getElementById('agent-kb-close');
    elements.maximizeBtn = document.getElementById('agent-kb-maximize');
    elements.minimizeBtn = document.getElementById('agent-kb-minimize');
    elements.backBtn = document.getElementById('agent-kb-back-btn');
    elements.title = document.getElementById('agent-kb-title');
    elements.searchInput = document.getElementById('agent-kb-search');
    elements.createBtn = document.getElementById('create-agent-btn');

    // Views
    elements.listView = document.getElementById('agent-kb-list-view');
    elements.detailView = document.getElementById('agent-kb-detail-view');
    elements.agentList = document.getElementById('agent-kb-list');
    elements.assignedList = document.getElementById('agent-assigned-kbs');
    elements.availableList = document.getElementById('agent-available-kbs');

    // Tabs
    elements.tabKBs = document.getElementById('agent-tab-kbs');
    elements.tabMemory = document.getElementById('agent-tab-memory');
    elements.tabSettings = document.getElementById('agent-tab-settings');
    elements.tabTasks = document.getElementById('agent-tab-tasks');
    elements.tabLoops = document.getElementById('agent-tab-loops');
    elements.fieldsList = document.getElementById('memory-fields-list');
    elements.addFieldBtn = document.getElementById('add-field-btn');

    // Settings
    elements.settingInitialPrompt = document.getElementById('setting-initial-prompt');
    elements.settingAutoplayInitialPrompt = document.getElementById('setting-autoplay-initial-prompt');
    elements.autoplayInitialPromptRow = document.getElementById('autoplay-initial-prompt-row');
    elements.settingNarratorMode = document.getElementById('setting-narrator-mode');
    elements.settingNarratorVerbatim = document.getElementById('setting-narrator-verbatim');
    elements.settingSystemPrompt = document.getElementById('setting-system-prompt');
    elements.systemPromptStatus = document.getElementById('system-prompt-status');
    elements.settingsStatus = document.getElementById('settings-status');

    // Tools settings — hidden checkboxes
    elements.toolVision = document.getElementById('tool-vision');
    elements.toolTextCleanup = document.getElementById('tool-text-cleanup');
    elements.toolCalculator = document.getElementById('tool-calculator');
    elements.toolDatetime = document.getElementById('tool-datetime');
    elements.toolPython = document.getElementById('tool-python');
    elements.toolShell = document.getElementById('tool-shell');
    elements.toolFileops = document.getElementById('tool-fileops');
    elements.toolBrowsercheck = document.getElementById('tool-browsercheck');
    elements.toolWebfetch = document.getElementById('tool-webfetch');
    elements.toolHttprequest = document.getElementById('tool-httprequest');
    elements.toolEmailsend = document.getElementById('tool-emailsend');
    elements.toolEmailfetch = document.getElementById('tool-emailfetch');
    elements.toolImagegen = document.getElementById('tool-imagegen');
    elements.toolScholar = document.getElementById('tool-scholar');
    elements.toolWebsearch = document.getElementById('tool-websearch');
    elements.toolRagsearch = document.getElementById('tool-ragsearch');
    elements.toolKbembed = document.getElementById('tool-kbembed');
    elements.toolRecall = document.getElementById('tool-recall');
    elements.toolSynthesize = document.getElementById('tool-synthesize');
    elements.toolBrainQuery = document.getElementById('tool-brainquery');
    // Tool card wrappers
    elements.toolCardVision = document.getElementById('tool-card-vision');
    elements.toolCardTextCleanup = document.getElementById('tool-card-text-cleanup');
    elements.toolCardCalculator = document.getElementById('tool-card-calculator');
    elements.toolCardDatetime = document.getElementById('tool-card-datetime');
    elements.toolCardPython = document.getElementById('tool-card-python');
    elements.toolCardShell = document.getElementById('tool-card-shell');
    elements.toolCardFileops = document.getElementById('tool-card-fileops');
    elements.toolCardBrowsercheck = document.getElementById('tool-card-browsercheck');
    elements.toolCardWebfetch = document.getElementById('tool-card-webfetch');
    elements.toolCardHttprequest = document.getElementById('tool-card-httprequest');
    elements.toolCardEmailsend = document.getElementById('tool-card-emailsend');
    elements.toolCardEmailfetch = document.getElementById('tool-card-emailfetch');
    elements.toolCardImagegen = document.getElementById('tool-card-imagegen');
    elements.toolCardScholar = document.getElementById('tool-card-scholar');
    elements.toolCardWebsearch = document.getElementById('tool-card-websearch');
    elements.toolCardRagsearch = document.getElementById('tool-card-ragsearch');
    elements.toolCardKbembed = document.getElementById('tool-card-kbembed');
    elements.toolCardRecall = document.getElementById('tool-card-recall');
    elements.toolCardSynthesize = document.getElementById('tool-card-synthesize');
    elements.toolCardBrainQuery = document.getElementById('tool-card-brainquery');
    elements.toolDoccreate = document.getElementById('tool-doccreate');
    elements.toolCardDoccreate = document.getElementById('tool-card-doccreate');
    // General settings accordion
    elements.generalSettingsAccordion = document.getElementById('general-settings-accordion');
    elements.gsTemperatureOverride = document.getElementById('gs-temperature-override');
    elements.gsTemperatureSlider = document.getElementById('gs-temperature-slider');
    elements.gsTemperatureValue = document.getElementById('gs-temperature-value');
    elements.gsTemperatureDefault = document.getElementById('gs-temperature-default');
    elements.gsTemperatureItem = document.getElementById('gs-temperature');
    elements.gsMemoryOverride = document.getElementById('gs-memory-override');
    elements.gsMemorySlider = document.getElementById('gs-memory-slider');
    elements.gsMemoryValue = document.getElementById('gs-memory-value');
    elements.gsMemoryDefault = document.getElementById('gs-memory-default');
    elements.gsMemoryItem = document.getElementById('gs-memory');
    elements.gsFidelityOverride = document.getElementById('gs-fidelity-override');
    elements.gsFidelitySlider = document.getElementById('gs-fidelity-slider');
    elements.gsFidelityValue = document.getElementById('gs-fidelity-value');
    elements.gsFidelityDefault = document.getElementById('gs-fidelity-default');
    elements.gsFidelityItem = document.getElementById('gs-fidelity');
    elements.gsKbThresholdOverride = document.getElementById('gs-kb-threshold-override');
    elements.gsKbThresholdSlider = document.getElementById('gs-kb-threshold-slider');
    elements.gsKbThresholdValue = document.getElementById('gs-kb-threshold-value');
    elements.gsKbThresholdDefault = document.getElementById('gs-kb-threshold-default');
    elements.gsKbThresholdItem = document.getElementById('gs-kb-threshold');

    // Accordion elements
    elements.teachingAccordion = document.getElementById('teaching-accordion');
    elements.toolsAccordion = document.getElementById('tools-accordion');
    elements.toolsAccordionSubtitle = document.getElementById('tools-accordion-subtitle');
    elements.toolSettingsAccordion = document.getElementById('tool-settings-accordion');
    // Tool settings controls
    // tool-mode-control removed — per-tool config panels used instead
    elements.maxIterationsSlider = document.getElementById('max-iterations-slider');
    elements.maxIterationsValue = document.getElementById('max-iterations-value');
    elements.hitlModeSelect = document.getElementById('hitl-mode-select');
    elements.usePlanningCheckbox = document.getElementById('setting-use-planning');
    elements.planningDefaultOnCheckbox = document.getElementById('setting-planning-default-on');
    elements.metaAgentAccordion = document.getElementById('meta-agent-accordion');
    elements.isMetaCheckbox = document.getElementById('setting-is-meta');
    elements.routingSummaryInput = document.getElementById('setting-routing-summary');
    elements.metaSubordinatesSection = document.getElementById('meta-subordinates-section');
    elements.metaSubordinatesList = document.getElementById('meta-subordinates-list');
    elements.metaAgentSubtitle = document.getElementById('meta-agent-subtitle');
    elements.metaMaxSpawns = document.getElementById('meta-max-spawns');
    elements.metaMaxSpawnTokens = document.getElementById('meta-max-spawn-tokens');
    elements.planningDefaultOnRow = document.getElementById('planning-default-on-row');
    elements.tokenBudgetInput = document.getElementById('token-budget-input');
    elements.tokenBudgetSlider = document.getElementById('token-budget-slider');
    elements.tokenBudgetValue = document.getElementById('token-budget-value');
    elements.tokenBudgetCustomBtn = document.getElementById('token-budget-custom-btn');
    elements.tokenBudgetCustomRow = document.getElementById('token-budget-custom-row');
    elements.tokenBudgetCustomInput = document.getElementById('token-budget-custom-input');
    elements.tokenBudgetCustomApply = document.getElementById('token-budget-custom-apply');
    elements.tokenBudgetCustomCancel = document.getElementById('token-budget-custom-cancel');

    // Tool chains
    elements.toolChainsAccordion = document.getElementById('tool-chains-accordion');
    elements.toolChainsSubtitle = document.getElementById('tool-chains-subtitle');
    elements.toolChainsList = document.getElementById('tool-chains-list');
    elements.addChainBtn = document.getElementById('add-chain-btn');

    // Copy dialog
    elements.copyDialog = document.getElementById('agent-copy-dialog');
    elements.copyAgentName = document.getElementById('copy-agent-name');
    elements.copyIncludeData = document.getElementById('copy-include-data');

    // Create dialog
    elements.createDialog = document.getElementById('agent-create-dialog');
    elements.newAgentName = document.getElementById('new-agent-name');
    elements.newAgentDescription = document.getElementById('new-agent-description');

    // Field editor dialog
    elements.fieldEditorDialog = document.getElementById('field-editor-dialog');
    elements.fieldEditorTitle = document.getElementById('field-editor-title');
    elements.fieldKey = document.getElementById('field-key');
    elements.fieldLabel = document.getElementById('field-label');
    elements.fieldRequired = document.getElementById('field-required');
    elements.fieldReminder = document.getElementById('field-reminder');
    elements.fieldDefault = document.getElementById('field-default');

    // System prompt accordion
    elements.systemPromptAccordion = document.getElementById('system-prompt-accordion');

    // Model overrides

    // Database elements
    elements.memoryFieldsAccordion = document.getElementById('memory-fields-accordion');
    elements.memoryFieldsAccordionSubtitle = document.getElementById('memory-fields-accordion-subtitle');
    elements.dbAccordion = document.getElementById('db-accordion');
    elements.dbAccordionSubtitle = document.getElementById('db-accordion-subtitle');
    elements.dbEnableToggle = document.getElementById('db-enable-toggle');
    elements.dbDefaultOnRow = document.getElementById('db-default-on-row');
    elements.dbDefaultOn = document.getElementById('db-default-on');
    elements.dbContent = document.getElementById('db-content');
    elements.dbTableList = document.getElementById('db-table-list');
    elements.dbCreateTableBtn = document.getElementById('db-create-table-btn');
    elements.dbImportRecordingBtn = document.getElementById('db-import-recording-btn');
    elements.dbTableDetail = document.getElementById('db-table-detail');
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
    elements.overlay?.addEventListener('click', closeAgentKBManager);
    elements.closeBtn?.addEventListener('click', closeAgentKBManager);
    elements.maximizeBtn?.addEventListener('click', () => setPanelMaximized(true));
    elements.minimizeBtn?.addEventListener('click', () => setPanelMaximized(false));
    elements.backBtn?.addEventListener('click', showListView);
    elements.searchInput?.addEventListener('input', renderAgentList);
    elements.createBtn?.addEventListener('click', showCreateDialog);

    // Tasks and loops are hidden in a container unless it allows them
    // (the board's "Tasks & loops" switch).
    if (isContainerMode() && !state.containerConfig?.allow_automation) {
        const tasksTabBtn = document.querySelector('.agent-tab[data-tab="tasks"]');
        if (tasksTabBtn) tasksTabBtn.style.display = 'none';
        const loopsTabBtn = document.querySelector('.agent-tab[data-tab="loops"]');
        if (loopsTabBtn) loopsTabBtn.style.display = 'none';
    }

    // Tab switching
    document.querySelectorAll('.agent-tab').forEach(tab => {
        tab.addEventListener('click', () => switchTab(tab.dataset.tab));
    });

    // Swipe left/right to switch tabs
    const agentTabOrder = isContainerMode() && !state.containerConfig?.allow_automation
        ? ['kbs', 'memory', 'settings']
        : ['kbs', 'memory', 'settings', 'tasks'];
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipeInScrollable = false;
    elements.detailView?.addEventListener('touchstart', (e) => {
        swipeStartX = e.touches[0].clientX;
        swipeStartY = e.touches[0].clientY;
        // Don't swipe tabs when touch starts inside a scrollable area or on a slider
        swipeInScrollable = !!e.target.closest('.db-data-table-wrap') || e.target.closest('input[type="range"]');
    }, { passive: true });
    elements.detailView?.addEventListener('touchend', (e) => {
        if (swipeInScrollable) return;
        const deltaX = e.changedTouches[0].clientX - swipeStartX;
        const deltaY = e.changedTouches[0].clientY - swipeStartY;
        // Require horizontal swipe > 50px and more horizontal than vertical
        if (Math.abs(deltaX) < 50 || Math.abs(deltaY) > Math.abs(deltaX)) return;
        const idx = agentTabOrder.indexOf(currentTab);
        if (deltaX < 0 && idx < agentTabOrder.length - 1) {
            switchTab(agentTabOrder[idx + 1]); // swipe left → next tab
        } else if (deltaX > 0 && idx > 0) {
            switchTab(agentTabOrder[idx - 1]); // swipe right → prev tab
        }
    }, { passive: true });

    // Add field button
    elements.addFieldBtn?.addEventListener('click', () => showFieldEditor(null));

    // Settings change tracking
    elements.settingInitialPrompt?.addEventListener('change', () => {
        // Enable/disable autoplay sub-setting based on initial prompt toggle
        if (elements.autoplayInitialPromptRow) {
            const enabled = elements.settingInitialPrompt.checked;
            elements.autoplayInitialPromptRow.style.opacity = enabled ? '1' : '0.4';
            elements.autoplayInitialPromptRow.style.pointerEvents = enabled ? '' : 'none';
            if (elements.settingAutoplayInitialPrompt) {
                elements.settingAutoplayInitialPrompt.disabled = !enabled;
                if (!enabled) elements.settingAutoplayInitialPrompt.checked = false;
            }
        }
        markSettingsModified();
    });
    elements.settingAutoplayInitialPrompt?.addEventListener('change', markSettingsModified);
    elements.routingSummaryInput?.addEventListener('input', markSettingsModified);
    elements.settingNarratorMode?.addEventListener('change', markSettingsModified);
    elements.settingNarratorVerbatim?.addEventListener('change', markSettingsModified);
    elements.settingSystemPrompt?.addEventListener('blur', saveSystemPrompt);

    // Accordion toggles
    elements.generalSettingsAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        elements.generalSettingsAccordion.classList.toggle('expanded');
    });

    // General settings — override toggles and sliders
    setupGeneralSettingListeners(elements.gsTemperatureOverride, elements.gsTemperatureSlider,
        elements.gsTemperatureValue, elements.gsTemperatureItem, elements.gsTemperatureDefault, v => v.toFixed(1));
    setupGeneralSettingListeners(elements.gsMemoryOverride, elements.gsMemorySlider,
        elements.gsMemoryValue, elements.gsMemoryItem, elements.gsMemoryDefault, v => getMemoryLabel(v));
    setupGeneralSettingListeners(elements.gsFidelityOverride, elements.gsFidelitySlider,
        elements.gsFidelityValue, elements.gsFidelityItem, elements.gsFidelityDefault, v => v.toFixed(1));
    setupGeneralSettingListeners(elements.gsKbThresholdOverride, elements.gsKbThresholdSlider,
        elements.gsKbThresholdValue, elements.gsKbThresholdItem, elements.gsKbThresholdDefault, v => v.toFixed(2));

    elements.teachingAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        elements.teachingAccordion.classList.toggle('expanded');
    });
    elements.toolsAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        elements.toolsAccordion.classList.toggle('expanded');
    });
    elements.toolSettingsAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        elements.toolSettingsAccordion.classList.toggle('expanded');
    });
    elements.toolChainsAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        elements.toolChainsAccordion.classList.toggle('expanded');
    });
    elements.metaAgentAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        elements.metaAgentAccordion.classList.toggle('expanded');
    });
    elements.addChainBtn?.addEventListener('click', () => openChainEditor());

    // Tool card click handlers — replace old checkbox change listeners
    // Pre-processing tools (tier -1, standalone boolean settings)
    setupToolCardClick(elements.toolCardVision, elements.toolVision, markSettingsModified);
    setupToolCardClick(elements.toolCardTextCleanup, elements.toolTextCleanup, markSettingsModified);
    // Tier 0 tools (pattern-matched)
    setupToolCardClick(elements.toolCardCalculator, elements.toolCalculator, markSettingsModified);
    setupToolCardClick(elements.toolCardDatetime, elements.toolDatetime, markSettingsModified);
    // Tier 1 tools (LLM-only)
    setupToolCardClick(elements.toolCardPython, elements.toolPython, markSettingsModified);
    setupToolCardClick(elements.toolCardShell, elements.toolShell, markSettingsModified);
    setupToolCardClick(elements.toolCardFileops, elements.toolFileops, markSettingsModified);
    setupToolCardClick(elements.toolCardBrowsercheck, elements.toolBrowsercheck, markSettingsModified);
    setupToolCardClick(elements.toolCardWebfetch, elements.toolWebfetch, markSettingsModified);
    setupToolCardClick(elements.toolCardHttprequest, elements.toolHttprequest, markSettingsModified);
    setupToolCardClick(elements.toolCardEmailsend, elements.toolEmailsend, markSettingsModified);
    setupToolCardClick(elements.toolCardEmailfetch, elements.toolEmailfetch, markSettingsModified);
    setupToolCardClick(elements.toolCardImagegen, elements.toolImagegen, markSettingsModified);
    setupToolCardClick(elements.toolCardScholar, elements.toolScholar, markSettingsModified);
    // Pre-flight search tools
    setupToolCardClick(elements.toolCardWebsearch, elements.toolWebsearch, markSettingsModified);
    setupToolCardClick(elements.toolCardRagsearch, elements.toolRagsearch, markSettingsModified);
    setupToolCardClick(elements.toolCardKbembed, elements.toolKbembed, markSettingsModified);
    setupToolCardClick(elements.toolCardRecall, elements.toolRecall, markSettingsModified);
    setupToolCardClick(elements.toolCardSynthesize, elements.toolSynthesize, markSettingsModified);
    setupToolCardClick(elements.toolCardBrainQuery, elements.toolBrainQuery, markSettingsModified);
    // Pre-processing tools (no tool mode interaction needed)
    setupToolCardClick(elements.toolCardDoccreate, elements.toolDoccreate, markSettingsModified);

    // Inject per-tool config panels (gear icon + settings)
    injectToolConfig(elements.toolCardVision, 'vision');
    injectToolConfig(elements.toolCardTextCleanup, 'text_cleanup');
    injectToolConfig(elements.toolCardDoccreate, 'doc_create');
    injectToolConfig(elements.toolCardCalculator, 'calculator');
    injectToolConfig(elements.toolCardDatetime, 'datetime');
    injectToolConfig(elements.toolCardPython, 'python_exec');
    injectToolConfig(elements.toolCardShell, 'shell_exec');
    injectToolConfig(elements.toolCardFileops, 'file_ops');
    injectToolConfig(elements.toolCardBrowsercheck, 'browser_check');
    injectToolConfig(elements.toolCardWebfetch, 'web_fetch');
    injectToolConfig(elements.toolCardHttprequest, 'http_request');
    injectToolConfig(elements.toolCardEmailsend, 'email_send');
    injectToolConfig(elements.toolCardEmailfetch, 'email_fetch');
    injectToolConfig(elements.toolCardImagegen, 'image_generate');
    injectToolConfig(elements.toolCardScholar, 'scholar');
    injectToolConfig(elements.toolCardWebsearch, 'web_search');
    injectToolConfig(elements.toolCardRagsearch, 'rag_search');
    injectToolConfig(elements.toolCardKbembed, 'kb_embed');
    injectToolConfig(elements.toolCardRecall, 'recall');
    injectToolConfig(elements.toolCardSynthesize, 'synthesize');
    injectToolConfig(elements.toolCardBrainQuery, 'brain_query');

    // Max iterations slider
    elements.maxIterationsSlider?.addEventListener('input', () => {
        if (elements.maxIterationsValue) {
            elements.maxIterationsValue.textContent = elements.maxIterationsSlider.value;
        }
        markSettingsModified();
    });

    // HITL mode dropdown
    elements.hitlModeSelect?.addEventListener('change', () => markSettingsModified());

    // Planning checkbox — show/hide "default on" sub-toggle
    elements.usePlanningCheckbox?.addEventListener('change', () => {
        if (elements.planningDefaultOnRow) {
            elements.planningDefaultOnRow.style.display = elements.usePlanningCheckbox.checked ? '' : 'none';
        }
        markSettingsModified();
    });
    elements.planningDefaultOnCheckbox?.addEventListener('change', () => markSettingsModified());

    // Meta agent checkbox — show/hide subordinates picker
    elements.isMetaCheckbox?.addEventListener('change', () => {
        renderMetaSubordinates();
        markSettingsModified();
    });
    elements.metaMaxSpawns?.addEventListener('change', () => markSettingsModified());
    elements.metaMaxSpawnTokens?.addEventListener('change', () => markSettingsModified());

    // Token budget slider (positions 1-25 = 1k-25k, position 26 = unlimited)
    elements.tokenBudgetSlider?.addEventListener('input', () => {
        const pos = parseInt(elements.tokenBudgetSlider.value);
        const val = pos === 26 ? 0 : pos * 1000;
        elements.tokenBudgetInput.value = val;
        elements.tokenBudgetValue.textContent = formatTokenBudget(val);
        markSettingsModified();
    });

    // Token budget custom button — toggles inline input row
    elements.tokenBudgetCustomBtn?.addEventListener('click', () => {
        const row = elements.tokenBudgetCustomRow;
        if (!row) return;
        const current = parseInt(elements.tokenBudgetInput.value || '0');
        elements.tokenBudgetCustomInput.value = current || '';
        row.classList.remove('hidden');
        elements.tokenBudgetCustomInput.focus();
        elements.tokenBudgetCustomInput.select();
    });

    // Custom input — apply
    elements.tokenBudgetCustomApply?.addEventListener('click', () => applyCustomTokenBudget());

    // Custom input — cancel
    elements.tokenBudgetCustomCancel?.addEventListener('click', () => {
        elements.tokenBudgetCustomRow?.classList.add('hidden');
    });

    // Custom input — Enter to apply, Escape to cancel
    elements.tokenBudgetCustomInput?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); applyCustomTokenBudget(); }
        if (e.key === 'Escape') { e.preventDefault(); elements.tokenBudgetCustomRow?.classList.add('hidden'); }
    });

    // Create dialog
    document.getElementById('agent-create-cancel')?.addEventListener('click', hideCreateDialog);
    document.getElementById('agent-create-confirm')?.addEventListener('click', handleCreateAgent);

    // Copy dialog
    document.getElementById('agent-copy-cancel')?.addEventListener('click', hideCopyDialog);
    document.getElementById('agent-copy-confirm')?.addEventListener('click', handleCopyAgent);
    document.getElementById('copy-agent-name')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') handleCopyAgent();
    });

    // Import agent dialog
    document.getElementById('import-agent-btn')?.addEventListener('click', showAgentImportDialog);
    document.getElementById('agent-import-close')?.addEventListener('click', hideAgentImportDialog);
    document.getElementById('agent-import-cancel')?.addEventListener('click', hideAgentImportDialog);
    document.getElementById('agent-import-submit')?.addEventListener('click', handleAgentImport);
    document.getElementById('agent-import-file-clear')?.addEventListener('click', () => {
        agentImportFile = null;
        document.getElementById('agent-import-file-input').value = '';
        document.getElementById('agent-import-file-info')?.classList.add('hidden');
        document.getElementById('agent-import-dropzone')?.classList.remove('hidden');
        document.getElementById('agent-import-submit').disabled = true;
    });

    const agentImportDropzone = document.getElementById('agent-import-dropzone');
    const agentImportFileInput = document.getElementById('agent-import-file-input');
    agentImportDropzone?.addEventListener('click', () => agentImportFileInput?.click());
    agentImportDropzone?.addEventListener('dragover', (e) => {
        e.preventDefault();
        agentImportDropzone.classList.add('dragover');
    });
    agentImportDropzone?.addEventListener('dragleave', () => {
        agentImportDropzone.classList.remove('dragover');
    });
    agentImportDropzone?.addEventListener('drop', (e) => {
        e.preventDefault();
        agentImportDropzone.classList.remove('dragover');
        if (e.dataTransfer.files.length) handleAgentImportFileSelect(e.dataTransfer.files[0]);
    });
    agentImportFileInput?.addEventListener('change', (e) => {
        if (e.target.files.length) handleAgentImportFileSelect(e.target.files[0]);
    });

    // Skill import into current agent
    const skillImportBtn = document.getElementById('skill-import-btn');
    const skillImportFile = document.getElementById('skill-import-file');
    skillImportBtn?.addEventListener('click', () => skillImportFile?.click());
    skillImportFile?.addEventListener('change', (e) => {
        if (e.target.files.length) handleSkillImport(e.target.files[0]);
        e.target.value = '';
    });

    // Field editor dialog
    document.getElementById('field-editor-close')?.addEventListener('click', hideFieldEditor);
    document.getElementById('field-editor-cancel')?.addEventListener('click', hideFieldEditor);
    document.getElementById('field-editor-save')?.addEventListener('click', handleSaveField);

    // Auto-generate label from key
    elements.fieldKey?.addEventListener('input', (e) => {
        if (!editingField && elements.fieldLabel) {
            const key = e.target.value;
            elements.fieldLabel.value = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
        }
    });

    // System Prompt accordion toggle
    elements.systemPromptAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        elements.systemPromptAccordion.classList.toggle('expanded');
    });

    // Model Overrides accordion toggle

    // Memory Fields accordion toggle
    elements.memoryFieldsAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        elements.memoryFieldsAccordion.classList.toggle('expanded');
    });

    // Database accordion toggle — smooth scroll into view when opening
    elements.dbAccordion?.querySelector('.settings-accordion-toggle')?.addEventListener('click', () => {
        const wasExpanded = elements.dbAccordion.classList.contains('expanded');
        elements.dbAccordion.classList.toggle('expanded');
        if (!wasExpanded) {
            // Scroll after CSS transition starts
            setTimeout(() => {
                elements.dbAccordion.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        }
    });

    // Database enable toggle
    elements.dbEnableToggle?.addEventListener('change', handleDbEnableToggle);
    elements.dbDefaultOn?.addEventListener('change', () => saveToolConfigsImmediate());

    // Database create table button
    elements.dbCreateTableBtn?.addEventListener('click', () => showTableEditor(null));

    // Database import recording button
    elements.dbImportRecordingBtn?.addEventListener('click', showRecordingPicker);

    // Table editor dialog
    document.getElementById('table-editor-close')?.addEventListener('click', hideTableEditor);
    document.getElementById('table-editor-cancel')?.addEventListener('click', hideTableEditor);
    document.getElementById('table-editor-save')?.addEventListener('click', handleSaveTable);
    document.getElementById('table-add-column-btn')?.addEventListener('click', addTableColumn);

    // Auto-generate table label from key
    document.getElementById('table-key')?.addEventListener('input', (e) => {
        if (!editingTable) {
            const labelInput = document.getElementById('table-label');
            if (labelInput) {
                labelInput.value = e.target.value
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, c => c.toUpperCase());
            }
        }
    });

    // Row editor dialog
    document.getElementById('row-editor-close')?.addEventListener('click', hideRowEditor);
    document.getElementById('row-editor-cancel')?.addEventListener('click', hideRowEditor);
    document.getElementById('row-editor-save')?.addEventListener('click', handleSaveRow);

    // Delete confirm dialog
    document.getElementById('db-confirm-cancel')?.addEventListener('click', hideDbConfirm);

    // Import dialog
    document.getElementById('import-dialog-close')?.addEventListener('click', hideImportDialog);
    document.getElementById('import-dialog-cancel')?.addEventListener('click', hideImportDialog);
    document.getElementById('import-dialog-submit')?.addEventListener('click', handleImportSubmit);

    const importFileInput = document.getElementById('import-file-input');
    const importDropzone = document.getElementById('import-dropzone');
    importDropzone?.addEventListener('click', () => importFileInput?.click());
    importDropzone?.addEventListener('dragover', (e) => {
        e.preventDefault();
        importDropzone.classList.add('dragover');
    });
    importDropzone?.addEventListener('dragleave', () => {
        importDropzone.classList.remove('dragover');
    });
    importDropzone?.addEventListener('drop', (e) => {
        e.preventDefault();
        importDropzone.classList.remove('dragover');
        const file = e.dataTransfer.files[0];
        if (file) setImportFile(file);
    });
    importFileInput?.addEventListener('change', () => {
        if (importFileInput.files[0]) setImportFile(importFileInput.files[0]);
    });
    document.getElementById('import-file-clear')?.addEventListener('click', () => {
        importFile = null;
        document.getElementById('import-file-info')?.classList.add('hidden');
        document.getElementById('import-dropzone')?.classList.remove('hidden');
        document.getElementById('import-dialog-submit').disabled = true;
    });
    document.querySelectorAll('input[name="import-mode"]').forEach(radio => {
        radio.addEventListener('change', () => {
            const warning = document.getElementById('import-replace-warning');
            if (warning) {
                const replaceSelected = document.querySelector('input[name="import-mode"][value="replace"]')?.checked;
                warning.classList.toggle('hidden', !replaceSelected);
            }
        });
    });

    // ── Task Tab event listeners ──
    document.getElementById('create-task-btn')?.addEventListener('click', () => showTaskEditor(null));
    document.getElementById('task-editor-close')?.addEventListener('click', hideTaskEditor);
    document.getElementById('task-editor-cancel')?.addEventListener('click', hideTaskEditor);
    document.getElementById('task-editor-save')?.addEventListener('click', handleSaveTask);
    document.getElementById('task-model-btn')?.addEventListener('click', () => {
        openModelManagerForSelection((model) => {
            const modelValue = model.tier || model.id;
            document.getElementById('task-model').value = modelValue;
            const display = document.getElementById('task-model-display');
            const clearBtn = document.getElementById('task-model-clear');
            if (display) {
                display.textContent = model.display_name || modelValue;
                display.title = model.id;
            }
            if (clearBtn) clearBtn.textContent = '×';
        });
    });
    document.getElementById('task-model-clear')?.addEventListener('click', () => {
        document.getElementById('task-model').value = '';
        document.getElementById('task-model-display').textContent = '';
        document.getElementById('task-model-clear').textContent = '';
    });
    document.getElementById('task-runs-back')?.addEventListener('click', hideTaskRuns);

    // ── Loops Tab event listeners ──
    document.getElementById('create-loop-btn')?.addEventListener('click', () => showLoopEditor(null));
    document.getElementById('loop-editor-close')?.addEventListener('click', hideLoopEditor);
    document.getElementById('loop-editor-cancel')?.addEventListener('click', hideLoopEditor);
    document.getElementById('loop-editor-save')?.addEventListener('click', handleSaveLoop);
    for (const kind of ['model', 'check-model']) {
        document.getElementById(`loop-${kind}-btn`)?.addEventListener('click', () => {
            openModelManagerForSelection((model) => {
                const modelValue = model.tier || model.id;
                document.getElementById(`loop-${kind}`).value = modelValue;
                const display = document.getElementById(`loop-${kind}-display`);
                const clearBtn = document.getElementById(`loop-${kind}-clear`);
                if (display) {
                    display.textContent = model.display_name || modelValue;
                    display.title = model.id;
                }
                if (clearBtn) clearBtn.textContent = '×';
            });
        });
        document.getElementById(`loop-${kind}-clear`)?.addEventListener('click', () => {
            document.getElementById(`loop-${kind}`).value = '';
            document.getElementById(`loop-${kind}-display`).textContent = '';
            document.getElementById(`loop-${kind}-clear`).textContent = '';
        });
    }

    // Schedule type radio toggle
    document.querySelectorAll('input[name="task-schedule"]').forEach(radio => {
        radio.addEventListener('change', () => {
            const isOnce = radio.value === 'once' && radio.checked;
            document.getElementById('task-run-at-row')?.classList.toggle('hidden', !isOnce);
            document.getElementById('task-interval-row')?.classList.toggle('hidden', isOnce);
        });
    });

    // Interval preset buttons
    document.querySelectorAll('.task-interval-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.task-interval-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            document.getElementById('task-custom-interval').value = '';
        });
    });

    // Search level buttons
    document.querySelectorAll('.task-search-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.task-search-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
        });
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) {
            const taskEditor = document.getElementById('task-editor');
            const taskRuns = document.getElementById('task-runs-panel');
            const importDialog = document.getElementById('db-import-dialog');
            const dbConfirm = document.getElementById('db-confirm-dialog');
            const rowEditor = document.getElementById('row-editor-dialog');
            const tableEditor = document.getElementById('table-editor-dialog');
            const loopEditor = document.getElementById('loop-editor');
            if (taskEditor && !taskEditor.classList.contains('hidden')) {
                hideTaskEditor();
            } else if (taskRuns && !taskRuns.classList.contains('hidden')) {
                hideTaskRuns();
            } else if (loopEditor && !loopEditor.classList.contains('hidden')) {
                hideLoopEditor();
            } else if (importDialog && !importDialog.classList.contains('hidden')) {
                hideImportDialog();
            } else if (dbConfirm && !dbConfirm.classList.contains('hidden')) {
                hideDbConfirm();
            } else if (rowEditor && !rowEditor.classList.contains('hidden')) {
                hideRowEditor();
            } else if (tableEditor && !tableEditor.classList.contains('hidden')) {
                hideTableEditor();
            } else if (!elements.fieldEditorDialog?.classList.contains('hidden')) {
                hideFieldEditor();
            } else if (!document.getElementById('agent-import-dialog')?.classList.contains('hidden')) {
                hideAgentImportDialog();
            } else if (!elements.copyDialog?.classList.contains('hidden')) {
                hideCopyDialog();
            } else if (!elements.createDialog?.classList.contains('hidden')) {
                hideCreateDialog();
            } else if (currentView === 'detail') {
                showListView();
            } else {
                closeAgentKBManager();
            }
        }
    });
}

/**
 * Auto-save settings after a short debounce (replaces manual Save Changes button)
 */
function markSettingsModified() {
    if (_autoSaveTimer) clearTimeout(_autoSaveTimer);
    _autoSaveTimer = setTimeout(() => {
        _autoSaveTimer = null;
        saveSettings();
    }, 600);
}

/**
 * Switch between tabs in detail view
 */
async function switchTab(tabName) {
    currentTab = tabName;

    // Update tab buttons
    document.querySelectorAll('.agent-tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.tab === tabName);
    });

    // Update content
    elements.tabKBs?.classList.toggle('hidden', tabName !== 'kbs');
    elements.tabMemory?.classList.toggle('hidden', tabName !== 'memory');
    elements.tabSettings?.classList.toggle('hidden', tabName !== 'settings');
    elements.tabTasks?.classList.toggle('hidden', tabName !== 'tasks');
    elements.tabLoops?.classList.toggle('hidden', tabName !== 'loops');

    // Loops tab polls while a loop is running; stop when leaving it
    if (tabName !== 'loops') stopLoopsPolling();

    // Load data when switching tabs
    if (tabName === 'memory' && selectedAgent) {
        await loadAgentSettings();
        loadMemoryFields();
        loadDbState();
    } else if (tabName === 'settings' && selectedAgent) {
        loadAgentSettings();
    } else if (tabName === 'tasks' && selectedAgent) {
        await loadAgentSettings();
        loadTasks(selectedAgent, agentSettings);
    } else if (tabName === 'loops' && selectedAgent) {
        loadLoops(selectedAgent);
    }
}

/**
 * Open the Agent KB Manager modal
 */
export async function openAgentKBManager() {
    modalOpen = true;
    selectedAgent = null;
    currentView = 'list';
    currentTab = 'kbs';

    // Reset search
    if (elements.searchInput) {
        elements.searchInput.value = '';
    }

    // Show modal
    elements.overlay?.classList.add('active');
    elements.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Fetch data and render
    await Promise.all([fetchAgents(), fetchAllKBs()]);
    showListView();
}

/**
 * Close the Agent KB Manager modal
 */
/**
 * Show a non-blocking overlay inside the modal panel that says "Saving…"
 * while we wait for an in-flight save. Returns a function that removes it.
 */
function _showSavingOverlay() {
    if (!elements.panel) return () => {};
    if (elements.panel.querySelector('.agent-kb-saving-overlay')) {
        // Already shown — return a noop remover so existing one stays
        return () => {
            const el = elements.panel.querySelector('.agent-kb-saving-overlay');
            if (el) el.remove();
        };
    }
    const overlay = document.createElement('div');
    overlay.className = 'agent-kb-saving-overlay';
    overlay.innerHTML = `
        <div class="agent-kb-saving-card">
            <svg class="agent-kb-saving-spinner" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <circle cx="12" cy="12" r="9" stroke-opacity="0.2"/>
                <path d="M21 12a9 9 0 0 0-9-9"/>
            </svg>
            <span>Saving…</span>
        </div>
    `;
    elements.panel.appendChild(overlay);
    return () => overlay.remove();
}

export async function closeAgentKBManager() {
    stopLoopsPolling();
    // If a save is pending (debounce timer hasn't fired yet) flush it
    // now so the value the user just set actually persists. If a save
    // is in flight, wait for it. Either way, show a "Saving…" overlay
    // during the wait so the modal doesn't appear frozen.
    const hasPending = !!_autoSaveTimer;
    if (_autoSaveTimer) {
        clearTimeout(_autoSaveTimer);
        _autoSaveTimer = null;
        // Fire immediately — saveSettings sets _savePromise which we await below.
        try { saveSettings(); } catch {}
    }
    if (_savePromise || hasPending) {
        const removeOverlay = _showSavingOverlay();
        try {
            // Wait for the save we just kicked off (saveSettings is async
            // but doesn't return a promise we can await directly here, so
            // wait on _savePromise which it assigns synchronously).
            while (_savePromise) {
                try { await _savePromise; } catch {}
            }
        } finally {
            removeOverlay();
        }
    }
    modalOpen = false;
    elements.overlay?.classList.remove('active');
    elements.panel?.classList.remove('open');
    document.body.style.overflow = '';
}

/**
 * Fetch all agents
 */
async function fetchAgents() {
    try {
        const headers = getHeaders(false);

        const response = await fetch('/agents', { headers });
        if (!response.ok) throw new Error('Failed to fetch agents');
        const data = await response.json();
        agents = data.agents || [];
    } catch (error) {
        console.error('Error fetching agents:', error);
        agents = [];
    }
}

/**
 * Fetch all available KBs
 */
async function fetchAllKBs() {
    try {
        const headers = getHeaders(false);

        const response = await fetch('/kb', { headers });
        if (!response.ok) throw new Error('Failed to fetch KBs');
        const data = await response.json();
        allKBs = data.kbs || [];
    } catch (error) {
        console.error('Error fetching KBs:', error);
        allKBs = [];
    }
}

/**
 * Fetch memory schema for selected agent
 */
async function loadMemoryFields() {
    if (!selectedAgent) return;

    try {
        const headers = getHeaders(false);

        // Fetch schema and values in parallel
        const [schemaRes, valuesRes] = await Promise.all([
            fetch(`/agents/${encodeURIComponent(selectedAgent)}/schema`, { headers }),
            fetch(`/agents/${encodeURIComponent(selectedAgent)}/memory/values`, { headers })
        ]);

        if (!schemaRes.ok) throw new Error('Failed to fetch memory schema');
        const schemaData = await schemaRes.json();
        memoryFields = schemaData.schema?.fields || [];

        if (valuesRes.ok) {
            const valuesData = await valuesRes.json();
            memoryValues = valuesData.values || {};
        } else {
            memoryValues = {};
        }
    } catch (error) {
        console.error('Error fetching memory fields:', error);
        memoryFields = [];
        memoryValues = {};
    }

    renderMemoryFields();
}

/**
 * Load agent settings
 */
async function loadAgentSettings() {
    if (!selectedAgent) return;

    try {
        const headers = getHeaders(false);

        const response = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/settings`, { headers });
        if (!response.ok) throw new Error('Failed to fetch agent settings');
        const data = await response.json();
        agentSettings = data.settings || {};
    } catch (error) {
        console.error('Error fetching agent settings:', error);
        agentSettings = {};
    }

    renderSettings();
}

/**
 * Get memory level label from numeric value
 */
function getMemoryLabel(value) {
    return ['Off', 'Basic', 'Full'][Math.round(value)] || 'Full';
}

/**
 * Wire up override toggle + slider for a general setting item
 */
function setupGeneralSettingListeners(overrideCb, slider, valueEl, itemEl, defaultLabel, formatter) {
    if (!overrideCb || !slider) return;

    overrideCb.addEventListener('change', () => {
        const isOverride = overrideCb.checked;
        itemEl?.classList.toggle('override-active', isOverride);
        if (defaultLabel) defaultLabel.classList.toggle('hidden', isOverride);
        slider.disabled = !isOverride;
        markSettingsModified();
    });

    slider.addEventListener('input', () => {
        const val = parseFloat(slider.value);
        if (valueEl) valueEl.textContent = formatter(val);
        markSettingsModified();
    });
}

/**
 * Render a single general setting with override toggle
 */
function renderGeneralSetting(value, overrideCb, slider, valueEl, itemEl, defaultLabel, fallback, formatter) {
    if (!overrideCb || !slider) return;

    const hasValue = value !== null && value !== undefined;
    overrideCb.checked = hasValue;
    slider.disabled = !hasValue;
    slider.value = hasValue ? value : fallback;
    if (valueEl) valueEl.textContent = formatter(hasValue ? value : fallback);
    itemEl?.classList.toggle('override-active', hasValue);
    if (defaultLabel) defaultLabel.classList.toggle('hidden', hasValue);
}

/**
 * Render settings form
 */
function renderSettings() {
    // Populate form fields
    elements.settingInitialPrompt.checked = agentSettings.initial_prompt || false;
    if (elements.settingAutoplayInitialPrompt) {
        elements.settingAutoplayInitialPrompt.checked = agentSettings.autoplay_initial_prompt || false;
    }
    // Enable/disable autoplay row based on initial prompt state
    if (elements.autoplayInitialPromptRow) {
        const enabled = agentSettings.initial_prompt;
        elements.autoplayInitialPromptRow.style.opacity = enabled ? '1' : '0.4';
        elements.autoplayInitialPromptRow.style.pointerEvents = enabled ? '' : 'none';
        if (elements.settingAutoplayInitialPrompt) {
            elements.settingAutoplayInitialPrompt.disabled = !enabled;
        }
    }
    elements.settingNarratorMode.checked = agentSettings.narrator_mode || false;
    if (elements.settingNarratorVerbatim) elements.settingNarratorVerbatim.checked = agentSettings.narrator_verbatim || false;
    if (elements.settingSystemPrompt) {
        elements.settingSystemPrompt.value = agentSettings.system_prompt || '';
    }

    // General settings — override sliders
    renderGeneralSetting(agentSettings.agent_temperature, elements.gsTemperatureOverride,
        elements.gsTemperatureSlider, elements.gsTemperatureValue, elements.gsTemperatureItem,
        elements.gsTemperatureDefault, 0.7, v => v.toFixed(1));
    renderGeneralSetting(agentSettings.agent_memory_level, elements.gsMemoryOverride,
        elements.gsMemorySlider, elements.gsMemoryValue, elements.gsMemoryItem,
        elements.gsMemoryDefault, 2, v => getMemoryLabel(v));
    renderGeneralSetting(agentSettings.agent_fidelity, elements.gsFidelityOverride,
        elements.gsFidelitySlider, elements.gsFidelityValue, elements.gsFidelityItem,
        elements.gsFidelityDefault, 0.7, v => v.toFixed(1));
    renderGeneralSetting(agentSettings.agent_kb_threshold, elements.gsKbThresholdOverride,
        elements.gsKbThresholdSlider, elements.gsKbThresholdValue, elements.gsKbThresholdItem,
        elements.gsKbThresholdDefault, 0.45, v => v.toFixed(2));

    // Set vision tool (standalone boolean, not in tools array)
    if (elements.toolVision) elements.toolVision.checked = agentSettings.tool_vision || false;
    if (elements.toolTextCleanup) elements.toolTextCleanup.checked = agentSettings.tool_text_cleanup || false;

    // Set tools checkboxes
    const enabledTools = agentSettings.tools || [];
    if (elements.toolCalculator) elements.toolCalculator.checked = enabledTools.includes('calculator');
    if (elements.toolDatetime) elements.toolDatetime.checked = enabledTools.includes('datetime');
    if (elements.toolPython) elements.toolPython.checked = enabledTools.includes('python_exec');
    if (elements.toolShell) elements.toolShell.checked = enabledTools.includes('shell_exec');
    if (elements.toolFileops) elements.toolFileops.checked = enabledTools.includes('file_ops');
    if (elements.toolWebfetch) elements.toolWebfetch.checked = enabledTools.includes('web_fetch');
    if (elements.toolHttprequest) elements.toolHttprequest.checked = enabledTools.includes('http_request');
    if (elements.toolEmailsend) elements.toolEmailsend.checked = enabledTools.includes('email_send');
    if (elements.toolEmailfetch) elements.toolEmailfetch.checked = enabledTools.includes('email_fetch');
    if (elements.toolImagegen) elements.toolImagegen.checked = enabledTools.includes('image_generate');
    if (elements.toolScholar) elements.toolScholar.checked = enabledTools.includes('scholar');
    if (elements.toolWebsearch) elements.toolWebsearch.checked = enabledTools.includes('web_search');
    if (elements.toolRagsearch) elements.toolRagsearch.checked = enabledTools.includes('rag_search');
    if (elements.toolKbembed) elements.toolKbembed.checked = enabledTools.includes('kb_embed');
    if (elements.toolRecall) elements.toolRecall.checked = enabledTools.includes('recall');
    if (elements.toolSynthesize) elements.toolSynthesize.checked = enabledTools.includes('synthesize');
    if (elements.toolBrainQuery) elements.toolBrainQuery.checked = enabledTools.includes('brain_query');
    // Pre-processing tool toggles (boolean settings, not in enabled_tools array)
    if (elements.toolDoccreate) elements.toolDoccreate.checked = agentSettings.tool_doc_create || false;

    // Track agent_db state for Memory tab toggle
    dbEnabled = enabledTools.includes('agent_db');

    // Sync card visual states with checkbox values
    syncToolCardStates();

    // Set max iterations
    const maxIterations = agentSettings.max_iterations || 5;
    if (elements.maxIterationsSlider) elements.maxIterationsSlider.value = maxIterations;
    if (elements.maxIterationsValue) elements.maxIterationsValue.textContent = maxIterations;

    // Set HITL mode
    if (elements.hitlModeSelect) elements.hitlModeSelect.value = agentSettings.hitl_mode || 'none';

    // Set planning toggle
    if (elements.usePlanningCheckbox) elements.usePlanningCheckbox.checked = agentSettings.use_planning || false;
    if (elements.planningDefaultOnCheckbox) elements.planningDefaultOnCheckbox.checked = agentSettings.planning_default_on !== false;
    if (elements.planningDefaultOnRow) elements.planningDefaultOnRow.style.display = (agentSettings.use_planning) ? '' : 'none';

    // Set token budget
    const budgetVal = agentSettings.token_budget || 0;
    if (elements.tokenBudgetInput) elements.tokenBudgetInput.value = budgetVal;
    if (elements.tokenBudgetSlider) elements.tokenBudgetSlider.value = tokenBudgetToSliderPos(budgetVal);
    if (elements.tokenBudgetValue) elements.tokenBudgetValue.textContent = formatTokenBudget(budgetVal);

    // Load per-tool config panels
    loadToolConfigs(agentSettings.tool_configs);

    // Load tool chains
    renderToolChains(agentSettings.tool_chains || []);

    // Meta agent section
    if (elements.isMetaCheckbox) elements.isMetaCheckbox.checked = agentSettings.is_meta || false;
    if (elements.metaMaxSpawns) elements.metaMaxSpawns.value = agentSettings.max_spawns_per_turn || 3;
    if (elements.routingSummaryInput) elements.routingSummaryInput.value = agentSettings.routing_summary || '';
    if (elements.metaMaxSpawnTokens) elements.metaMaxSpawnTokens.value = agentSettings.max_spawn_tokens_per_turn || 0;
    renderMetaSubordinates();

    // Reset status
    elements.settingsStatus.textContent = '';
    elements.settingsStatus.className = 'settings-status';
}

/**
 * Select tool mode
 */
// Tool tier/capability classifications for per-tool config
const PATTERN_TOOLS = new Set(['calculator', 'datetime']);  // Tier 0: can use pattern_only
const HITL_TOOLS = new Set(['python_exec', 'file_ops', 'web_fetch', 'web_search', 'agent_db', 'http_request', 'email_send', 'email_fetch', 'image_generate', 'scholar', 'kb_embed']);  // Non-safe risk levels
// Tools whose config gets the full treatment: the card lifts out of the grid,
// grows to the container width, and opens a tall panel. The sandbox tools earn
// it for the same reason the email ones do — a filesystem path is long, and
// editing one inside a grid cell meant reading a directory through a slot a few
// characters wide.
const EXTENDED_CONFIG_TOOLS = new Set(['email_send', 'email_fetch', 'image_generate', 'scholar', 'vision', 'text_cleanup', 'doc_create', 'shell_exec', 'file_ops', 'browser_check']);

// The sandbox-scoped tools: one long path (or a list of them) rather than a
// stack of short credential fields, so their panel is laid out differently.
const SANDBOX_CONFIG_TOOLS = new Set(['shell_exec', 'file_ops', 'browser_check']);

// Config for LLM-model-override tools. Key = tool name, value = { label, hint, visionOnly }
const MODEL_OVERRIDE_TOOLS = {
    vision: {
        label: 'Vision Model',
        defaultModel: 'qwen3-vl:2b',
        hint: 'Override the built-in local vision model (qwen3-vl:2b) with any vision-capable model from your configured providers. Leave blank to use the default.',
        visionOnly: true,
    },
    text_cleanup: {
        label: 'Proofread Model',
        defaultModel: 'qwen2.5:3b',
        hint: 'Override the built-in local proofreader (qwen2.5:3b) with any LLM. Text-only — any model works. Leave blank to use the default.',
        visionOnly: false,
    },
    doc_create: {
        label: 'Doc Intent Model',
        defaultModel: 'qwen2.5:3b',
        hint: 'Override the built-in local intent detector (qwen2.5:3b) — used to detect whether the user wants a downloadable document. Any LLM works. Leave blank to use the default.',
        visionOnly: false,
    },
};

/**
 * Setup a tool card to toggle its hidden checkbox on click
 */
function setupToolCardClick(cardEl, checkboxEl, callback) {
    if (!cardEl || !checkboxEl) return;
    cardEl.addEventListener('click', (e) => {
        // Don't toggle when config is open, or clicking gear button / config panel
        if (cardEl.classList.contains('config-open')) return;
        if (e.target.closest('.tool-config-btn') || e.target.closest('.tool-config-panel')) return;
        checkboxEl.checked = !checkboxEl.checked;
        cardEl.classList.toggle('enabled', checkboxEl.checked);
        callback();
        updateToolsAccordionSubtitle();
    });
}

// SVG icons for gear/close toggle
const GEAR_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>';
const CLOSE_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

// Lightbox overlay for tool config (created once, shared)
let toolConfigOverlay = null;
let activeConfigCard = null;

function getToolConfigOverlay() {
    if (!toolConfigOverlay) {
        toolConfigOverlay = document.createElement('div');
        toolConfigOverlay.className = 'tool-config-overlay';
        // Append inside the admin panel so it respects its stacking context (z-index: 500)
        const panel = document.getElementById('agent-kb-manager-panel');
        if (panel) {
            panel.appendChild(toolConfigOverlay);
        } else {
            document.body.appendChild(toolConfigOverlay);
        }
        toolConfigOverlay.addEventListener('click', () => closeToolConfig());
    }
    return toolConfigOverlay;
}

function openToolConfig(cardEl) {
    if (activeConfigCard === cardEl) { closeToolConfig(); return; }
    if (activeConfigCard) closeToolConfig(true); // instant close previous

    const overlay = getToolConfigOverlay();
    const panel = cardEl.querySelector('.tool-config-panel');
    const gearBtn = cardEl.querySelector('.tool-config-btn');
    if (!panel) return;

    const isExtended = EXTENDED_CONFIG_TOOLS.has(panel.dataset.tool);
    if (isExtended) {
        panel.classList.add('has-smtp');
        if (panel.dataset.tool === 'scholar') panel.classList.add('has-scholar');
        if (SANDBOX_CONFIG_TOOLS.has(panel.dataset.tool)) panel.classList.add('has-sandbox');
    }
    activeConfigCard = cardEl;

    const grid = cardEl.closest('.tool-cards-grid');

    if (isExtended && grid) {
        // ── Extended config: full card-growth choreography ──
        const gridRect = grid.getBoundingClientRect();
        const cardRect = cardEl.getBoundingClientRect();
        const gridStyle = getComputedStyle(grid);
        const gridPadLeft = parseFloat(gridStyle.paddingLeft) || 16;
        const gridPadRight = parseFloat(gridStyle.paddingRight) || 16;

        // Save original position for close animation
        cardEl._origLeft = cardRect.left - gridRect.left;
        cardEl._origTop = cardRect.top - gridRect.top + grid.scrollTop;
        cardEl._origWidth = cardRect.width;
        cardEl._origHeight = cardRect.height;

        // Expand grid padding to accommodate expanded card + config panel
        const panelHeight = 420;
        grid.style.paddingBottom = `${panelHeight + 24}px`;
        grid.dataset.configPadding = 'true';

        // Find the scrollable ancestor (.agent-tab-content)
        const scrollParent = cardEl.closest('.agent-tab-content') || grid.parentElement;

        // Step 1: Smooth scroll the card to center of the scrollable area
        const scrollParentRect = scrollParent.getBoundingClientRect();
        const cardCenter = cardRect.top + cardRect.height / 2;
        const scrollParentCenter = scrollParentRect.top + scrollParentRect.height / 2;
        const scrollDelta = cardCenter - scrollParentCenter;
        scrollParent.scrollBy({ top: scrollDelta, behavior: 'smooth' });

        setTimeout(() => {
            // Step 2: Show overlay, elevate card, then lift out of grid
            overlay.classList.add('active');
            cardEl.classList.add('config-open');
            if (gearBtn) { gearBtn.classList.add('open'); gearBtn.innerHTML = CLOSE_SVG; }

            // Insert invisible placeholder to maintain grid space
            const placeholder = document.createElement('div');
            placeholder.style.height = cardRect.height + 'px';
            placeholder.style.visibility = 'hidden';
            cardEl.parentNode.insertBefore(placeholder, cardEl);
            cardEl._placeholder = placeholder;

            // Lift card out of grid flow at its current position (no visual jump)
            cardEl.style.position = 'absolute';
            cardEl.style.left = cardEl._origLeft + 'px';
            cardEl.style.top = cardEl._origTop + 'px';
            cardEl.style.width = cardEl._origWidth + 'px';
            cardEl.style.zIndex = '100';

            // Force reflow so the browser registers the starting position
            cardEl.offsetHeight;

            // Add transition class and animate to expanded size (fill container width)
            cardEl.classList.add('config-expanding');
            const expandedWidth = gridRect.width - gridPadLeft - gridPadRight;
            cardEl.style.left = gridPadLeft + 'px';
            cardEl.style.width = expandedWidth + 'px';

            // Step 3: After card finishes growing, open config panel
            setTimeout(() => {
                panel.classList.add('open');
                // Re-scroll to keep expanded card + panel centered
                requestAnimationFrame(() => {
                    const newCardRect = cardEl.getBoundingClientRect();
                    const totalHeight = newCardRect.height + panelHeight;
                    const targetCenter = newCardRect.top + totalHeight / 2;
                    const newScrollParentRect = scrollParent.getBoundingClientRect();
                    const parentCenter = newScrollParentRect.top + newScrollParentRect.height / 2;
                    const drift = targetCenter - parentCenter;
                    if (Math.abs(drift) > 20) {
                        scrollParent.scrollBy({ top: drift, behavior: 'smooth' });
                    }
                });
            }, 380);
        }, 280);
    } else {
        // ── Simple config: scroll + overlay + panel ──
        if (grid) {
            const gridRect = grid.getBoundingClientRect();
            const cardRect = cardEl.getBoundingClientRect();
            const overflow = (cardRect.bottom + 120) - gridRect.bottom;
            if (overflow > 0) {
                grid.style.paddingBottom = `${overflow + 16}px`;
                grid.dataset.configPadding = 'true';
            }
        }
        // Smooth scroll card to center of scrollable area
        const scrollParent = cardEl.closest('.agent-tab-content') || (grid ? grid.parentElement : null);
        if (scrollParent) {
            const spRect = scrollParent.getBoundingClientRect();
            const cRect = cardEl.getBoundingClientRect();
            const delta = (cRect.top + cRect.height / 2) - (spRect.top + spRect.height / 2);
            scrollParent.scrollBy({ top: delta, behavior: 'smooth' });
        }

        setTimeout(() => {
            overlay.classList.add('active');
            cardEl.classList.add('config-open');
            if (gearBtn) { gearBtn.classList.add('open'); gearBtn.innerHTML = CLOSE_SVG; }
            setTimeout(() => { panel.classList.add('open'); }, 100);
        }, 280);
    }
}

function closeToolConfig(instant = false) {
    if (!activeConfigCard) return;
    const card = activeConfigCard;
    const panel = card.querySelector('.tool-config-panel');
    const gearBtn = card.querySelector('.tool-config-btn');
    const isExtended = card.classList.contains('config-expanding');
    const grid = card.closest('.tool-cards-grid');
    activeConfigCard = null;

    // Helper to fully clean up card state
    function cleanup() {
        if (panel) panel.classList.remove('has-smtp', 'has-scholar', 'has-sandbox');
        if (gearBtn) { gearBtn.classList.remove('open'); gearBtn.innerHTML = GEAR_SVG; }
        card.classList.remove('config-open');
        getToolConfigOverlay().classList.remove('active');
        if (grid?.dataset.configPadding) { grid.style.paddingBottom = ''; delete grid.dataset.configPadding; }
    }

    // Helper to restore card to grid flow after absolute positioning
    function restoreToGrid() {
        card.classList.remove('config-expanding');
        card.style.position = '';
        card.style.left = '';
        card.style.top = '';
        card.style.width = '';
        card.style.zIndex = '';
        if (card._placeholder) { card._placeholder.remove(); delete card._placeholder; }
        delete card._origLeft; delete card._origTop;
        delete card._origWidth; delete card._origHeight;
    }

    if (instant) {
        // Instant close — no animation, used when switching between cards
        if (panel) panel.classList.remove('open', 'has-smtp', 'has-scholar', 'has-sandbox');
        if (isExtended) restoreToGrid();
        card.classList.remove('config-open');
        if (gearBtn) { gearBtn.classList.remove('open'); gearBtn.innerHTML = GEAR_SVG; }
        getToolConfigOverlay().classList.remove('active');
        if (grid?.dataset.configPadding) { grid.style.paddingBottom = ''; delete grid.dataset.configPadding; }
        return;
    }

    if (isExtended) {
        // ── Reverse choreography: panel up → card shrinks → overlay fades ──

        // Step 1: Close config panel
        if (panel) panel.classList.remove('open');

        // Step 2: After panel closes, animate card back to original size
        setTimeout(() => {
            card.style.left = card._origLeft + 'px';
            card.style.width = card._origWidth + 'px';

            // Step 3: After card finishes shrinking, restore to grid + cleanup
            setTimeout(() => {
                restoreToGrid();
                cleanup();
            }, 380);
        }, 280);
    } else {
        // ── Simple reverse: panel up → cleanup ──
        if (panel) panel.classList.remove('open');
        setTimeout(() => cleanup(), 280);
    }
}

/**
 * Inject gear icon and config panel into a tool card
 */
function injectToolConfig(cardEl, toolName) {
    if (!cardEl) return;

    const hasMode = PATTERN_TOOLS.has(toolName);
    const hasReset = HITL_TOOLS.has(toolName);

    // Gear button
    const gearBtn = document.createElement('button');
    gearBtn.type = 'button';
    gearBtn.className = 'tool-config-btn';
    gearBtn.title = 'Tool settings';
    gearBtn.innerHTML = GEAR_SVG;

    gearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        openToolConfig(cardEl);
    });

    cardEl.appendChild(gearBtn);

    // Config panel
    const panel = document.createElement('div');
    panel.className = 'tool-config-panel';
    panel.dataset.tool = toolName;

    let panelHTML = '';

    // Mode toggle (only for tier 0 pattern-matchable tools)
    if (hasMode) {
        panelHTML += `
            <div class="config-row">
                <span class="config-label">Mode</span>
                <div class="config-mode-toggle" data-tool="${toolName}">
                    <button type="button" class="config-mode-btn active" data-value="llm_assisted">LLM</button>
                    <button type="button" class="config-mode-btn" data-value="pattern_only">Pattern</button>
                </div>
            </div>`;
    }

    // Default on toggle (controls whether tool starts enabled in chat plus menu)
    panelHTML += `
        <div class="config-row">
            <span class="config-label">Default on</span>
            <label class="config-toggle">
                <input type="checkbox" class="config-default-on" data-tool="${toolName}" checked>
                <span class="config-toggle-slider"></span>
            </label>
        </div>`;

    // Sandbox scoping — file_ops paths, shell working dir, browser app URL.
    // All three write to the same settings.integrations.sandbox object.
    const SANDBOX_FIELDS = {
        file_ops: { field: 'allowed_paths', label: 'Allowed paths', textarea: true,
            hint: 'One directory per line. Empty = defaults (~/Documents, ~/Downloads, ~/Desktop, ./). Set a single project folder to confine this agent.' },
        shell_exec: { field: 'working_dir', label: 'Working directory', textarea: false,
            hint: 'Directory shell commands run in (empty = server default).' },
        browser_check: { field: 'app_url', label: 'App URL', textarea: false,
            hint: 'Default page to check when the agent gives no URL (e.g. http://192.168.1.111:8085/).' },
    };
    if (SANDBOX_FIELDS[toolName]) {
        const sf = SANDBOX_FIELDS[toolName];
        panelHTML += `
            <div class="config-divider"></div>
            <div class="config-section-label">Sandbox</div>
            <div class="config-hint" style="margin-bottom:0.5rem;opacity:0.7;font-size:0.8rem;">${sf.hint}</div>
            <div class="config-row config-input-row">
                <span class="config-label">${sf.label}</span>
                ${sf.textarea
                    ? `<textarea class="config-input config-sandbox" data-field="${sf.field}" rows="3" placeholder="/path/to/project"></textarea>`
                    : `<input type="text" class="config-input config-sandbox" data-field="${sf.field}" placeholder="">`}
            </div>
            <div class="config-row">
                <span class="config-sandbox-status"></span>
                <button type="button" class="config-smtp-save-btn config-sandbox-save">Save</button>
            </div>`;
    }

    // Email account config — shared by Send (SMTP) and Fetch (IMAP, host derived).
    if (toolName === 'email_send' || toolName === 'email_fetch') {
        panelHTML += `
            <div class="config-divider"></div>
            <div class="config-section-label">Email Account (SMTP send + IMAP fetch)</div>
            <div class="config-hint" style="margin-bottom:0.5rem;opacity:0.7;font-size:0.8rem;">One account powers both tools. Fetch reads via IMAP using this same login (app password required for Gmail/Yahoo/iCloud).</div>
            <div class="config-row config-input-row">
                <span class="config-label">Provider</span>
                <select class="config-input config-smtp-provider">
                    <option value="">Custom</option>
                    <option value="gmail">Gmail</option>
                    <option value="outlook">Outlook / Hotmail</option>
                    <option value="yahoo">Yahoo</option>
                    <option value="sendgrid">SendGrid</option>
                    <option value="mailgun">Mailgun</option>
                    <option value="ses">Amazon SES</option>
                    <option value="zoho">Zoho</option>
                    <option value="fastmail">Fastmail</option>
                    <option value="proton">Proton Mail Bridge</option>
                </select>
            </div>
            <div class="config-row config-input-row">
                <span class="config-label">Host</span>
                <input type="text" class="config-input config-smtp" data-field="host" placeholder="smtp.example.com">
            </div>
            <div class="config-row config-input-row">
                <span class="config-label">Port</span>
                <input type="number" class="config-input config-smtp" data-field="port" placeholder="587" value="587">
            </div>
            <div class="config-row config-input-row">
                <span class="config-label">User</span>
                <input type="text" class="config-input config-smtp" data-field="user" placeholder="you@example.com">
            </div>
            <div class="config-row config-input-row">
                <span class="config-label">Password</span>
                <input type="password" class="config-input config-smtp" data-field="password" placeholder="App password or API key">
            </div>
            <div class="config-row config-input-row">
                <span class="config-label">From</span>
                <input type="text" class="config-input config-smtp" data-field="from" placeholder="(defaults to user)">
            </div>`;
    }

    // Image generation config (only for image_generate)
    if (toolName === 'image_generate') {
        panelHTML += `
            <div class="config-divider"></div>
            <div class="config-section-label">Image API Configuration</div>
            <div class="config-row config-input-row">
                <span class="config-label">Provider</span>
                <select class="config-input config-image-provider">
                    <option value="">Select provider...</option>
                    <option value="pollinations">Pollinations (free)</option>
                    <option value="gemini">Google Imagen (Gemini API)</option>
                    <option value="openai">OpenAI (DALL-E 3)</option>
                    <option value="stability">Stability AI (SDXL)</option>
                </select>
            </div>
            <div class="config-row config-input-row config-image-key-row">
                <span class="config-label">API Key</span>
                <input type="password" class="config-input config-image" data-field="api_key" placeholder="API key">
            </div>`;
    }

    // Scholar providers config (only for scholar)
    if (toolName === 'scholar') {
        panelHTML += `
            <div class="config-divider"></div>
            <div class="scholar-config">
                <div class="scholar-config-header">
                    <span class="config-section-label" style="margin:0">API Keys</span>
                    <span class="scholar-config-hint">optional — unlocks additional sources</span>
                </div>
                <div class="scholar-builtin-note">
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    arXiv, Crossref, Unpaywall, Wikipedia &amp; SEC EDGAR always available — no key needed
                </div>
                <div class="scholar-provider-group">
                    <div class="scholar-group-label">Academic</div>
                    <div class="scholar-provider-row">
                        <span class="scholar-provider-name" title="220M papers, abstracts, citations, TLDR">Semantic Scholar</span>
                        <input type="password" class="config-input config-scholar" data-provider="semantic_scholar" placeholder="Free API key">
                    </div>
                    <div class="scholar-provider-row">
                        <span class="scholar-provider-name" title="36M biomedical papers, PMC full text">PubMed</span>
                        <input type="password" class="config-input config-scholar" data-provider="pubmed" placeholder="Free API key">
                    </div>
                    <div class="scholar-provider-row">
                        <span class="scholar-provider-name" title="250M works, abstracts, OA links">OpenAlex</span>
                        <input type="password" class="config-input config-scholar" data-provider="openalex" placeholder="Free API key">
                    </div>
                    <div class="scholar-provider-row">
                        <span class="scholar-provider-name" title="46M full text open access papers">CORE</span>
                        <input type="password" class="config-input config-scholar" data-provider="core" placeholder="Free API key">
                    </div>
                </div>
                <div class="scholar-provider-group">
                    <div class="scholar-group-label">Legal &amp; Economic</div>
                    <div class="scholar-provider-row">
                        <span class="scholar-provider-name" title="Millions of court opinions &amp; oral arguments">CourtListener</span>
                        <input type="password" class="config-input config-scholar" data-provider="courtlistener" placeholder="Free API key">
                    </div>
                    <div class="scholar-provider-row">
                        <span class="scholar-provider-name" title="Bills, legislation, voting records">Congress.gov</span>
                        <input type="password" class="config-input config-scholar" data-provider="congress" placeholder="Free API key">
                    </div>
                    <div class="scholar-provider-row">
                        <span class="scholar-provider-name" title="820K economic time series from the Federal Reserve">FRED</span>
                        <input type="password" class="config-input config-scholar" data-provider="fred" placeholder="Free API key">
                    </div>
                    <div class="scholar-provider-row">
                        <span class="scholar-provider-name" title="US patent search &amp; full text">USPTO Patents</span>
                        <input type="password" class="config-input config-scholar" data-provider="uspto" placeholder="Free API key">
                    </div>
                </div>
            </div>`;
    }

    // Model override panel for tools that delegate an LLM call (vision / text_cleanup / doc_create)
    const overrideCfg = MODEL_OVERRIDE_TOOLS[toolName];
    if (overrideCfg) {
        panelHTML += `
            <div class="config-divider"></div>
            <div class="config-section-label">${overrideCfg.label}</div>
            <div class="vision-config-hint">${overrideCfg.hint}</div>
            <div class="config-row config-input-row">
                <span class="config-label">Model</span>
                <select class="config-input config-model-override" data-tool="${toolName}" data-vision-only="${overrideCfg.visionOnly ? '1' : '0'}">
                    <option value="">Default (local ${overrideCfg.defaultModel})</option>
                </select>
            </div>
            <div class="config-row">
                <button type="button" class="config-model-save-btn" data-tool="${toolName}">Save Model</button>
                <span class="config-model-status"></span>
            </div>`;
    }

    // Reset approvals (only for tools with non-safe risk levels)
    if (hasReset) {
        panelHTML += `
            <div class="config-row">
                <button type="button" class="config-reset-btn" data-tool="${toolName}">Reset remembered approvals</button>
            </div>`;
    }

    // Save SMTP button last (after reset)
    if (toolName === 'email_send' || toolName === 'email_fetch') {
        panelHTML += `
            <div class="config-row">
                <button type="button" class="config-smtp-save-btn">Save Email Account</button>
                <span class="config-smtp-status"></span>
            </div>`;
    }

    // Save image config button (after reset)
    if (toolName === 'image_generate') {
        panelHTML += `
            <div class="config-row">
                <button type="button" class="config-image-save-btn">Save Image Config</button>
                <span class="config-image-status"></span>
            </div>`;
    }

    // Save scholar config button
    if (toolName === 'scholar') {
        panelHTML += `
            <div class="config-row">
                <button type="button" class="config-scholar-save-btn">Save Scholar Keys</button>
                <span class="config-scholar-status"></span>
            </div>`;
    }

    panel.innerHTML = panelHTML;

    // Event listeners for mode buttons
    if (hasMode) {
        panel.querySelectorAll('.config-mode-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                panel.querySelectorAll('.config-mode-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                saveToolConfigsImmediate();
            });
        });
    }

    // Default on toggle
    panel.querySelector('.config-default-on')?.addEventListener('change', (e) => {
        e.stopPropagation();
        saveToolConfigsImmediate();
    });

    // Reset approvals button
    const resetBtn = panel.querySelector('.config-reset-btn');
    if (resetBtn) {
        resetBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            resetToolApprovals(toolName);
            resetBtn.textContent = 'Cleared!';
            resetBtn.classList.add('cleared');
            setTimeout(() => {
                resetBtn.textContent = 'Reset remembered approvals';
                resetBtn.classList.remove('cleared');
            }, 2000);
        });
    }

    // Sandbox save (file_ops / shell_exec / browser_check). Each panel edits
    // its own field but the PUT replaces integrations.sandbox, so merge the
    // other fields from local state before sending.
    const sandboxSaveBtn = panel.querySelector('.config-sandbox-save');
    if (sandboxSaveBtn) {
        sandboxSaveBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (!selectedAgent) return;
            const statusEl = panel.querySelector('.config-sandbox-status');
            const input = panel.querySelector('.config-sandbox');
            const field = input?.dataset.field;
            if (!field) return;

            const current = { ...(agentSettings?.integrations?.sandbox || {}) };
            const val = input.value.trim();
            if (field === 'allowed_paths') {
                current.allowed_paths = val ? val.split('\n').map(p => p.trim()).filter(Boolean) : [];
            } else {
                current[field] = val;
            }

            try {
                const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/integrations/sandbox`, {
                    method: 'PUT',
                    headers: getHeaders(),
                    body: JSON.stringify(current),
                });
                if (!res.ok) throw new Error('Failed to save');
                const data = await res.json();
                if (!agentSettings.integrations) agentSettings.integrations = {};
                agentSettings.integrations.sandbox = data.sandbox || current;
                if (statusEl) { statusEl.textContent = 'Saved!'; statusEl.className = 'config-sandbox-status success'; }
                setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 2000);
            } catch (err) {
                if (statusEl) { statusEl.textContent = 'Save failed'; statusEl.className = 'config-sandbox-status error'; }
            }
        });
        panel.querySelectorAll('.config-sandbox').forEach(input => {
            input.addEventListener('click', (e) => e.stopPropagation());
        });
    }

    // SMTP save button (email_send only)
    const smtpSaveBtn = panel.querySelector('.config-smtp-save-btn');
    if (smtpSaveBtn) {
        smtpSaveBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (!selectedAgent) return;
            const statusEl = panel.querySelector('.config-smtp-status');

            const smtp = {};
            panel.querySelectorAll('.config-smtp').forEach(input => {
                const field = input.dataset.field;
                const val = input.value.trim();
                // Don't overwrite password if placeholder is showing
                if (field === 'password' && val === '') return;
                if (val) smtp[field] = field === 'port' ? parseInt(val) : val;
            });

            if (!smtp.host || !smtp.user || !smtp.password) {
                if (statusEl) { statusEl.textContent = 'Host, user, and password required'; statusEl.className = 'config-smtp-status error'; }
                return;
            }

            try {
                const headers = getHeaders();
                const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/integrations/smtp`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(smtp)
                });
                if (!res.ok) throw new Error('Failed to save');

                // Update local state
                if (!agentSettings.integrations) agentSettings.integrations = {};
                agentSettings.integrations.smtp = { ...smtp, password: '••••••••' };

                // Clear password field to show placeholder
                const pwInput = panel.querySelector('.config-smtp[data-field="password"]');
                if (pwInput) { pwInput.value = ''; pwInput.placeholder = '••••••••  (saved)'; }

                if (statusEl) { statusEl.textContent = 'Saved!'; statusEl.className = 'config-smtp-status success'; }
                setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 2000);
            } catch (err) {
                if (statusEl) { statusEl.textContent = 'Save failed'; statusEl.className = 'config-smtp-status error'; }
            }
        });

        // Stop input clicks from toggling card
        panel.querySelectorAll('.config-smtp').forEach(input => {
            input.addEventListener('click', (e) => e.stopPropagation());
        });

        // Provider presets — auto-fill host, port, and contextual hints
        const SMTP_PROVIDERS = {
            gmail:    { host: 'smtp.gmail.com',                   port: 587, user: 'you@gmail.com',       password: 'App password',           note: 'Enable 2FA → create App password in Google account' },
            outlook:  { host: 'smtp-mail.outlook.com',            port: 587, user: 'you@outlook.com',     password: 'Account password',       note: '' },
            yahoo:    { host: 'smtp.mail.yahoo.com',              port: 587, user: 'you@yahoo.com',       password: 'App password',           note: 'Generate App password in Yahoo account security' },
            sendgrid: { host: 'smtp.sendgrid.net',                port: 587, user: 'apikey',              password: 'SendGrid API key',       note: 'User is literally "apikey", password is your API key' },
            mailgun:  { host: 'smtp.mailgun.org',                 port: 587, user: 'postmaster@your.domain', password: 'Mailgun SMTP password', note: '' },
            ses:      { host: 'email-smtp.us-east-1.amazonaws.com', port: 587, user: 'SMTP username',     password: 'SMTP password',          note: 'Use IAM SMTP credentials, not your AWS key' },
            zoho:     { host: 'smtp.zoho.com',                    port: 587, user: 'you@zoho.com',        password: 'App password',           note: '' },
            fastmail: { host: 'smtp.fastmail.com',                port: 587, user: 'you@fastmail.com',    password: 'App password',           note: '' },
            proton:   { host: '127.0.0.1',                        port: 1025, user: 'you@protonmail.com', password: 'Bridge password',        note: 'Requires Proton Mail Bridge running locally' },
        };

        const providerSelect = panel.querySelector('.config-smtp-provider');
        if (providerSelect) {
            providerSelect.addEventListener('click', (e) => e.stopPropagation());
            providerSelect.addEventListener('change', () => {
                const preset = SMTP_PROVIDERS[providerSelect.value];
                const hostInput = panel.querySelector('.config-smtp[data-field="host"]');
                const portInput = panel.querySelector('.config-smtp[data-field="port"]');
                const userInput = panel.querySelector('.config-smtp[data-field="user"]');
                const pwInput   = panel.querySelector('.config-smtp[data-field="password"]');

                if (preset) {
                    if (hostInput) { hostInput.value = preset.host; hostInput.placeholder = preset.host; }
                    if (portInput) { portInput.value = preset.port; }
                    if (userInput) { userInput.placeholder = preset.user; }
                    if (pwInput)   { pwInput.placeholder = preset.password; }
                } else {
                    // Custom — reset to generic placeholders
                    if (hostInput) { hostInput.value = ''; hostInput.placeholder = 'smtp.example.com'; }
                    if (portInput) { portInput.value = 587; }
                    if (userInput) { userInput.placeholder = 'you@example.com'; }
                    if (pwInput)   { pwInput.placeholder = 'Password or API key'; }
                }
            });
        }
    }

    // Image config save button (image_generate only)
    const imageSaveBtn = panel.querySelector('.config-image-save-btn');
    if (imageSaveBtn) {
        imageSaveBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (!selectedAgent) return;
            const statusEl = panel.querySelector('.config-image-status');
            const providerSel = panel.querySelector('.config-image-provider');
            const apiKeyInput = panel.querySelector('.config-image[data-field="api_key"]');

            const provider = providerSel?.value || '';
            const apiKey = apiKeyInput?.value?.trim() || '';
            const needsKey = provider !== 'pollinations';

            if (!provider) {
                if (statusEl) { statusEl.textContent = 'Select a provider'; statusEl.className = 'config-image-status error'; }
                return;
            }
            if (needsKey && !apiKey) {
                if (statusEl) { statusEl.textContent = 'API key required'; statusEl.className = 'config-image-status error'; }
                return;
            }

            try {
                const headers = getHeaders();
                const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/integrations/image`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({ provider, api_key: apiKey || 'none' })
                });
                if (!res.ok) throw new Error('Failed to save');

                if (!agentSettings.integrations) agentSettings.integrations = {};
                agentSettings.integrations.image = { api_provider: provider, api_key: needsKey ? '••••••••' : 'none' };

                if (needsKey && apiKeyInput) { apiKeyInput.value = ''; apiKeyInput.placeholder = '••••••••  (saved)'; }
                if (statusEl) { statusEl.textContent = 'Saved!'; statusEl.className = 'config-image-status success'; }
                setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 2000);
            } catch (err) {
                if (statusEl) { statusEl.textContent = 'Save failed'; statusEl.className = 'config-image-status error'; }
            }
        });

        // Show/hide API key row based on provider
        const providerSel = panel.querySelector('.config-image-provider');
        const keyRow = panel.querySelector('.config-image-key-row');
        if (providerSel && keyRow) {
            const updateKeyVisibility = () => {
                keyRow.style.display = providerSel.value === 'pollinations' ? 'none' : '';
            };
            providerSel.addEventListener('change', updateKeyVisibility);
            updateKeyVisibility();
        }

        // Stop input clicks from toggling card
        panel.querySelectorAll('.config-image, .config-image-provider').forEach(input => {
            input.addEventListener('click', (e) => e.stopPropagation());
        });
    }

    // Scholar config save button
    const scholarSaveBtn = panel.querySelector('.config-scholar-save-btn');
    if (scholarSaveBtn) {
        scholarSaveBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (!selectedAgent) return;
            const statusEl = panel.querySelector('.config-scholar-status');

            // Collect all non-empty API keys
            const keys = {};
            panel.querySelectorAll('.config-scholar').forEach(input => {
                const provider = input.dataset.provider;
                const val = input.value?.trim();
                if (provider && val) {
                    keys[provider] = val;
                }
            });

            try {
                const headers = getHeaders();
                const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/integrations/scholar`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(keys)
                });
                if (!res.ok) throw new Error('Failed to save');

                // Update local state
                if (!agentSettings.integrations) agentSettings.integrations = {};
                const saved = {};
                for (const p of Object.keys(keys)) saved[p] = '••••••••';
                agentSettings.integrations.scholar = saved;

                // Clear inputs, show saved placeholders
                panel.querySelectorAll('.config-scholar').forEach(input => {
                    const provider = input.dataset.provider;
                    if (keys[provider]) {
                        input.value = '';
                        input.placeholder = '••••••••  (saved)';
                    }
                });

                if (statusEl) { statusEl.textContent = 'Saved!'; statusEl.className = 'config-scholar-status success'; }
                setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 2000);
            } catch (err) {
                if (statusEl) { statusEl.textContent = 'Save failed'; statusEl.className = 'config-scholar-status error'; }
            }
        });

        // Stop input clicks from toggling card
        panel.querySelectorAll('.config-scholar').forEach(input => {
            input.addEventListener('click', (e) => e.stopPropagation());
        });
    }

    // Model override save button (vision / text_cleanup / doc_create)
    const modelSaveBtn = panel.querySelector('.config-model-save-btn');
    if (modelSaveBtn) {
        const toolKey = modelSaveBtn.dataset.tool;
        const selectEl = panel.querySelector('.config-model-override');
        const visionOnly = selectEl?.dataset.visionOnly === '1';
        populateModelSelect(selectEl, { visionOnly });

        modelSaveBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (!selectedAgent) return;
            const statusEl = panel.querySelector('.config-model-status');
            const option = selectEl?.selectedOptions?.[0];
            const model = selectEl?.value || '';
            const provider = option?.dataset?.provider || '';

            try {
                const headers = getHeaders();
                const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/integrations/${toolKey}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({ provider, model })
                });
                if (!res.ok) throw new Error('Failed to save');

                if (!agentSettings.integrations) agentSettings.integrations = {};
                agentSettings.integrations[toolKey] = model ? { provider, model } : null;

                if (statusEl) { statusEl.textContent = 'Saved!'; statusEl.className = 'config-model-status success'; }
                setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 2000);
            } catch (err) {
                if (statusEl) { statusEl.textContent = 'Save failed'; statusEl.className = 'config-model-status error'; }
            }
        });

        if (selectEl) selectEl.addEventListener('click', (e) => e.stopPropagation());
    }

    // Prevent panel clicks from toggling the card
    panel.addEventListener('click', (e) => e.stopPropagation());

    cardEl.appendChild(panel);
}

// Cache of all available models (fetched once per agent manager session)
let _allModelCache = null;

// What a tool actually falls back to with no override — the server
// resolves it, because it depends on which workers and cloud lanes are up.
let _toolDefaults = {};

async function fetchAllAvailableModels() {
    if (_allModelCache) return _allModelCache;
    try {
        const res = await fetch('/models/all', { headers: getHeaders() });
        const data = await res.json();
        _allModelCache = (data.models || []).filter(m => m.is_available);
        _toolDefaults = data.tool_defaults || {};
    } catch (e) {
        console.error('[ModelOverride] Failed to load models:', e);
        _allModelCache = [];
    }
    return _allModelCache;
}

/** Relabel the "Default" option with the model that would really run. */
function labelDefaultOption(selectEl, toolName) {
    const opt = selectEl?.querySelector('option[value=""]');
    const d = _toolDefaults[toolName];
    if (!opt || !d) return;
    opt.textContent = d.is_cloud
        ? `Default (${d.model} — cloud)`
        : `Default (local ${d.model})`;
}

async function populateModelSelect(selectEl, { visionOnly = false } = {}) {
    if (!selectEl) return;
    if (selectEl.querySelector('optgroup')) return;  // Already populated
    const all = await fetchAllAvailableModels();
    labelDefaultOption(selectEl, selectEl.dataset.tool);
    const models = visionOnly ? all.filter(m => m.supports_vision) : all;
    const byProvider = {};
    for (const m of models) {
        const key = m.provider_name || m.provider;
        (byProvider[key] = byProvider[key] || []).push(m);
    }
    const providerOrder = Object.keys(byProvider).sort();
    for (const provider of providerOrder) {
        const group = document.createElement('optgroup');
        group.label = provider;
        for (const m of byProvider[provider]) {
            const opt = document.createElement('option');
            opt.value = m.id;
            opt.dataset.provider = m.provider;
            const price = m.pricing ? ` — $${m.pricing.input}/$${m.pricing.output}/M` : (m.is_free ? ' — free' : '');
            opt.textContent = `${m.display_name}${price}`;
            group.appendChild(opt);
        }
        selectEl.appendChild(group);
    }
    // Restore current selection if any (tool-keyed)
    const toolKey = selectEl.dataset.tool;
    const current = toolKey ? agentSettings?.integrations?.[toolKey]?.model : '';
    if (current) selectEl.value = current;
}

/**
 * Reset remembered HITL approvals for a specific tool
 */
function resetToolApprovals(toolName) {
    if (!selectedAgent) return;
    const prefix = `hitl_${selectedAgent}_${toolName}_`;
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
}

/**
 * Build tool_configs dict from all config panels
 */
function getToolConfigs() {
    const configs = {};
    document.querySelectorAll('.tool-config-panel').forEach(panel => {
        const toolName = panel.dataset.tool;
        if (!toolName) return;

        const config = {};
        let hasOverride = false;

        // Mode (only for tier 0 tools)
        if (PATTERN_TOOLS.has(toolName)) {
            const activeMode = panel.querySelector('.config-mode-btn.active');
            const mode = activeMode?.dataset.value || 'llm_assisted';
            if (mode !== 'llm_assisted') {
                config.mode = mode;
                hasOverride = true;
            }
        }

        // Default on (in chat plus menu)
        const defaultOnCheckbox = panel.querySelector('.config-default-on');
        if (defaultOnCheckbox && !defaultOnCheckbox.checked) {
            config.default_on = false;
            hasOverride = true;
        }

        // Only include tools that differ from defaults
        if (hasOverride) {
            configs[toolName] = config;
        }
    });

    // agent_db default_on (managed in Memory tab, not a tool card)
    if (elements.dbDefaultOn && !elements.dbDefaultOn.checked) {
        configs.agent_db = { ...(configs.agent_db || {}), default_on: false };
    }

    return configs;
}

/**
 * Immediately save tool_configs to server (used by config panel toggles)
 */
async function saveToolConfigsImmediate() {
    if (!selectedAgent) return;

    const toolConfigs = getToolConfigs();

    try {
        const headers = getHeaders();

        const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/settings`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ key: 'tool_configs', value: toolConfigs })
        });
        if (!res.ok) throw new Error('Failed to save tool_configs');

        // Keep local state in sync
        agentSettings.tool_configs = toolConfigs;
    } catch (error) {
        console.error('Error saving tool_configs:', error);
    }
}

/**
 * Load tool_configs into config panels from settings
 */
function loadToolConfigs(toolConfigs) {
    // Reset all config panels to defaults first (prevents stale state between agent loads)
    document.querySelectorAll('.tool-config-panel').forEach(panel => {
        // Reset default_on to checked (default: true)
        const defaultOnCheckbox = panel.querySelector('.config-default-on');
        if (defaultOnCheckbox) defaultOnCheckbox.checked = true;

        // Reset mode buttons to LLM (default)
        panel.querySelectorAll('.config-mode-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.value === 'llm_assisted');
        });
    });

    // Reset agent_db default_on (Memory tab)
    if (elements.dbDefaultOn) elements.dbDefaultOn.checked = true;

    // Apply overrides from saved tool_configs (may be empty)
    if (toolConfigs && typeof toolConfigs === 'object') {
        for (const [toolName, config] of Object.entries(toolConfigs)) {
            const panel = document.querySelector(`.tool-config-panel[data-tool="${toolName}"]`);
            if (!panel) continue;

            // Mode
            if (config.mode && PATTERN_TOOLS.has(toolName)) {
                panel.querySelectorAll('.config-mode-btn').forEach(btn => {
                    btn.classList.toggle('active', btn.dataset.value === config.mode);
                });
            }

            // Default on
            if (config.default_on === false) {
                const defaultOnCheckbox = panel.querySelector('.config-default-on');
                if (defaultOnCheckbox) defaultOnCheckbox.checked = false;
            }
        }

        // agent_db default_on (Memory tab)
        if (toolConfigs.agent_db?.default_on === false && elements.dbDefaultOn) {
            elements.dbDefaultOn.checked = false;
        }
    }

    // Load sandbox config into file_ops / shell_exec / browser_check panels
    const sandbox = agentSettings?.integrations?.sandbox || {};
    document.querySelectorAll('.config-sandbox').forEach(input => {
        const field = input.dataset.field;
        if (field === 'allowed_paths') {
            input.value = Array.isArray(sandbox.allowed_paths) ? sandbox.allowed_paths.join('\n') : '';
        } else {
            input.value = sandbox[field] || '';
        }
    });

    // Load SMTP config into email_send panel
    const smtpPanel = document.querySelector('.tool-config-panel[data-tool="email_send"]');
    if (smtpPanel) {
        const smtp = agentSettings?.integrations?.smtp || {};

        // Detect provider from saved host and set dropdown
        const providerSelect = smtpPanel.querySelector('.config-smtp-provider');
        if (providerSelect && smtp.host) {
            const hostMap = {
                'smtp.gmail.com': 'gmail', 'smtp-mail.outlook.com': 'outlook',
                'smtp.mail.yahoo.com': 'yahoo', 'smtp.sendgrid.net': 'sendgrid',
                'smtp.mailgun.org': 'mailgun', 'smtp.zoho.com': 'zoho',
                'smtp.fastmail.com': 'fastmail',
            };
            // SES hosts vary by region
            const detected = hostMap[smtp.host]
                || (smtp.host.includes('amazonaws.com') ? 'ses' : '')
                || (smtp.host === '127.0.0.1' ? 'proton' : '');
            providerSelect.value = detected;
            // Trigger change to set placeholders
            if (detected) providerSelect.dispatchEvent(new Event('change'));
        }

        smtpPanel.querySelectorAll('.config-smtp').forEach(input => {
            const field = input.dataset.field;
            if (field === 'password') {
                input.value = '';
                input.placeholder = smtp.password ? '••••••••  (saved)' : (input.placeholder || 'Password or API key');
            } else {
                input.value = smtp[field] || '';
            }
        });
    }

    // Load image config into image_generate panel
    const imagePanel = document.querySelector('.tool-config-panel[data-tool="image_generate"]');
    if (imagePanel) {
        const image = agentSettings?.integrations?.image || {};
        const providerSel = imagePanel.querySelector('.config-image-provider');
        if (providerSel && image.api_provider) {
            providerSel.value = image.api_provider;
            // Trigger change to update key row visibility
            providerSel.dispatchEvent(new Event('change'));
        }
        const apiKeyInput = imagePanel.querySelector('.config-image[data-field="api_key"]');
        if (apiKeyInput) {
            apiKeyInput.value = '';
            const isFree = image.api_provider === 'pollinations';
            apiKeyInput.placeholder = (!isFree && image.api_key && image.api_key !== 'none') ? '••••••••  (saved)' : 'API key';
        }
    }

    // Load scholar config into scholar panel
    const scholarPanel = document.querySelector('.tool-config-panel[data-tool="scholar"]');
    if (scholarPanel) {
        const scholar = agentSettings?.integrations?.scholar || {};
        scholarPanel.querySelectorAll('.config-scholar').forEach(input => {
            const provider = input.dataset.provider;
            input.value = '';
            if (provider && scholar[provider] && scholar[provider] !== 'none') {
                input.placeholder = '••••••••  (saved)';
            } else {
                input.placeholder = 'API key (free)';
            }
        });
    }

    // Load model-override config for vision / text_cleanup / doc_create panels
    document.querySelectorAll('.tool-config-panel .config-model-override').forEach(select => {
        const toolKey = select.dataset.tool;
        const visionOnly = select.dataset.visionOnly === '1';
        const current = agentSettings?.integrations?.[toolKey]?.model || '';
        populateModelSelect(select, { visionOnly }).then(() => {
            if (current && select.querySelector(`option[value="${CSS.escape(current)}"]`)) {
                select.value = current;
            } else {
                select.value = '';
            }
        });
    });
}

/**
 * Format token budget value for display
 */
function formatTokenBudget(val) {
    if (val === 0) return '∞';
    if (val >= 1000) return `${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k`;
    return `${val}`;
}

/**
 * Convert token budget value to slider position (1-26)
 */
function tokenBudgetToSliderPos(val) {
    if (val === 0) return 26;
    // Clamp to 1k-25k range, round to nearest 1k
    const pos = Math.round(Math.max(1000, Math.min(25000, val)) / 1000);
    return pos;
}

/**
 * Apply custom token budget from inline input
 */
function applyCustomTokenBudget() {
    const raw = elements.tokenBudgetCustomInput?.value;
    const val = Math.max(0, parseInt(raw) || 0);
    elements.tokenBudgetInput.value = val;
    elements.tokenBudgetSlider.value = tokenBudgetToSliderPos(val);
    elements.tokenBudgetValue.textContent = formatTokenBudget(val);
    elements.tokenBudgetCustomRow?.classList.add('hidden');
    markSettingsModified();
}

/**
 * Update the "X enabled" subtitle on the Tools accordion header
 */
function updateToolsAccordionSubtitle() {
    if (!elements.toolsAccordionSubtitle) return;
    const count = getEnabledTools().length +
        (elements.toolVision?.checked ? 1 : 0) +
        (elements.toolTextCleanup?.checked ? 1 : 0) +
        (elements.toolDoccreate?.checked ? 1 : 0);
    elements.toolsAccordionSubtitle.textContent = `${count} enabled`;
}

/**
 * Sync all tool card visual states with their hidden checkbox values
 */
function syncToolCardStates() {
    document.querySelectorAll('.tool-card').forEach(card => {
        const cb = card.querySelector('.tool-card-checkbox');
        if (cb) card.classList.toggle('enabled', cb.checked);
    });
    updateToolsAccordionSubtitle();
}

/**
 * Get list of enabled tools
 */
function getEnabledTools() {
    const tools = [];
    if (elements.toolCalculator?.checked) tools.push('calculator');
    if (elements.toolDatetime?.checked) tools.push('datetime');
    if (elements.toolPython?.checked) tools.push('python_exec');
    if (elements.toolShell?.checked) tools.push('shell_exec');
    if (elements.toolFileops?.checked) tools.push('file_ops');
    if (elements.toolBrowsercheck?.checked) tools.push('browser_check');
    if (elements.toolWebfetch?.checked) tools.push('web_fetch');
    if (elements.toolHttprequest?.checked) tools.push('http_request');
    if (elements.toolEmailsend?.checked) tools.push('email_send');
    if (elements.toolEmailfetch?.checked) tools.push('email_fetch');
    if (elements.toolImagegen?.checked) tools.push('image_generate');
    if (elements.toolScholar?.checked) tools.push('scholar');
    if (elements.toolWebsearch?.checked) tools.push('web_search');
    if (elements.toolRagsearch?.checked) tools.push('rag_search');
    if (elements.toolKbembed?.checked) tools.push('kb_embed');
    if (elements.toolRecall?.checked) tools.push('recall');
    if (elements.toolSynthesize?.checked) tools.push('synthesize');
    if (elements.toolBrainQuery?.checked) tools.push('brain_query');
    // doc_create is a pre-processing tool — saved as boolean setting, not in enabled_tools
    // Preserve agent_db if enabled via Memory tab toggle
    if (dbEnabled) tools.push('agent_db');
    return tools;
}

/**
 * Auto-save system prompt on blur
 */
async function saveSystemPrompt() {
    if (!selectedAgent) return;
    const value = elements.settingSystemPrompt.value;

    // getHeaders(true) forwards X-Auth-Token (admin) AND X-Container-Token
    // (container mode). The inline X-Auth-Token-only build below it used to
    // 401 the auth middleware before the PUT settings handler could run —
    // same bug previously fixed in the bulk saveSettings path.
    const headers = getHeaders(true);

    try {
        if (value === (agentSettings.system_prompt || '')) return;
        const response = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/settings`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ key: 'system_prompt', value })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to save system prompt');
        }
        agentSettings.system_prompt = value;

        if (elements.systemPromptStatus) {
            elements.systemPromptStatus.textContent = 'Saved';
            elements.systemPromptStatus.className = 'system-prompt-status saved';
            setTimeout(() => {
                elements.systemPromptStatus.textContent = '';
                elements.systemPromptStatus.className = 'system-prompt-status';
            }, 2000);
        }
    } catch (error) {
        console.error('[AgentKBManager] Failed to save system prompt:', error);
        if (elements.systemPromptStatus) {
            elements.systemPromptStatus.textContent = error.message;
            elements.systemPromptStatus.className = 'system-prompt-status error';
        }
    }
}

/**
 * Save agent settings. One bulk PUT instead of N per-key PUTs — the
 * old loop did 17 sequential round-trips (each with its own DB connection
 * and read-modify-write) which made every save visibly slow.
 *
 * Sets _savePromise to the in-flight Promise so closeAgentKBManager()
 * can await it before letting the modal close. Cleared when the request
 * settles (success or failure).
 */
/**
 * Meta agent: render the subordinate-agent checkbox list. Every other agent
 * is a candidate; checked = in agentSettings.subordinate_agents.
 */
function renderMetaSubordinates() {
    const isMeta = elements.isMetaCheckbox?.checked || false;
    if (elements.metaSubordinatesSection) {
        elements.metaSubordinatesSection.style.display = isMeta ? '' : 'none';
    }
    const selected = new Set(agentSettings.subordinate_agents || []);
    if (elements.metaAgentSubtitle) {
        elements.metaAgentSubtitle.textContent = isMeta
            ? `${selected.size} subordinate${selected.size !== 1 ? 's' : ''}`
            : '';
    }
    const list = elements.metaSubordinatesList;
    if (!list || !isMeta) return;

    const candidates = agents.filter(a => a.name !== selectedAgent);
    if (candidates.length === 0) {
        list.innerHTML = '<p class="tasks-empty">No other agents available</p>';
        return;
    }
    list.innerHTML = candidates.map(a => `
        <label class="setting-toggle compact meta-subordinate-row">
            <input type="checkbox" class="meta-subordinate-checkbox" value="${escapeHtml(a.name)}"
                   ${selected.has(a.name) ? 'checked' : ''}>
            <span class="toggle-slider"></span>
            <span class="toggle-label">
                <strong>${escapeHtml(a.name)}</strong>
                ${a.description ? `<small>${escapeHtml(a.description)}</small>` : '<small style="opacity:0.6">No description — add one; it guides the meta agent\'s routing</small>'}
            </span>
        </label>
    `).join('');
    list.querySelectorAll('.meta-subordinate-checkbox').forEach(cb => {
        cb.addEventListener('change', () => {
            // Keep the in-memory selection current so re-renders don't lose state
            agentSettings.subordinate_agents = getSelectedSubordinates();
            if (elements.metaAgentSubtitle) {
                const n = agentSettings.subordinate_agents.length;
                elements.metaAgentSubtitle.textContent = `${n} subordinate${n !== 1 ? 's' : ''}`;
            }
            markSettingsModified();
        });
    });
}

function getSelectedSubordinates() {
    if (!elements.metaSubordinatesList) return agentSettings.subordinate_agents || [];
    return [...elements.metaSubordinatesList.querySelectorAll('.meta-subordinate-checkbox:checked')]
        .map(cb => cb.value);
}

async function saveSettings() {
    if (!selectedAgent) return;
    // If another save is already in flight, await it and then re-run with
    // the latest form values. Prevents an out-of-order write where an
    // older payload lands AFTER a newer one and clobbers it.
    if (_savePromise) {
        try { await _savePromise; } catch {}
    }

    // getHeaders() forwards both X-Auth-Token (admin) AND X-Container-Token
    // (container mode) — the old inline build sent only X-Auth-Token, so a
    // container-mode user with no admin session got 401 from the auth
    // middleware before the handler ever ran.
    const headers = getHeaders(true);

    elements.settingsStatus.textContent = 'Saving...';
    elements.settingsStatus.className = 'settings-status saving';

    const settings = {
        initial_prompt: elements.settingInitialPrompt.checked,
        autoplay_initial_prompt: elements.settingAutoplayInitialPrompt?.checked || false,
        narrator_mode: elements.settingNarratorMode.checked,
        narrator_verbatim: elements.settingNarratorVerbatim?.checked || false,
        tool_vision: elements.toolVision?.checked || false,
        tool_text_cleanup: elements.toolTextCleanup?.checked || false,
        tool_doc_create: elements.toolDoccreate?.checked || false,
        tools: getEnabledTools(),
        tool_configs: getToolConfigs(),
        max_iterations: parseInt(elements.maxIterationsSlider?.value || '5'),
        hitl_mode: elements.hitlModeSelect?.value || 'none',
        use_planning: elements.usePlanningCheckbox?.checked || false,
        planning_default_on: elements.planningDefaultOnCheckbox?.checked !== false,
        is_meta: elements.isMetaCheckbox?.checked || false,
        routing_summary: (elements.routingSummaryInput?.value || '').trim(),
        subordinate_agents: elements.isMetaCheckbox?.checked ? getSelectedSubordinates() : [],
        max_spawns_per_turn: parseInt(elements.metaMaxSpawns?.value) || 3,
        max_spawn_tokens_per_turn: parseInt(elements.metaMaxSpawnTokens?.value) || 0,
        token_budget: parseInt(elements.tokenBudgetInput?.value || '0'),
        agent_temperature: elements.gsTemperatureOverride?.checked ? parseFloat(elements.gsTemperatureSlider?.value) : null,
        agent_memory_level: elements.gsMemoryOverride?.checked ? parseInt(elements.gsMemorySlider?.value) : null,
        agent_fidelity: elements.gsFidelityOverride?.checked ? parseFloat(elements.gsFidelitySlider?.value) : null,
        agent_kb_threshold: elements.gsKbThresholdOverride?.checked ? parseFloat(elements.gsKbThresholdSlider?.value) : null,
    };

    const doSave = async () => {
        const response = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/settings/bulk`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ settings }),
        });
        if (!response.ok) {
            let detail = `Save failed (${response.status})`;
            try {
                const j = await response.json();
                if (j?.detail) detail = j.detail;
            } catch {}
            throw new Error(detail);
        }
    };

    _savePromise = doSave();
    try {
        await _savePromise;

        // Sync the in-memory cache to match what we just persisted
        Object.assign(agentSettings, {
            initial_prompt: settings.initial_prompt,
            autoplay_initial_prompt: settings.autoplay_initial_prompt,
            narrator_mode: settings.narrator_mode,
            narrator_verbatim: settings.narrator_verbatim,
            tools: settings.tools,
            tool_configs: settings.tool_configs,
            use_planning: settings.use_planning,
            planning_default_on: settings.planning_default_on,
            is_meta: settings.is_meta,
            routing_summary: settings.routing_summary,
            subordinate_agents: settings.subordinate_agents,
            max_spawns_per_turn: settings.max_spawns_per_turn,
            max_spawn_tokens_per_turn: settings.max_spawn_tokens_per_turn,
            token_budget: settings.token_budget,
            agent_temperature: settings.agent_temperature,
            agent_memory_level: settings.agent_memory_level,
            agent_fidelity: settings.agent_fidelity,
            agent_kb_threshold: settings.agent_kb_threshold,
        });

        elements.settingsStatus.textContent = 'Saved';
        elements.settingsStatus.className = 'settings-status saved';
        setTimeout(() => {
            if (elements.settingsStatus.textContent === 'Saved') {
                elements.settingsStatus.textContent = '';
                elements.settingsStatus.className = 'settings-status';
            }
        }, 2000);
    } catch (error) {
        elements.settingsStatus.textContent = error.message;
        elements.settingsStatus.className = 'settings-status error';
    } finally {
        _savePromise = null;
    }
}

/**
 * Show the list view
 */
function showListView() {
    currentView = 'list';
    selectedAgent = null;
    elements.listView?.classList.remove('hidden');
    elements.detailView?.classList.add('hidden');
    elements.backBtn?.classList.add('hidden');
    elements.title.textContent = 'Manage Agents';
    renderAgentList();
}

/**
 * Show the detail view for an agent
 */
async function showDetailView(agentName) {
    currentView = 'detail';
    // Reset DB table selection when switching to a different agent
    if (selectedAgent !== agentName) {
        dbSelectedTable = null;
    }
    selectedAgent = agentName;
    currentTab = 'kbs';

    elements.listView?.classList.add('hidden');
    elements.detailView?.classList.remove('hidden');
    elements.backBtn?.classList.remove('hidden');
    elements.title.textContent = formatDisplayName(agentName);

    // Reset tabs
    document.querySelectorAll('.agent-tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.tab === 'kbs');
    });
    elements.tabKBs?.classList.remove('hidden');
    elements.tabMemory?.classList.add('hidden');
    elements.tabSettings?.classList.add('hidden');

    await loadAgentKBs();
}

/**
 * Load KBs for the selected agent
 */
async function loadAgentKBs() {
    if (!selectedAgent) return;

    try {
        const headers = getHeaders(false);

        const response = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/kbs`, { headers });
        if (!response.ok) throw new Error('Failed to fetch agent KBs');
        const data = await response.json();
        assignedKBs = data.kbs || [];
    } catch (error) {
        console.error('Error fetching agent KBs:', error);
        assignedKBs = [];
    }

    renderAssignmentLists();
}

/**
 * Render the agent list
 */
function renderAgentList() {
    if (!elements.agentList) return;

    const searchTerm = elements.searchInput?.value.toLowerCase() || '';

    const filteredAgents = agents.filter(agent => {
        // Hide read-only (admin) agents from the manage list — container users can't edit them
        if (agent.read_only) return false;
        const name = agent.name?.toLowerCase() || '';
        const desc = agent.description?.toLowerCase() || '';
        return name.includes(searchTerm) || desc.includes(searchTerm);
    });

    if (filteredAgents.length === 0) {
        elements.agentList.innerHTML = `
            <div class="kb-empty">
                ${agents.length === 0 ? 'No agents available' : 'No agents match your search'}
            </div>
        `;
        return;
    }

    // Sort: system agents first, then alphabetically
    filteredAgents.sort((a, b) => {
        const sysA = a.is_system_agent ? 0 : 1;
        const sysB = b.is_system_agent ? 0 : 1;
        if (sysA !== sysB) return sysA - sysB;
        const nameA = formatDisplayName(a.name);
        const nameB = formatDisplayName(b.name);
        return nameA.localeCompare(nameB);
    });

    let html = '<div class="context-card-grid">';
    for (const agent of filteredAgents) {
        const displayName = formatDisplayName(agent.name);
        html += `
            <div class="context-card" data-agent-name="${escapeHtml(agent.name)}">
                <div class="context-card-actions">
                    <button class="replace-agent-btn" data-agent="${escapeHtml(agent.name)}" title="Replace from file (overwrite this agent)">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
                            <polyline points="21 3 21 8 16 8"/>
                            <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
                            <polyline points="3 21 3 16 8 16"/>
                        </svg>
                    </button>
                    ${agent.has_backup ? `<button class="restore-agent-btn" data-agent="${escapeHtml(agent.name)}" title="Restore from backup">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="9 14 4 9 9 4"/>
                            <path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
                        </svg>
                    </button>` : ''}
                    <button class="export-agent-btn" data-agent="${escapeHtml(agent.name)}" title="Export Agent">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                    </button>
                    ${agent.is_system_agent ? '' : `<button class="copy-agent-btn" data-agent="${escapeHtml(agent.name)}" title="Copy Agent">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                        </svg>
                    </button>`}
                    ${agent.is_system_agent ? '' : `<button class="delete-btn delete-agent-btn" data-agent="${escapeHtml(agent.name)}" title="Delete Agent">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                        </svg>
                    </button>`}
                </div>
                <div class="context-card-header">
                    <span class="context-card-name">${escapeHtml(displayName)}</span>
                    <div class="context-card-badges">
                        ${agent.is_system_agent ? '<span class="badge system">SYSTEM</span>' : ''}
                        ${agent.is_global && agent.read_only ? '<span class="badge global" title="Shared from outside this container. Read-only here.">GLOBAL</span>' : ''}
                        ${agent.is_light ? '<span class="badge free">LIGHT</span>' : ''}
                    </div>
                </div>
                ${agent.description ? `<div class="context-card-description">${escapeHtml(agent.description)}</div>` : ''}
                ${renderAgentChips(agent)}
            </div>
        `;
    }
    html += '</div>';

    elements.agentList.innerHTML = html;

    // Add click handlers
    elements.agentList.querySelectorAll('.context-card').forEach(card => {
        card.addEventListener('click', (e) => {
            // Don't trigger if clicking action buttons
            if (e.target.closest('.delete-agent-btn')
                || e.target.closest('.copy-agent-btn')
                || e.target.closest('.export-agent-btn')
                || e.target.closest('.replace-agent-btn')
                || e.target.closest('.restore-agent-btn')) return;
            const agentName = card.dataset.agentName;
            showDetailView(agentName);
        });
    });

    // Add delete handlers
    elements.agentList.querySelectorAll('.delete-agent-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const agentName = btn.dataset.agent;
            const confirmed = await confirm({
                title: 'Delete Agent',
                message: `Delete agent "${agentName}"? This cannot be undone.`,
                confirmText: 'Delete',
                danger: true
            });
            if (confirmed) {
                await deleteAgent(agentName);
            }
        });
    });

    // Add copy handlers
    elements.agentList.querySelectorAll('.copy-agent-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            showCopyDialog(btn.dataset.agent);
        });
    });

    // Add export handlers
    elements.agentList.querySelectorAll('.export-agent-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            exportAgent(btn.dataset.agent);
        });
    });

    // Add replace-from-file handlers
    elements.agentList.querySelectorAll('.replace-agent-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            replaceAgentFromFile(btn.dataset.agent);
        });
    });

    // Add restore-from-backup handlers
    elements.agentList.querySelectorAll('.restore-agent-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            restoreAgentFromBackup(btn.dataset.agent);
        });
    });
}

/**
 * Render assignment lists (assigned and available KBs)
 */
function renderAssignmentLists() {
    // Assigned KBs
    if (elements.assignedList) {
        if (assignedKBs.length === 0) {
            elements.assignedList.innerHTML = '<div class="kb-empty">No KBs assigned</div>';
        } else {
            let html = '';
            for (const kbName of assignedKBs) {
                html += `
                    <div class="kb-assignment-item">
                        <span class="kb-assignment-name">${escapeHtml(kbName)}</span>
                        <button class="unassign-btn" data-kb="${escapeHtml(kbName)}">Remove</button>
                    </div>
                `;
            }
            elements.assignedList.innerHTML = html;

            // Add unassign handlers
            elements.assignedList.querySelectorAll('.unassign-btn').forEach(btn => {
                btn.addEventListener('click', () => unassignKB(btn.dataset.kb));
            });
        }
    }

    // Available KBs (not assigned)
    if (elements.availableList) {
        const availableKBs = allKBs.filter(kb => !assignedKBs.includes(kb.name));

        if (availableKBs.length === 0) {
            elements.availableList.innerHTML = '<div class="kb-empty">All KBs are assigned</div>';
        } else {
            let html = '';
            for (const kb of availableKBs) {
                html += `
                    <div class="kb-assignment-item">
                        <span class="kb-assignment-name">${escapeHtml(kb.name)}</span>
                        <button class="assign-btn" data-kb="${escapeHtml(kb.name)}">Add</button>
                    </div>
                `;
            }
            elements.availableList.innerHTML = html;

            // Add assign handlers
            elements.availableList.querySelectorAll('.assign-btn').forEach(btn => {
                btn.addEventListener('click', () => assignKB(btn.dataset.kb));
            });
        }
    }
}

/**
 * Render memory fields list
 */
function renderMemoryFields() {
    if (!elements.fieldsList) return;

    const visibleFields = memoryFields;

    // Update accordion subtitle
    if (elements.memoryFieldsAccordionSubtitle) {
        elements.memoryFieldsAccordionSubtitle.textContent = visibleFields.length > 0
            ? `${visibleFields.length} field${visibleFields.length !== 1 ? 's' : ''}`
            : '';
    }

    if (visibleFields.length === 0) {
        elements.fieldsList.innerHTML = `
            <div class="memory-fields-empty">
                <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p>No memory fields defined</p>
                <span>Add fields to capture and persist structured data from conversations</span>
            </div>
        `;
        return;
    }

    let html = '';
    for (const field of visibleFields) {
        const typeIcon = field.type === 'autodate' ? `
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
        ` : `
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
            </svg>
        `;

        const requiredBadge = field.required ? '<span class="field-badge required">Required</span>' : '';
        const reminderBadge = field.reminder ? '<span class="field-badge reminder" title="Injected as a separate system message right before each user turn">Reminder</span>' : '';
        const storedValue = memoryValues[field.key] || '';
        const effectiveValue = storedValue || field.default || '';
        const isDefault = !storedValue && !!field.default;

        let valueHtml;
        if (effectiveValue) {
            const truncated = effectiveValue.length > 120 ? effectiveValue.substring(0, 120) + '...' : effectiveValue;
            const defaultIndicator = isDefault ? ' <span class="value-default-indicator">(default)</span>' : '';
            valueHtml = `<div class="field-card-value"><span class="value-text">${escapeHtml(truncated)}</span>${defaultIndicator}</div>`;
        } else {
            valueHtml = '<div class="field-card-value empty"><span class="value-text">(not set)</span></div>';
        }

        html += `
            <div class="memory-field-card" data-field-key="${escapeHtml(field.key)}">
                <div class="field-card-main">
                    <div class="field-card-header">
                        <span class="field-card-label">${escapeHtml(field.label)}</span>
                        <span class="field-badge type ${field.type}">${typeIcon} ${field.type}</span>
                        ${requiredBadge}
                        ${reminderBadge}
                    </div>
                    <div class="field-card-meta">
                        <code class="field-key">${escapeHtml(field.key)}</code>
                    </div>
                    ${valueHtml}
                </div>
                <div class="field-card-actions">
                    <button class="field-action-btn edit-field-btn" title="Edit">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                    </button>
                    <button class="field-action-btn delete-field-btn" title="Delete">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                        </svg>
                    </button>
                </div>
            </div>
        `;
    }

    elements.fieldsList.innerHTML = html;

    // Add edit handlers
    elements.fieldsList.querySelectorAll('.edit-field-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const card = btn.closest('.memory-field-card');
            const fieldKey = card.dataset.fieldKey;
            const field = memoryFields.find(f => f.key === fieldKey);
            if (field) showFieldEditor(field);
        });
    });

    // Add delete handlers
    elements.fieldsList.querySelectorAll('.delete-field-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
            const card = btn.closest('.memory-field-card');
            const fieldKey = card.dataset.fieldKey;
            const field = memoryFields.find(f => f.key === fieldKey);
            if (field) {
                const confirmed = await confirm({
                    title: 'Delete Field',
                    message: `Delete field "${field.label}"? Any stored values will be lost.`,
                    confirmText: 'Delete',
                    danger: true
                });
                if (confirmed) {
                    await deleteField(fieldKey);
                }
            }
        });
    });
}

/**
 * Show field editor dialog
 */
async function showFieldEditor(field) {
    editingField = field;
    elements.fieldEditorDialog?.classList.remove('hidden');

    // Reset form
    elements.fieldKey.value = field?.key || '';
    elements.fieldLabel.value = field?.label || '';
    elements.fieldRequired.checked = field?.required || false;
    if (elements.fieldReminder) elements.fieldReminder.checked = field?.reminder || false;
    elements.fieldDefault.value = field?.default || '';

    // Clear any previous error
    const errorEl = document.getElementById('field-editor-error');
    if (errorEl) errorEl.textContent = '';

    // Update title
    elements.fieldEditorTitle.textContent = field ? 'Edit Field' : 'Add Field';

    // Disable key editing for existing fields
    elements.fieldKey.disabled = !!field;

    // Focus appropriate field
    setTimeout(() => {
        if (field) {
            elements.fieldLabel?.focus();
        } else {
            elements.fieldKey?.focus();
        }
    }, 100);
}

/**
 * Hide field editor dialog
 */
function hideFieldEditor() {
    elements.fieldEditorDialog?.classList.add('hidden');
    editingField = null;
}

/**
 * Show inline error in field editor
 */
function showFieldError(msg) {
    const el = document.getElementById('field-editor-error');
    if (el) {
        el.textContent = msg;
        setTimeout(() => { if (el.textContent === msg) el.textContent = ''; }, 4000);
    }
}

/**
 * Handle save field
 */
async function handleSaveField() {
    const key = elements.fieldKey?.value.trim().toLowerCase().replace(/\s+/g, '_');
    const label = elements.fieldLabel?.value.trim();
    const type = 'text';
    const required = elements.fieldRequired?.checked || false;
    const reminder = elements.fieldReminder?.checked || false;
    const defaultValue = elements.fieldDefault?.value.trim() || null;

    if (!key) {
        showFieldError('Field key is required');
        elements.fieldKey?.focus();
        return;
    }

    if (!label) {
        showFieldError('Display label is required');
        elements.fieldLabel?.focus();
        return;
    }

    // Validate key format
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
        showFieldError('Key must be lowercase letters, numbers, and underscores');
        elements.fieldKey?.focus();
        return;
    }

    try {
        const headers = getHeaders();

        const body = { key, label, type, required, reminder, default: defaultValue };

        let response;
        if (editingField) {
            response = await fetch(
                `/agents/${encodeURIComponent(selectedAgent)}/schema/fields/${encodeURIComponent(editingField.key)}`,
                { method: 'PUT', headers, body: JSON.stringify(body) }
            );
        } else {
            response = await fetch(
                `/agents/${encodeURIComponent(selectedAgent)}/schema/fields`,
                { method: 'POST', headers, body: JSON.stringify(body) }
            );
        }

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to save field');
        }

        hideFieldEditor();
        await loadMemoryFields();
    } catch (error) {
        showFieldError(error.message);
    }
}

/**
 * Delete a field
 */
async function deleteField(fieldKey) {
    try {
        const headers = getHeaders(false);

        const response = await fetch(
            `/agents/${encodeURIComponent(selectedAgent)}/schema/fields/${encodeURIComponent(fieldKey)}`,
            { method: 'DELETE', headers }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete field');
        }

        await loadMemoryFields();
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Assign a KB to the selected agent
 */
async function assignKB(kbName) {
    if (!selectedAgent) return;

    try {
        const headers = getHeaders(false);

        const response = await fetch(
            `/agents/${encodeURIComponent(selectedAgent)}/kbs?kb_name=${encodeURIComponent(kbName)}`,
            { method: 'POST', headers }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to assign KB');
        }

        // Refresh the lists
        await loadAgentKBs();
        // Update agent count in list
        const agent = agents.find(a => a.name === selectedAgent);
        if (agent) agent.kb_count = assignedKBs.length;
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Unassign a KB from the selected agent
 */
async function unassignKB(kbName) {
    if (!selectedAgent) return;

    try {
        const headers = getHeaders(false);

        const response = await fetch(
            `/agents/${encodeURIComponent(selectedAgent)}/kbs/${encodeURIComponent(kbName)}`,
            { method: 'DELETE', headers }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to unassign KB');
        }

        // Refresh the lists
        await loadAgentKBs();
        // Update agent count in list
        const agent = agents.find(a => a.name === selectedAgent);
        if (agent) agent.kb_count = assignedKBs.length;
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Show create agent dialog
 */
function showCreateDialog() {
    elements.createDialog?.classList.remove('hidden');
    elements.newAgentName.value = '';
    elements.newAgentDescription.value = '';
    elements.newAgentName?.focus();
}

/**
 * Hide create agent dialog
 */
function hideCreateDialog() {
    elements.createDialog?.classList.add('hidden');
}

let copySourceAgent = null;

/**
 * Show copy agent dialog
 */
function showCopyDialog(agentName) {
    copySourceAgent = agentName;
    document.getElementById('agent-copy-title').textContent = `Copy "${agentName}"`;
    elements.copyAgentName.value = '';
    elements.copyIncludeData.checked = false;
    elements.copyDialog?.classList.remove('hidden');
    elements.copyAgentName?.focus();
}

/**
 * Hide copy agent dialog
 */
function hideCopyDialog() {
    elements.copyDialog?.classList.add('hidden');
    copySourceAgent = null;
}

/**
 * Handle copy agent confirm
 */
async function handleCopyAgent() {
    const newName = elements.copyAgentName?.value.trim();
    if (!newName) {
        elements.copyAgentName?.focus();
        return;
    }
    const sourceName = copySourceAgent;
    const includeData = elements.copyIncludeData?.checked || false;
    hideCopyDialog();
    await copyAgent(sourceName, newName, includeData);
}

/**
 * Handle create agent
 */
async function handleCreateAgent() {
    const name = elements.newAgentName?.value.trim();
    const description = elements.newAgentDescription?.value.trim();

    if (!name) {
        alert('Agent name is required');
        return;
    }

    try {
        const headers = getHeaders();

        const response = await fetch('/agents', {
            method: 'POST',
            headers,
            body: JSON.stringify({ name, description: description || null })
        });

        if (!response.ok) {
            let msg = 'Failed to create agent';
            try {
                const error = await response.json();
                msg = error.detail || msg;
            } catch { /* non-JSON response */ }
            throw new Error(msg);
        }

        hideCreateDialog();
        await fetchAgents();
        renderAgentList();
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Copy/duplicate an agent
 */
async function copyAgent(agentName, newName, includeData) {
    try {
        const headers = getHeaders();

        const response = await fetch(`/agents/${encodeURIComponent(agentName)}/copy`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ new_name: newName, include_data: includeData })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || 'Failed to copy agent');
        }

        const result = await response.json();
        await fetchAgents();
        renderAgentList();
        showToast(`Agent copied as "${result.name}"`);
    } catch (error) {
        showToast(error.message, 'error');
    }
}

/**
 * Export an agent as a ZIP download
 */
async function exportAgent(agentName) {
    const controller = new AbortController();

    // Show export overlay
    const overlay = document.createElement('div');
    overlay.className = 'export-overlay';
    overlay.innerHTML = `
        <div class="export-overlay-content">
            <div class="spinner"></div>
            <p>Exporting "${agentName}"...</p>
            <button class="btn-secondary export-cancel-btn">Cancel</button>
        </div>
    `;
    overlay.querySelector('.export-cancel-btn').addEventListener('click', () => controller.abort());
    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('active'));

    try {
        const headers = getHeaders(false);
        const response = await fetch(`/agents/${encodeURIComponent(agentName)}/export`, {
            method: 'GET',
            headers,
            signal: controller.signal
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || 'Failed to export agent');
        }

        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition') || '';
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch ? filenameMatch[1] : `${agentName}.zip`;

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        showToast(`Agent "${agentName}" exported`);
    } catch (error) {
        if (error.name === 'AbortError') {
            showToast('Export cancelled', 'error');
        } else {
            showToast(error.message, 'error');
        }
    } finally {
        overlay.classList.remove('active');
        setTimeout(() => overlay.remove(), 300);
    }
}

/**
 * Replace an existing agent from a user-picked file. Shows a destructive-action
 * confirmation modal, then uploads the file with overwrite_existing=true.
 */
async function replaceAgentFromFile(agentName) {
    // Build a hidden file picker
    const picker = document.createElement('input');
    picker.type = 'file';
    picker.accept = '.zip,.md';
    picker.style.display = 'none';
    document.body.appendChild(picker);

    const cleanup = () => picker.remove();

    picker.addEventListener('change', async () => {
        const file = picker.files && picker.files[0];
        cleanup();
        if (!file) return;

        const confirmed = await confirm({
            title: `Replace "${agentName}" from file?`,
            message:
                `This will overwrite "${agentName}"'s settings, system prompt, memory schema, ` +
                `stored memory values, table rows, and KB associations with the contents of ` +
                `"${file.name}". The existing configuration will be discarded.\n\n` +
                `A backup of the current canonical snapshot (if any) will be saved to ` +
                `agents/${agentName}.backup.zip — you can restore from it afterwards.`,
            confirmText: 'Replace',
            danger: true,
        });
        if (!confirmed) return;

        try {
            const headers = getHeaders(false);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('overwrite_existing', 'true');
            formData.append('target_name', agentName);
            const resp = await fetch('/agents/import', {
                method: 'POST',
                headers,
                body: formData,
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `Replace failed (${resp.status})`);
            }
            const result = await resp.json();
            if (result.status === 'replaced') {
                showToast(`"${agentName}" replaced from ${file.name}`);
            } else if (result.status === 'renamed') {
                showToast(`Replace fell back to rename → ${result.name}`, 'error');
            } else {
                showToast(`Imported as ${result.name}`);
            }
            (result.warnings || []).forEach(w => showToast(w, 'error'));
            // Refresh the agent list so badges + has_backup update
            await fetchAgents();
            renderAgentList();
        } catch (e) {
            showToast(e.message || 'Replace failed', 'error');
        }
    });

    picker.click();
}

/**
 * Restore an agent from agents/<name>.backup.zip. The backup file lives on
 * the server; this endpoint reads it and applies it to the DB.
 */
async function restoreAgentFromBackup(agentName) {
    const confirmed = await confirm({
        title: `Restore "${agentName}" from backup?`,
        message:
            `This will overwrite "${agentName}"'s current configuration with the contents of ` +
            `agents/${agentName}.backup.zip. The current state will be discarded.\n\n` +
            `The backup file itself is not modified — you can re-restore it later if needed.`,
        confirmText: 'Restore',
        danger: true,
    });
    if (!confirmed) return;

    try {
        const headers = getHeaders();
        const resp = await fetch(`/agents/${encodeURIComponent(agentName)}/restore-backup`, {
            method: 'POST',
            headers,
        });
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.detail || `Restore failed (${resp.status})`);
        }
        const result = await resp.json();
        showToast(`"${agentName}" restored from backup`);
        if (result.warning) showToast(result.warning, 'error');
        await fetchAgents();
        renderAgentList();
    } catch (e) {
        showToast(e.message || 'Restore failed', 'error');
    }
}

/**
 * Show/hide agent import dialog
 */
function showAgentImportDialog() {
    const dialog = document.getElementById('agent-import-dialog');
    if (!dialog) return;
    agentImportFile = null;
    const fileInput = document.getElementById('agent-import-file-input');
    if (fileInput) fileInput.value = '';
    document.getElementById('agent-import-file-info')?.classList.add('hidden');
    document.getElementById('agent-import-dropzone')?.classList.remove('hidden');
    document.getElementById('agent-import-name').value = '';
    document.getElementById('agent-import-data').checked = true;
    document.getElementById('agent-import-kbs').checked = true;
    const errorEl = document.getElementById('agent-import-error');
    if (errorEl) errorEl.textContent = '';
    const submitBtn = document.getElementById('agent-import-submit');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Import'; }
    dialog.classList.remove('hidden');
}

function hideAgentImportDialog() {
    const dialog = document.getElementById('agent-import-dialog');
    if (dialog) dialog.classList.add('hidden');
    agentImportFile = null;
}

function handleAgentImportFileSelect(file) {
    if (!file.name.toLowerCase().endsWith('.zip')) {
        showToast('Please select a .zip file', 'error');
        return;
    }
    agentImportFile = file;
    document.getElementById('agent-import-file-name').textContent = file.name;
    document.getElementById('agent-import-file-info')?.classList.remove('hidden');
    document.getElementById('agent-import-dropzone')?.classList.add('hidden');
    document.getElementById('agent-import-submit').disabled = false;
    const errorEl = document.getElementById('agent-import-error');
    if (errorEl) errorEl.textContent = '';
}

async function handleAgentImport() {
    if (!agentImportFile) return;

    const controller = new AbortController();
    const file = agentImportFile;
    const fileName = file.name;

    // Capture form values before hiding dialog
    const nameOverride = document.getElementById('agent-import-name')?.value.trim();
    const includeData = document.getElementById('agent-import-data')?.checked ? 'true' : 'false';
    const includeKbs = document.getElementById('agent-import-kbs')?.checked ? 'true' : 'false';

    // Hide import dialog and show overlay
    hideAgentImportDialog();
    const overlay = document.createElement('div');
    overlay.className = 'export-overlay';
    overlay.innerHTML = `
        <div class="export-overlay-content">
            <div class="spinner"></div>
            <p>Importing "${fileName}"...</p>
            <button class="btn-secondary export-cancel-btn">Cancel</button>
        </div>
    `;
    overlay.querySelector('.export-cancel-btn').addEventListener('click', () => controller.abort());
    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('active'));

    try {
        const formData = new FormData();
        formData.append('file', file);
        if (nameOverride) formData.append('name_override', nameOverride);
        formData.append('include_data', includeData);
        formData.append('include_kbs', includeKbs);

        const headers = getHeaders(false);
        const response = await fetch('/agents/import', {
            method: 'POST',
            headers,
            body: formData,
            signal: controller.signal
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.detail || 'Import failed');
        }

        const result = await response.json();
        await fetchAgents();
        renderAgentList();

        let msg = `Agent "${result.name}" imported`;
        if (result.kb_results) {
            const created = Object.values(result.kb_results).filter(s => s === 'created').length;
            if (created > 0) msg += ` with ${created} KB${created > 1 ? 's' : ''}`;
        }
        if (result.warnings?.length) msg += ` (${result.warnings.length} warning${result.warnings.length > 1 ? 's' : ''})`;
        showToast(msg);
    } catch (error) {
        if (error.name === 'AbortError') {
            showToast('Import cancelled', 'error');
        } else {
            showToast(error.message || String(error), 'error');
        }
    } finally {
        overlay.classList.remove('active');
        setTimeout(() => overlay.remove(), 300);
    }
}

/**
 * Import a SKILL.md into the currently open agent.
 * Backend parses and saves directly, then we refresh the UI.
 */
async function handleSkillImport(file) {
    if (!file.name.toLowerCase().endsWith('.md')) {
        showToast('Please select a .md file', 'error');
        return;
    }
    if (!selectedAgent) {
        showToast('No agent selected', 'error');
        return;
    }

    try {
        const formData = new FormData();
        formData.append('file', file);

        const headers = getHeaders(false);
        const response = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/import-skill`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.detail || 'Skill import failed');
        }

        const result = await response.json();

        // Refresh UI to show saved changes
        await loadAgentSettings();
        await loadMemoryFields();

        let msg = `Skill "${result.name}" imported`;
        if (result.tools?.length) msg += ` with ${result.tools.length} tool${result.tools.length > 1 ? 's' : ''}`;
        if (result.unmapped_tools?.length) msg += ` (${result.unmapped_tools.length} unmapped)`;
        showToast(msg);

    } catch (error) {
        showToast(error.message || String(error), 'error');
    }
}

/**
 * Delete an agent
 */
async function deleteAgent(agentName) {
    try {
        const headers = getHeaders(false);

        const response = await fetch(`/agents/${encodeURIComponent(agentName)}`, {
            method: 'DELETE',
            headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete agent');
        }

        await fetchAgents();
        renderAgentList();
    } catch (error) {
        showToast(error.message, 'error');
    }
}

/**
 * Format display name (convert kebab-case to Title Case)
 */
function formatDisplayName(name) {
    if (!name) return '';
    return name
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

const TOOL_DISPLAY_NAMES = {
    calculator: 'Calc', datetime: 'DateTime', python_exec: 'Python',
    shell_exec: 'Shell', file_ops: 'Files', web_fetch: 'Web Fetch',
    http_request: 'HTTP', email_send: 'Email', email_fetch: 'Email Fetch', image_generate: 'Image Gen', scholar: 'Scholar',
    web_search: 'Web Search', rag_search: 'RAG Search',
    agent_db: 'Memory', doc_create: 'Doc Create', vision: 'Vision', text_cleanup: 'Proofread'
};

function renderAgentChips(agent) {
    const chips = [];
    const kbs = agent.kbs || [];
    const tools = agent.tools || [];

    for (const kb of kbs) {
        const name = kb.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        chips.push(`<span class="context-card-chip kb" title="${escapeHtml(kb)}">${escapeHtml(name)}</span>`);
    }
    for (const tool of tools) {
        const name = TOOL_DISPLAY_NAMES[tool] || tool;
        chips.push(`<span class="context-card-chip tool">${escapeHtml(name)}</span>`);
    }
    return chips.length ? `<div class="context-card-chips">${chips.join('')}</div>` : '';
}

/**
 * Escape HTML to prevent XSS
 */

// ============================================================================
// Agent Database Functions
// ============================================================================

/**
 * Load database state: schema tables + check if tool is enabled
 */
async function loadDbState() {
    if (!selectedAgent) return;

    try {
        const headers = getHeaders(false);

        // Fetch schema (has tables) and settings (has tools list) in parallel
        const [schemaRes, settingsRes] = await Promise.all([
            fetch(`/agents/${encodeURIComponent(selectedAgent)}/schema`, { headers }),
            fetch(`/agents/${encodeURIComponent(selectedAgent)}/settings`, { headers })
        ]);

        if (schemaRes.ok) {
            const schemaData = await schemaRes.json();
            dbTables = schemaData.schema?.tables || [];
        } else {
            dbTables = [];
        }

        if (settingsRes.ok) {
            const settingsData = await settingsRes.json();
            const tools = settingsData.settings?.tools || [];
            dbEnabled = tools.includes('agent_db');
        } else {
            dbEnabled = false;
        }
    } catch (error) {
        console.error('Error loading DB state:', error);
        dbTables = [];
        dbEnabled = false;
    }

    renderDbAccordion();

    // If a table was previously selected, reload its rows (data may have changed via chat)
    if (dbSelectedTable) {
        if (dbTables.some(t => t.key === dbSelectedTable)) {
            await selectDbTable(dbSelectedTable);
        } else {
            dbSelectedTable = null;  // Table no longer exists
        }
    }
}

/**
 * Render the entire database accordion content
 */
function renderDbAccordion() {
    // Update toggle state
    if (elements.dbEnableToggle) {
        elements.dbEnableToggle.checked = dbEnabled;
    }

    // Show/hide DB content and default-on row based on toggle
    if (elements.dbContent) {
        elements.dbContent.classList.toggle('hidden', !dbEnabled);
    }
    if (elements.dbDefaultOnRow) {
        elements.dbDefaultOnRow.style.display = dbEnabled ? '' : 'none';
    }

    // Update accordion subtitle
    if (elements.dbAccordionSubtitle) {
        if (dbEnabled) {
            elements.dbAccordionSubtitle.textContent = `${dbTables.length} table${dbTables.length !== 1 ? 's' : ''}`;
        } else {
            elements.dbAccordionSubtitle.textContent = 'disabled';
        }
    }

    renderDbTableList();
}

/**
 * Compute table hierarchy tiers from reference relationships.
 * Returns { tiers: {key: tierNum}, edges: [{from, to}], maxTier }
 */
function computeTableTiers(tables) {
    const tableKeys = new Set(tables.map(t => t.key));
    const parentOf = {};  // childKey -> Set of parent keys
    for (const t of tables) parentOf[t.key] = new Set();

    const edges = [];
    for (const t of tables) {
        for (const col of (t.columns || [])) {
            if (col.type === 'reference' && col.references_table && tableKeys.has(col.references_table) && col.references_table !== t.key) {
                parentOf[t.key].add(col.references_table);
                edges.push({ from: col.references_table, to: t.key });
            }
        }
    }

    // Iterative tier assignment (BFS from roots)
    const tiers = {};
    const assigned = new Set();
    let currentTier = 0;
    let batch = tables.filter(t => parentOf[t.key].size === 0).map(t => t.key);

    while (batch.length > 0) {
        for (const key of batch) {
            tiers[key] = currentTier;
            assigned.add(key);
        }
        currentTier++;
        batch = tables
            .filter(t => !assigned.has(t.key) && [...parentOf[t.key]].every(p => assigned.has(p)))
            .map(t => t.key);
    }
    // Cycles: assign unassigned to last tier
    for (const t of tables) {
        if (!assigned.has(t.key)) tiers[t.key] = currentTier;
    }

    return { tiers, edges, maxTier: Math.max(0, currentTier - 1) };
}

/**
 * Build HTML for a single table card
 */
function buildTableCardHTML(table) {
    const colCount = (table.columns || []).length;
    const isSelected = dbSelectedTable === table.key;
    return `
        <div class="db-table-card ${isSelected ? 'selected' : ''}" data-table-key="${escapeHtml(table.key)}">
            <div class="db-table-card-main">
                <span class="db-table-card-label">${escapeHtml(table.label || table.key)}</span>
                <code class="db-table-card-key">${escapeHtml(table.key)}</code>
            </div>
            <div class="db-table-card-meta">
                <span class="db-badge">${colCount} col${colCount !== 1 ? 's' : ''}</span>
            </div>
            <div class="db-table-card-actions">
                <button class="field-action-btn db-edit-table-btn" title="Edit schema" data-table-key="${escapeHtml(table.key)}">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                </button>
                <button class="field-action-btn delete-field-btn db-delete-table-btn" title="Delete table" data-table-key="${escapeHtml(table.key)}">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                </button>
            </div>
        </div>
    `;
}

/**
 * Attach click handlers to table cards
 */
function attachTableCardHandlers() {
    elements.dbTableList.querySelectorAll('.db-table-card').forEach(card => {
        card.addEventListener('click', (e) => {
            if (e.target.closest('.db-edit-table-btn') || e.target.closest('.db-delete-table-btn')) return;
            selectDbTable(card.dataset.tableKey);
        });
    });
    elements.dbTableList.querySelectorAll('.db-edit-table-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const table = dbTables.find(t => t.key === btn.dataset.tableKey);
            if (table) showTableEditor(table);
        });
    });
    elements.dbTableList.querySelectorAll('.db-delete-table-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            handleDeleteTable(btn.dataset.tableKey);
        });
    });
}

/**
 * Draw SVG relationship lines between connected table cards
 */
function drawRelationshipLines(edges) {
    const container = elements.dbTableList;
    if (!container) return;

    // Remove existing SVG
    clearRelationshipLines();
    if (edges.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add('db-relationship-svg');
    svg.setAttribute('width', container.scrollWidth);
    svg.setAttribute('height', container.scrollHeight);
    svg.setAttribute('viewBox', `0 0 ${container.scrollWidth} ${container.scrollHeight}`);

    for (const edge of edges) {
        const fromCard = container.querySelector(`.db-table-card[data-table-key="${CSS.escape(edge.from)}"]`);
        const toCard = container.querySelector(`.db-table-card[data-table-key="${CSS.escape(edge.to)}"]`);
        if (!fromCard || !toCard) continue;

        const fromRect = fromCard.getBoundingClientRect();
        const toRect = toCard.getBoundingClientRect();

        // Coordinates relative to container
        const x1 = (fromRect.left + fromRect.width / 2) - containerRect.left;
        const y1 = fromRect.bottom - containerRect.top;
        const x2 = (toRect.left + toRect.width / 2) - containerRect.left;
        const y2 = toRect.top - containerRect.top;

        // Cubic bezier curve
        const midY = (y1 + y2) / 2;
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`);
        path.classList.add('db-relationship-line');
        svg.appendChild(path);

        // Connection dots
        for (const [cx, cy] of [[x1, y1], [x2, y2]]) {
            const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            dot.setAttribute('cx', cx);
            dot.setAttribute('cy', cy);
            dot.setAttribute('r', 3);
            dot.classList.add('db-relationship-dot');
            svg.appendChild(dot);
        }
    }

    container.insertBefore(svg, container.firstChild);
}

/**
 * Remove SVG relationship overlay
 */
function clearRelationshipLines() {
    const svg = elements.dbTableList?.querySelector('.db-relationship-svg');
    if (svg) svg.remove();
}

// ResizeObserver for relationship lines
let _dbResizeObserver = null;

/**
 * Render table cards with tiered layout when relationships exist
 */
function renderDbTableList() {
    if (!elements.dbTableList) return;

    if (dbTables.length === 0) {
        elements.dbTableList.innerHTML = `
            <div class="db-empty-state">
                <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.5">
                    <ellipse cx="12" cy="5" rx="9" ry="3"/>
                    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                </svg>
                <p>No tables yet</p>
                <span>Create a table to give your agent structured data storage</span>
            </div>
        `;
        elements.dbTableList.classList.remove('db-table-list--tiered');
        clearRelationshipLines();
        if (elements.dbTableDetail) elements.dbTableDetail.classList.add('hidden');
        return;
    }

    const { tiers, edges, maxTier } = computeTableTiers(dbTables);
    const hasRelationships = edges.length > 0 && maxTier > 0;

    let html = '';
    if (hasRelationships) {
        for (let tier = 0; tier <= maxTier; tier++) {
            const tierTables = dbTables.filter(t => tiers[t.key] === tier);
            if (tierTables.length === 0) continue;
            html += `<div class="db-table-tier" data-tier="${tier}">`;
            for (const table of tierTables) html += buildTableCardHTML(table);
            html += `</div>`;
        }
    } else {
        for (const table of dbTables) html += buildTableCardHTML(table);
    }

    elements.dbTableList.innerHTML = html;
    elements.dbTableList.classList.toggle('db-table-list--tiered', hasRelationships);
    attachTableCardHandlers();

    // Draw relationship lines after layout settles
    if (hasRelationships) {
        requestAnimationFrame(() => drawRelationshipLines(edges));

        // Set up resize observer for redraw
        if (!_dbResizeObserver) {
            _dbResizeObserver = new ResizeObserver(() => {
                clearTimeout(elements.dbTableList._resizeTimer);
                elements.dbTableList._resizeTimer = setTimeout(() => {
                    const { edges: e } = computeTableTiers(dbTables);
                    if (e.length > 0) drawRelationshipLines(e);
                }, 100);
            });
            _dbResizeObserver.observe(elements.dbTableList);
        }
    } else {
        clearRelationshipLines();
        if (_dbResizeObserver) {
            _dbResizeObserver.disconnect();
            _dbResizeObserver = null;
        }
    }
}

/**
 * Select a table and load its rows
 */
async function selectDbTable(tableKey) {
    dbSelectedTable = tableKey;
    dbCurrentPage = 1;
    dbSortColumn = null;
    dbSortDirection = null;

    // Re-render table list for selection highlight
    renderDbTableList();

    if (!elements.dbTableDetail) return;

    const hasExisting = !elements.dbTableDetail.classList.contains('hidden') && elements.dbTableDetail.innerHTML.trim();

    if (hasExisting) {
        // Overlay spinner on current content
        elements.dbTableDetail.classList.add('db-table-detail--loading');
        let spinner = elements.dbTableDetail.querySelector('.db-table-loading-spinner');
        if (!spinner) {
            spinner = document.createElement('div');
            spinner.className = 'db-table-loading-spinner';
            elements.dbTableDetail.appendChild(spinner);
        }
    } else {
        // First load — show simple loading message
        elements.dbTableDetail.classList.remove('hidden');
        elements.dbTableDetail.innerHTML = '<div class="db-loading">Loading rows...</div>';
    }

    try {
        const headers = getHeaders(false);

        const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(tableKey)}`, { headers });
        if (!res.ok) throw new Error('Failed to fetch rows');
        const data = await res.json();
        dbTableRows = data.rows || [];
    } catch (error) {
        console.error('Error fetching table rows:', error);
        dbTableRows = [];
    }

    // Remove overlay
    elements.dbTableDetail.classList.remove('db-table-detail--loading');
    elements.dbTableDetail.querySelector('.db-table-loading-spinner')?.remove();

    await renderDbTableData();

    // Smooth scroll to show the data grid
    setTimeout(() => {
        elements.dbTableDetail?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
}

/**
 * Render data grid for selected table
 */
// Observer for the live table, so column widths that settle after layout are
// picked up. Held at module scope purely so the previous one can be dropped:
// each render replaces the table node, and an observer left pointing at the
// old one keeps firing against a detached element.
let dbScrollObserver = null;

function wireDbHorizontalScroll(tableKey) {
    const root = elements.dbTableDetail;
    if (!root) return;
    const wrap = root.querySelector('.db-data-table-wrap');
    const rail = root.querySelector('.db-scroll-rail');
    const inner = rail && rail.querySelector('.db-scroll-rail-inner');
    const table = wrap && wrap.querySelector('.db-data-table');
    if (dbScrollObserver) { dbScrollObserver.disconnect(); dbScrollObserver = null; }
    if (!wrap || !table) return;

    // The rail scrolls a spacer exactly as wide as the table, which is what
    // makes its thumb the same size and travel as the real one underneath.
    const measure = () => {
        const w = table.scrollWidth;
        if (inner) inner.style.width = w + 'px';
        // Nothing to scroll: hide the rail rather than show a dead trough.
        if (rail) rail.classList.toggle('db-scroll-rail-idle', w <= wrap.clientWidth + 1);
    };
    measure();

    // Restore where the operator was. Done before any listener is attached so
    // the restore itself is not mistaken for the user scrolling.
    const saved = dbScrollLeft[tableKey] || 0;
    if (saved > 0) {
        wrap.scrollLeft = saved;
        if (rail) rail.scrollLeft = saved;
    }

    // Mirror the two, and remember the position. The guard matters: assigning
    // scrollLeft fires 'scroll' on the other element, which would assign back
    // and leave the pair fighting each other mid-drag.
    let mirroring = false;
    const follow = (from, to) => {
        from.addEventListener('scroll', () => {
            if (mirroring) return;
            mirroring = true;
            if (to) to.scrollLeft = from.scrollLeft;
            dbScrollLeft[tableKey] = from.scrollLeft;
            mirroring = false;
        }, { passive: true });
    };
    follow(wrap, rail);
    if (rail) follow(rail, wrap);

    // Column widths depend on content that is laid out after this runs, so the
    // first measurement is usually short. Re-measure once the browser has.
    requestAnimationFrame(measure);
    if (window.ResizeObserver) {
        dbScrollObserver = new ResizeObserver(measure);
        dbScrollObserver.observe(table);
        dbScrollObserver.observe(wrap);
    }
}

async function renderDbTableData() {
    if (!elements.dbTableDetail || !dbSelectedTable) return;

    const tableDef = dbTables.find(t => t.key === dbSelectedTable);
    if (!tableDef) {
        elements.dbTableDetail.classList.add('hidden');
        return;
    }

    const columns = tableDef.columns || [];
    const tableLabel = tableDef.label || tableDef.key;
    const totalRows = dbTableRows.length;

    // Sort rows if a sort column is active
    let sortedRows;
    if (dbSortColumn && dbSortDirection) {
        const colDef = columns.find(c => c.key === dbSortColumn);
        const colType = colDef ? colDef.type : 'text';
        const dir = dbSortDirection === 'asc' ? 1 : -1;

        sortedRows = [...dbTableRows].sort((a, b) => {
            let va, vb;
            if (dbSortColumn === '__id') {
                va = a.id;
                vb = b.id;
            } else {
                va = (a.row_data || {})[dbSortColumn];
                vb = (b.row_data || {})[dbSortColumn];
            }

            // Nulls sort to the bottom regardless of direction
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;

            let cmp = 0;
            if (colType === 'number' || colType === 'reference' || dbSortColumn === '__id') {
                cmp = Number(va) - Number(vb);
            } else if (colType === 'boolean') {
                cmp = (va === true || va === 1 ? 1 : 0) - (vb === true || vb === 1 ? 1 : 0);
            } else if (colType === 'date') {
                cmp = new Date(va).getTime() - new Date(vb).getTime();
            } else {
                cmp = String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
            }
            return cmp * dir;
        });
    } else {
        sortedRows = dbTableRows;
    }

    const totalPages = Math.max(1, Math.ceil(totalRows / dbPageSize));

    // Clamp current page
    if (dbCurrentPage > totalPages) dbCurrentPage = totalPages;
    if (dbCurrentPage < 1) dbCurrentPage = 1;

    const startIdx = (dbCurrentPage - 1) * dbPageSize;
    const pageRows = sortedRows.slice(startIdx, startIdx + dbPageSize);

    // Resolve reference columns for display
    const refColumns = columns.filter(c => c.type === 'reference' && c.references_table);
    const refDisplayMaps = {}; // { col_key: { id_str: display_name } }
    // The one reference column that gets a jump-to-edit pencil, and only if
    // the table it points at is still part of this agent — a pencil that
    // opens nothing is worse than no pencil.
    const linkedCol = refColumns.find(c => dbTables.some(t => t.key === c.references_table)) || null;
    const linkedColKey = linkedCol ? linkedCol.key : null;
    const linkedRefTable = linkedCol ? linkedCol.references_table : '';
    const linkedColLabel = linkedCol ? (linkedCol.label || linkedCol.key) : '';

    if (refColumns.length > 0 && pageRows.length > 0) {
        const headers = getHeaders(false);

        for (const rc of refColumns) {
            const ids = new Set();
            for (const row of pageRows) {
                const val = (row.row_data || {})[rc.key];
                if (val !== null && val !== undefined && val !== '') {
                    ids.add(parseInt(val));
                }
            }
            if (ids.size === 0) continue;

            try {
                const res = await fetch(
                    `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(rc.references_table)}/resolve`,
                    { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ ids: [...ids].filter(id => !isNaN(id)) }) }
                );
                if (res.ok) {
                    const data = await res.json();
                    refDisplayMaps[rc.key] = data.resolved || {};
                }
            } catch (e) { /* fallback to raw IDs */ }
        }
    }

    let html = `<div class="db-table-detail-inner">
        <div class="db-detail-header">
            <h4>${escapeHtml(tableLabel)}</h4>
            <span class="db-badge">${totalRows} row${totalRows !== 1 ? 's' : ''}</span>
            <div class="db-detail-header-actions">
                <button class="btn-secondary db-import-btn" title="Import CSV/XLSX">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    <span class="btn-label">Import</span>
                </button>
                <div class="db-export-dropdown">
                    <button class="btn-secondary db-export-btn" title="Export data"${totalRows === 0 ? ' disabled' : ''}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        <span class="btn-label">Export</span>
                    </button>
                    <div class="db-export-menu hidden">
                        <button class="db-export-option" data-format="csv">CSV</button>
                        <button class="db-export-option" data-format="xlsx">XLSX</button>
                    </div>
                </div>
                <button class="kb-create-btn db-add-row-btn">+ Add Row</button>
            </div>
        </div>
    `;

    if (totalRows === 0) {
        html += `<div class="db-empty-rows">No rows yet. Click "+ Add Row" to add data.</div>`;
    } else {
        // A second scrollbar ABOVE the table, mirroring the real one below.
        // A wide table's only horizontal scrollbar sits under its last row, so
        // reaching it meant scrolling to the bottom of the page first — and on
        // a 100-row page the columns you were trying to reach had scrolled out
        // of sight by the time you got there. This is a plain div whose inner
        // spacer is as wide as the table; the two are kept in step below.
        html += '<div class="db-scroll-rail" aria-hidden="true"><div class="db-scroll-rail-inner"></div></div>';
        html += '<div class="db-data-table-wrap"><table class="db-data-table"><thead><tr>';
        // ID column (sortable)
        html += `<th class="db-sortable-th${dbSortColumn === '__id' ? ' db-sorted' : ''}" data-sort-key="__id">ID${dbSortColumn === '__id' ? (dbSortDirection === 'asc' ? ' &#9650;' : ' &#9660;') : ''}</th>`;
        // Actions column (not sortable)
        html += '<th></th>';
        // Data columns (sortable)
        for (const col of columns) {
            const isSorted = dbSortColumn === col.key;
            const arrow = isSorted ? (dbSortDirection === 'asc' ? ' &#9650;' : ' &#9660;') : '';
            // The linked column's values start one pencil-width in, so its
            // header has to as well — otherwise the label lines up with the
            // pencil slot instead of the names underneath it.
            const refTh = (linkedColKey && col.key === linkedColKey) ? ' db-ref-th' : '';
            html += `<th class="db-sortable-th${isSorted ? ' db-sorted' : ''}${refTh}" data-sort-key="${escapeHtml(col.key)}">${escapeHtml(col.label || col.key)}${arrow}</th>`;
        }
        html += '</tr></thead><tbody>';

        for (const row of pageRows) {
            const rd = row.row_data || {};
            html += `<tr data-row-id="${row.id}">`;
            html += `<td class="db-id-cell">${row.id}</td>`;
            html += `<td class="db-row-actions">
                <button class="field-action-btn db-edit-row-btn" title="Edit" data-row-id="${row.id}">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                </button>
                <button class="field-action-btn delete-field-btn db-delete-row-btn" title="Delete" data-row-id="${row.id}">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                </button>
            </td>`;
            for (const col of columns) {
                const val = rd[col.key];
                let display;
                if (col.type === 'reference' && refDisplayMaps[col.key] && val != null && val !== '') {
                    display = refDisplayMaps[col.key][String(val)] || `#${val}`;
                } else {
                    display = val !== undefined && val !== null ? String(val) : '-';
                }
                const truncated = display.length > 80 ? display.substring(0, 80) + '...' : display;
                // The first joined column carries a pencil straight to the row
                // it points at, in the table that row lives in. Following a
                // reference otherwise means leaving this table, finding the
                // row, editing it, and navigating back — losing the sort, the
                // page and the scroll position on the way. Only the first
                // reference gets one: a row with four joins and four pencils
                // is a row you cannot read.
                if (linkedColKey && col.key === linkedColKey) {
                    // The pencil leads, and every row in this column reserves
                    // the same slot for it — including rows with no value. A
                    // slot that collapses when empty ragged the whole column
                    // and put the names on two different left edges.
                    const pencil = (val != null && val !== '')
                        ? `<button class="db-ref-edit-btn" title="Edit this ${escapeHtml(linkedColLabel)}"`
                          + ` data-ref-table="${escapeHtml(linkedRefTable)}" data-ref-id="${escapeHtml(String(val))}">`
                          + `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2">`
                          + `<path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>`
                          + `<path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>`
                        : `<span class="db-ref-edit-spacer" aria-hidden="true"></span>`;
                    html += `<td class="db-ref-cell">${pencil}`
                        + `<span class="db-ref-text">${escapeHtml(truncated)}</span></td>`;
                } else {
                    html += `<td>${escapeHtml(truncated)}</td>`;
                }
            }
            html += `</tr>`;
        }

        html += '</tbody></table></div>';

        // Table footer: page size selector + pagination
        html += '<div class="db-table-footer">';

        // Max rows dropdown (left)
        html += `<div class="db-page-size-wrap">
            <label class="db-page-size-label">Show</label>
            <select class="db-page-size-select">
                ${[10, 25, 50, 100].map(n =>
                    `<option value="${n}" ${dbPageSize === n ? 'selected' : ''}>${n}</option>`
                ).join('')}
            </select>
            <span class="db-page-size-label">rows</span>
        </div>`;

        // Pagination controls (center, only if >1 page)
        if (totalPages > 1) {
            html += `
                <div class="db-pagination">
                    <button class="db-page-btn" data-page="prev" ${dbCurrentPage <= 1 ? 'disabled' : ''}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                    <span class="db-page-info">Page ${dbCurrentPage} of ${totalPages}</span>
                    <button class="db-page-btn" data-page="next" ${dbCurrentPage >= totalPages ? 'disabled' : ''}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                </div>
            `;
        } else {
            html += '<div class="db-pagination"></div>';
        }

        html += '</div>'; // close .db-table-footer
    }

    html += `</div>`; // close .db-table-detail-inner

    elements.dbTableDetail.innerHTML = html;

    wireDbHorizontalScroll(dbSelectedTable);

    // Add Row button
    elements.dbTableDetail.querySelector('.db-add-row-btn')?.addEventListener('click', () => {
        showRowEditor(null, tableDef);
    });

    // Jump to the referenced row, in its own table, without leaving this one.
    elements.dbTableDetail.querySelectorAll('.db-ref-edit-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            // The cell is inside a row that has its own click behaviour, and
            // this pencil means "edit the OTHER row", not this one.
            e.stopPropagation();
            btn.disabled = true;
            try {
                await openRelatedRowEditor(btn.dataset.refTable, parseInt(btn.dataset.refId, 10));
            } finally {
                btn.disabled = false;
            }
        });
    });

    // Edit row
    elements.dbTableDetail.querySelectorAll('.db-edit-row-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const row = dbTableRows.find(r => r.id === parseInt(btn.dataset.rowId));
            if (row) showRowEditor(row, tableDef);
        });
    });

    // Delete row
    elements.dbTableDetail.querySelectorAll('.db-delete-row-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            handleDeleteRow(parseInt(btn.dataset.rowId));
        });
    });

    // Export dropdown
    const exportBtn = elements.dbTableDetail.querySelector('.db-export-btn');
    const exportMenu = elements.dbTableDetail.querySelector('.db-export-menu');
    if (exportBtn && exportMenu) {
        exportBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            exportMenu.classList.toggle('hidden');
        });
        document.addEventListener('click', () => exportMenu.classList.add('hidden'), { once: true });
    }
    elements.dbTableDetail.querySelectorAll('.db-export-option').forEach(btn => {
        btn.addEventListener('click', () => {
            exportMenu?.classList.add('hidden');
            handleExportTable(btn.dataset.format);
        });
    });

    // Import button
    elements.dbTableDetail.querySelector('.db-import-btn')?.addEventListener('click', () => {
        showImportDialog();
    });

    // Pagination buttons
    elements.dbTableDetail.querySelectorAll('.db-page-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            if (btn.dataset.page === 'prev' && dbCurrentPage > 1) {
                dbCurrentPage--;
            } else if (btn.dataset.page === 'next') {
                dbCurrentPage++;
            }
            renderDbTableData();
            elements.dbTableDetail?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    });

    // Sortable column headers (three-click cycle: asc → desc → clear)
    elements.dbTableDetail.querySelectorAll('.db-sortable-th').forEach(th => {
        th.addEventListener('click', () => {
            const key = th.dataset.sortKey;
            if (dbSortColumn === key) {
                if (dbSortDirection === 'asc') {
                    dbSortDirection = 'desc';
                } else {
                    dbSortColumn = null;
                    dbSortDirection = null;
                }
            } else {
                dbSortColumn = key;
                dbSortDirection = 'asc';
            }
            dbCurrentPage = 1;
            renderDbTableData();
        });
    });

    // Page size selector
    const pageSizeSelect = elements.dbTableDetail.querySelector('.db-page-size-select');
    if (pageSizeSelect) {
        pageSizeSelect.addEventListener('change', () => {
            dbPageSize = parseInt(pageSizeSelect.value, 10);
            dbCurrentPage = 1;
            renderDbTableData();
        });
    }
}

/**
 * Handle enable/disable database toggle
 */
async function handleDbEnableToggle() {
    const enabled = elements.dbEnableToggle?.checked || false;

    // Confirm before disabling if tables exist
    if (!enabled && dbTables.length > 0) {
        const confirmed = confirm(`Disable the database tool? The agent will lose access to ${dbTables.length} table${dbTables.length !== 1 ? 's' : ''} during chat. Tables and data are preserved.`);
        if (!confirmed) {
            if (elements.dbEnableToggle) elements.dbEnableToggle.checked = true;
            return;
        }
    }

    try {
        const headers = getHeaders();

        // Load current tools list
        const settingsRes = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/settings`, { headers });
        if (!settingsRes.ok) throw new Error('Failed to fetch settings');
        const settingsData = await settingsRes.json();
        let tools = settingsData.settings?.tools || [];

        if (enabled && !tools.includes('agent_db')) {
            tools.push('agent_db');
        } else if (!enabled) {
            tools = tools.filter(t => t !== 'agent_db');
        }

        // Save updated tools list
        const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/settings`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ key: 'tools', value: tools })
        });
        if (!res.ok) throw new Error('Failed to save tools setting');

        dbEnabled = enabled;
    } catch (error) {
        console.error('Error toggling database:', error);
        // Revert toggle
        if (elements.dbEnableToggle) elements.dbEnableToggle.checked = !enabled;
    }

    renderDbAccordion();
}

// --- Recording Picker ---

async function showRecordingPicker() {
    if (!selectedAgent) return;

    // Fetch processed recordings
    let recordings;
    try {
        const res = await fetch('/import/audio/processed', { headers: getHeaders(false) });
        if (!res.ok) throw new Error('Failed to fetch recordings');
        recordings = await res.json();
    } catch (err) {
        showToast('Failed to load recordings', 'error');
        return;
    }

    if (!recordings.length) {
        showToast('No processed recordings found', 'error');
        return;
    }

    // Check which recordings are already imported into this agent
    const existingTitles = new Set();
    const recTable = dbTables.find(t => t.key === 'recordings');
    if (recTable && dbSelectedTable === 'recordings') {
        // If we already have the rows loaded, check titles
        document.querySelectorAll('.db-data-table tbody tr td:first-child').forEach(td => {
            existingTitles.add(td.textContent.trim());
        });
    }

    // Pagination state — render 20 at a time
    const PAGE_SIZE = 20;
    let renderedCount = 0;

    const renderItem = (r) => {
        const title = r.title || r.stem.replace(/_/g, ' ').replace(/-/g, ' ');
        return `
            <button class="recording-picker-item" data-stem="${r.stem}">
                <div class="recording-picker-info">
                    <span class="recording-picker-title">${title}</span>
                    <span class="recording-picker-meta">
                        ${r.duration_formatted || ''} &middot; ${r.speaker_count} speaker${r.speaker_count !== 1 ? 's' : ''}
                        ${r.has_summary ? ' &middot; has summary' : ''}
                    </span>
                </div>
                <svg class="recording-picker-arrow" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>`;
    };

    // Build picker dialog dynamically
    const backdrop = document.createElement('div');
    backdrop.className = 'db-dialog-backdrop';
    backdrop.innerHTML = `
        <div class="db-dialog" style="max-width: 520px;">
            <div class="db-dialog-header">
                <h3>Import Recording <span style="font-size:0.75rem; color:#888; font-weight:normal;">(${recordings.length} total)</span></h3>
                <button class="db-dialog-close">&times;</button>
            </div>
            <div class="db-dialog-body" style="max-height: 400px; overflow-y: auto; padding: 0;">
                <div class="recording-picker-list"></div>
                <div class="recording-picker-loadmore" style="display:none; padding:12px; text-align:center;">
                    <button class="recording-picker-loadmore-btn" style="background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.15); color:#ccc; padding:8px 20px; border-radius:6px; cursor:pointer; font-size:0.85rem;">Load more</button>
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(backdrop);
    requestAnimationFrame(() => backdrop.classList.add('visible'));

    const listEl = backdrop.querySelector('.recording-picker-list');
    const loadMoreEl = backdrop.querySelector('.recording-picker-loadmore');
    const loadMoreBtn = backdrop.querySelector('.recording-picker-loadmore-btn');
    const scrollEl = backdrop.querySelector('.db-dialog-body');

    const renderNextPage = () => {
        const next = recordings.slice(renderedCount, renderedCount + PAGE_SIZE);
        listEl.insertAdjacentHTML('beforeend', next.map(renderItem).join(''));
        renderedCount += next.length;
        loadMoreEl.style.display = renderedCount < recordings.length ? '' : 'none';
    };
    renderNextPage();

    loadMoreBtn.addEventListener('click', renderNextPage);

    // Auto-load more when scrolled near bottom
    scrollEl.addEventListener('scroll', () => {
        if (scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 100
            && renderedCount < recordings.length) {
            renderNextPage();
        }
    });

    const close = () => {
        backdrop.classList.remove('visible');
        setTimeout(() => backdrop.remove(), 200);
    };

    backdrop.querySelector('.db-dialog-close').addEventListener('click', close);
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });

    // Handle recording selection (event delegation — works for items added later)
    listEl.addEventListener('click', async (e) => {
        const btn = e.target.closest('.recording-picker-item');
        if (!btn || btn.disabled) return;
        const stem = btn.dataset.stem;
        btn.disabled = true;
        btn.style.opacity = '0.5';
        btn.querySelector('.recording-picker-arrow').innerHTML = '<circle cx="12" cy="12" r="3" fill="currentColor"/>';

        try {
            const res = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/db/import-recording`, {
                method: 'POST',
                headers: { ...getHeaders(false), 'Content-Type': 'application/json' },
                body: JSON.stringify({ recording_stem: stem }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Import failed');
            }
            showToast('Recording imported successfully');
            close();
            await loadDbState();
            await selectDbTable('recordings');
        } catch (err) {
            showToast(err.message || 'Failed to import recording', 'error');
            btn.disabled = false;
            btn.style.opacity = '1';
        }
    });
}

// --- Table Editor ---

function showTableEditor(table) {
    editingTable = table;
    const dialog = document.getElementById('table-editor-dialog');
    const title = document.getElementById('table-editor-title');
    const keyInput = document.getElementById('table-key');
    const labelInput = document.getElementById('table-label');
    const errorEl = document.getElementById('table-editor-error');
    if (!dialog) return;

    title.textContent = table ? 'Edit Table' : 'Create Table';
    keyInput.value = table ? table.key : '';
    keyInput.disabled = !!table;  // Can't change key on edit
    labelInput.value = table ? (table.label || '') : '';
    if (errorEl) errorEl.textContent = '';

    // Render columns
    const columns = table ? [...table.columns] : [{ key: '', label: '', type: 'text', description: '' }];
    renderTableColumns(columns);

    dialog.classList.remove('hidden');
}

function hideTableEditor() {
    const dialog = document.getElementById('table-editor-dialog');
    if (dialog) dialog.classList.add('hidden');
    editingTable = null;
}

function renderTableColumns(columns) {
    const list = document.getElementById('table-columns-list');
    if (!list) return;

    // Build list of other tables for reference target dropdown
    // Exclude the currently-being-edited table to prevent self-reference
    const editingKey = document.getElementById('table-key')?.value?.trim();
    const refTargets = dbTables.filter(t => t.key !== editingKey);

    let html = `<div class="db-column-header">
        <span></span><span>Key</span><span>Label</span><span>Type</span><span></span><span></span>
    </div>`;
    columns.forEach((col, i) => {
        const isRef = col.type === 'reference';
        const isText = !col.type || col.type === 'text' || col.type === 'longtext';
        html += `
            <div class="db-column-row" data-index="${i}">
                <div class="db-col-drag-handle" draggable="true" title="Drag to reorder">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>
                </div>
                <input type="text" class="col-key" placeholder="key" value="${escapeHtml(col.key || '')}" autocomplete="off">
                <input type="text" class="col-label" placeholder="Label" value="${escapeHtml(col.label || '')}" autocomplete="off">
                <select class="col-type">
                    <option value="text" ${col.type === 'text' || !col.type ? 'selected' : ''}>Text</option>
                    <option value="longtext" ${col.type === 'longtext' ? 'selected' : ''}>Long Text</option>
                    <option value="number" ${col.type === 'number' ? 'selected' : ''}>Number</option>
                    <option value="boolean" ${col.type === 'boolean' ? 'selected' : ''}>Boolean</option>
                    <option value="date" ${col.type === 'date' ? 'selected' : ''}>Date</option>
                    <option value="reference" ${col.type === 'reference' ? 'selected' : ''}>Link</option>
                </select>
                <div class="db-col-extra">
                    ${isText ? `
                    <label class="db-searchable-toggle" title="Mark as display column (used when other tables link to this one)">
                        <input type="checkbox" class="col-searchable" ${col.searchable ? 'checked' : ''}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    </label>` : ''}
                </div>
                <button type="button" class="db-remove-column-btn field-action-btn delete-field-btn" title="Remove" ${columns.length <= 1 ? 'disabled' : ''}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
                ${isRef ? `
                <div class="db-col-ref-row">
                    <select class="col-ref-table" title="Linked table">
                        <option value="">-- link to --</option>
                        ${refTargets.map(t => `<option value="${escapeHtml(t.key)}" ${col.references_table === t.key ? 'selected' : ''}>${escapeHtml(t.label || t.key)}</option>`).join('')}
                    </select>
                </div>` : ''}
            </div>
        `;
    });

    list.innerHTML = html;

    // Re-render on type change to show/hide reference picker and searchable toggle
    list.querySelectorAll('.col-type').forEach(select => {
        select.addEventListener('change', () => {
            const cols = getTableColumnsFromForm();
            renderTableColumns(cols);
        });
    });

    // Searchable checkbox: enforce at most 3 per table
    list.querySelectorAll('.col-searchable').forEach(cb => {
        cb.addEventListener('change', () => {
            if (cb.checked) {
                const checked = list.querySelectorAll('.col-searchable:checked');
                if (checked.length > 3) cb.checked = false;
            }
        });
    });

    // Auto-generate label from key
    list.querySelectorAll('.col-key').forEach(input => {
        input.addEventListener('input', () => {
            const row = input.closest('.db-column-row');
            const labelInput = row?.querySelector('.col-label');
            if (labelInput && !labelInput.dataset.manual) {
                labelInput.value = input.value
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, c => c.toUpperCase());
            }
        });
    });

    // Track manual label edits
    list.querySelectorAll('.col-label').forEach(input => {
        input.addEventListener('input', () => { input.dataset.manual = '1'; });
    });

    // Remove column
    list.querySelectorAll('.db-remove-column-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const cols = getTableColumnsFromForm();
            const idx = parseInt(btn.closest('.db-column-row').dataset.index);
            cols.splice(idx, 1);
            renderTableColumns(cols);
        });
    });

    // Drag-and-drop column reordering
    let dragSrcRow = null;

    list.querySelectorAll('.db-col-drag-handle').forEach(handle => {
        handle.addEventListener('dragstart', (e) => {
            dragSrcRow = handle.closest('.db-column-row');
            dragSrcRow.classList.add('db-col-dragging');
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', dragSrcRow.dataset.index);
        });
    });

    list.querySelectorAll('.db-column-row').forEach(row => {
        row.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (row === dragSrcRow) return;
            const midY = row.getBoundingClientRect().top + row.getBoundingClientRect().height / 2;
            row.classList.toggle('db-col-drop-above', e.clientY < midY);
            row.classList.toggle('db-col-drop-below', e.clientY >= midY);
        });

        row.addEventListener('dragleave', () => {
            row.classList.remove('db-col-drop-above', 'db-col-drop-below');
        });

        row.addEventListener('drop', (e) => {
            e.preventDefault();
            row.classList.remove('db-col-drop-above', 'db-col-drop-below');
            if (!dragSrcRow || dragSrcRow === row) return;
            const midY = row.getBoundingClientRect().top + row.getBoundingClientRect().height / 2;
            if (e.clientY < midY) {
                list.insertBefore(dragSrcRow, row);
            } else {
                list.insertBefore(dragSrcRow, row.nextSibling);
            }
            // Re-index so remove-column uses correct index
            list.querySelectorAll('.db-column-row').forEach((r, idx) => { r.dataset.index = idx; });
        });

        row.addEventListener('dragend', () => {
            if (dragSrcRow) dragSrcRow.classList.remove('db-col-dragging');
            dragSrcRow = null;
            list.querySelectorAll('.db-column-row').forEach(r => {
                r.classList.remove('db-col-drop-above', 'db-col-drop-below');
            });
        });
    });
}

function addTableColumn() {
    const cols = getTableColumnsFromForm();
    cols.push({ key: '', label: '', type: 'text', description: '' });
    renderTableColumns(cols);
}

function getTableColumnsFromForm() {
    const list = document.getElementById('table-columns-list');
    if (!list) return [];
    const cols = [];
    list.querySelectorAll('.db-column-row').forEach(row => {
        const col = {
            key: row.querySelector('.col-key')?.value?.trim() || '',
            label: row.querySelector('.col-label')?.value?.trim() || '',
            type: row.querySelector('.col-type')?.value || 'text',
            description: ''
        };
        const searchableCb = row.querySelector('.col-searchable');
        if (searchableCb?.checked) {
            col.searchable = true;
        }
        const refTable = row.querySelector('.col-ref-table');
        if (col.type === 'reference' && refTable) {
            col.references_table = refTable.value || '';
        }
        cols.push(col);
    });
    return cols;
}

async function handleSaveTable() {
    const keyInput = document.getElementById('table-key');
    const labelInput = document.getElementById('table-label');
    const errorEl = document.getElementById('table-editor-error');
    const key = keyInput?.value?.trim() || '';
    const label = labelInput?.value?.trim() || '';

    // Validate
    if (!key) {
        if (errorEl) errorEl.textContent = 'Table key is required';
        return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
        if (errorEl) errorEl.textContent = 'Key must be lowercase, start with letter, and use only a-z, 0-9, _';
        return;
    }

    const columns = getTableColumnsFromForm().filter(c => c.key);
    if (columns.length === 0) {
        if (errorEl) errorEl.textContent = 'At least one column is required';
        return;
    }

    // Check for duplicate column keys
    const colKeys = columns.map(c => c.key);
    if (new Set(colKeys).size !== colKeys.length) {
        if (errorEl) errorEl.textContent = 'Column keys must be unique';
        return;
    }

    // Validate reference columns have a target selected
    for (const c of columns) {
        if (c.type === 'reference' && !c.references_table) {
            if (errorEl) errorEl.textContent = `Link column "${c.label || c.key}" must select a linked table`;
            return;
        }
    }

    // Validate at most 3 searchable columns
    if (columns.filter(c => c.searchable).length > 3) {
        if (errorEl) errorEl.textContent = 'At most 3 columns can be marked as display columns';
        return;
    }

    try {
        const headers = getHeaders();

        const body = { key, label: label || key, columns };
        const url = editingTable
            ? `/agents/${encodeURIComponent(selectedAgent)}/schema/tables/${encodeURIComponent(editingTable.key)}`
            : `/agents/${encodeURIComponent(selectedAgent)}/schema/tables`;
        const method = editingTable ? 'PUT' : 'POST';

        const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.detail || 'Failed to save table');
        }

        hideTableEditor();
        await loadDbState();

        // Auto-select the new/edited table
        dbSelectedTable = key;
        await selectDbTable(key);
    } catch (error) {
        if (errorEl) errorEl.textContent = error.message;
    }
}

async function handleDeleteTable(tableKey) {
    const table = dbTables.find(t => t.key === tableKey);
    const label = table ? (table.label || table.key) : tableKey;

    showDbConfirm(
        'Delete Table',
        `Delete table "${label}" and all its rows? This cannot be undone.`,
        async () => {
            try {
                const headers = getHeaders(false);
                const res = await fetch(
                    `/agents/${encodeURIComponent(selectedAgent)}/schema/tables/${encodeURIComponent(tableKey)}`,
                    { method: 'DELETE', headers }
                );
                if (!res.ok) throw new Error('Failed to delete table');

                if (dbSelectedTable === tableKey) {
                    dbSelectedTable = null;
                    dbTableRows = [];
                    if (elements.dbTableDetail) elements.dbTableDetail.classList.add('hidden');
                }

                await loadDbState();
            } catch (error) {
                console.error('Error deleting table:', error);
            }
        }
    );
}

// --- Row Editor ---

/**
 * Open the row editor for a row in a DIFFERENT table.
 *
 * The point is not to navigate. Following a reference by hand means leaving
 * the table you are reading, finding the row, editing it, and coming back to
 * a view that has forgotten your sort, your page and your scroll position.
 * This edits the related row in place and returns you to exactly where you
 * were, with the display names re-resolved in case the edit changed one.
 */
async function openRelatedRowEditor(tableKey, rowId) {
    const def = dbTables.find(t => t.key === tableKey);
    if (!def) {
        showToast('That table is no longer part of this agent');
        return;
    }
    if (!Number.isInteger(rowId)) {
        showToast('That reference does not point at a row');
        return;
    }
    try {
        const res = await fetch(
            `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(tableKey)}`,
            { headers: getHeaders(false) }
        );
        if (!res.ok) throw new Error(`Could not open ${def.label || tableKey} (${res.status})`);
        const data = await res.json();
        const row = (data.rows || []).find(r => r.id === rowId);
        // A reference can outlive the row it points at: the target may have
        // been deleted since this table was last resolved. Say so rather than
        // opening a blank editor that would create a NEW row on save.
        if (!row) {
            showToast(`Row #${rowId} is no longer in ${def.label || tableKey}`);
            return;
        }
        showRowEditor(row, def);
    } catch (err) {
        showToast(err.message || 'Could not open that row');
    }
}

function showRowEditor(row, tableDef) {
    editingDbRow = row;
    editingDbTableKey = tableDef.key;
    const dialog = document.getElementById('row-editor-dialog');
    const title = document.getElementById('row-editor-title');
    const form = document.getElementById('row-editor-form');
    const errorEl = document.getElementById('row-editor-error');
    if (!dialog || !form) return;

    title.textContent = row ? `Edit Row #${row.id}` : 'Add Row';
    if (errorEl) errorEl.textContent = '';

    const columns = tableDef.columns || [];
    const rowData = row ? (row.row_data || {}) : {};

    let html = '';
    for (const col of columns) {
        const val = rowData[col.key] !== undefined ? rowData[col.key] : '';
        const inputId = `row-field-${col.key}`;

        const isWide = col.type === 'longtext' || (col.type === 'reference' && col.references_table);
        html += `<div class="field-row ${isWide ? 'db-row-field-wide' : 'db-row-field'}">`;
        html += `<label for="${inputId}">${escapeHtml(col.label || col.key)}</label>`;

        if (col.type === 'reference' && col.references_table) {
            const refId = val !== undefined && val !== null && val !== '' ? val : '';
            html += `<div class="db-ref-field" data-ref-table="${escapeHtml(col.references_table)}">`;
            html += `<input type="hidden" class="db-ref-value" data-col-key="${escapeHtml(col.key)}" data-col-type="reference" value="${escapeHtml(String(refId))}">`;
            html += `<div class="db-ref-input-row">`;
            html += `<input type="text" class="db-ref-search" placeholder="Search ${escapeHtml(col.label || col.key)}..." autocomplete="off">`;
            html += `<button type="button" class="db-ref-clear btn-secondary" title="Clear" style="display:${refId ? 'inline-flex' : 'none'}">&times;</button>`;
            html += `</div>`;
            html += `<div class="db-ref-dropdown hidden"></div>`;
            html += `<div class="db-ref-display">${refId ? 'Loading...' : '<span class="db-ref-none">None</span>'}</div>`;
            html += `</div>`;
        } else if (col.type === 'boolean') {
            html += `<label class="setting-toggle compact">
                <input type="checkbox" id="${inputId}" data-col-key="${escapeHtml(col.key)}" data-col-type="boolean" ${val ? 'checked' : ''}>
                <span class="toggle-slider"></span>
                <span class="toggle-label"><strong>${escapeHtml(col.label || col.key)}</strong></span>
            </label>`;
        } else if (col.type === 'number') {
            html += `<input type="number" id="${inputId}" data-col-key="${escapeHtml(col.key)}" data-col-type="number" value="${escapeHtml(String(val))}" autocomplete="off">`;
        } else if (col.type === 'date') {
            html += `<input type="date" id="${inputId}" data-col-key="${escapeHtml(col.key)}" data-col-type="date" value="${escapeHtml(String(val))}" autocomplete="off">`;
        } else if (col.type === 'longtext') {
            html += `<textarea id="${inputId}" data-col-key="${escapeHtml(col.key)}" data-col-type="longtext" rows="4" autocomplete="off">${escapeHtml(String(val))}</textarea>`;
        } else {
            html += `<input type="text" id="${inputId}" data-col-key="${escapeHtml(col.key)}" data-col-type="text" value="${escapeHtml(String(val))}" autocomplete="off">`;
        }
        html += `</div>`;
    }

    form.innerHTML = html;

    // Initialize reference field dropdowns
    form.querySelectorAll('.db-ref-field').forEach(field => {
        initReferenceField(field);
    });

    dialog.classList.remove('hidden');
}

async function initReferenceField(field) {
    const refTable = field.dataset.refTable;
    const hiddenInput = field.querySelector('.db-ref-value');
    const searchInput = field.querySelector('.db-ref-search');
    const dropdown = field.querySelector('.db-ref-dropdown');
    const displayEl = field.querySelector('.db-ref-display');
    const clearBtn = field.querySelector('.db-ref-clear');

    // If there's an existing value, resolve it to display name
    const currentId = hiddenInput.value;
    if (currentId) {
        try {
            const headers = getHeaders(true);
            const res = await fetch(
                `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(refTable)}/resolve`,
                { method: 'POST', headers,
                  body: JSON.stringify({ ids: [parseInt(currentId)] }) }
            );
            if (res.ok) {
                const data = await res.json();
                const name = data.resolved?.[currentId] || `#${currentId}`;
                displayEl.textContent = name;
            } else {
                displayEl.textContent = `#${currentId}`;
            }
        } catch (e) {
            displayEl.textContent = `#${currentId}`;
        }
    }

    // Search state
    let debounceTimer;
    let abortController = null;

    // Debounced search on input (min 2 chars)
    searchInput.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        const q = searchInput.value.trim();
        if (q.length < 2) {
            dropdown.classList.add('hidden');
            return;
        }
        debounceTimer = setTimeout(() => searchReferenceRows(field, abortController = new AbortController()), 250);
    });

    // Escape to close dropdown
    searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            dropdown.classList.add('hidden');
            searchInput.blur();
        }
    });

    // Close dropdown on outside click
    const closeHandler = (e) => {
        if (!field.contains(e.target)) {
            dropdown.classList.add('hidden');
        }
    };
    document.addEventListener('click', closeHandler);
    field._closeHandler = closeHandler;

    // Clear button
    clearBtn.addEventListener('click', () => {
        hiddenInput.value = '';
        searchInput.value = '';
        displayEl.innerHTML = '<span class="db-ref-none">None</span>';
        clearBtn.style.display = 'none';
        dropdown.classList.add('hidden');
    });
}

async function searchReferenceRows(field, abortController) {
    const refTable = field.dataset.refTable;
    const searchInput = field.querySelector('.db-ref-search');
    const dropdown = field.querySelector('.db-ref-dropdown');
    const hiddenInput = field.querySelector('.db-ref-value');
    const displayEl = field.querySelector('.db-ref-display');
    const clearBtn = field.querySelector('.db-ref-clear');
    const q = searchInput.value.trim();

    // Cancel any previous in-flight request
    if (field._prevAbort) field._prevAbort.abort();
    field._prevAbort = abortController;

    try {
        const headers = getHeaders(false);

        const res = await fetch(
            `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(refTable)}/search?q=${encodeURIComponent(q)}&limit=10`,
            { headers, signal: abortController?.signal }
        );
        if (!res.ok) {
            if (res.status === 400) {
                dropdown.innerHTML = '<div class="db-ref-no-results">No display column set on target table</div>';
                dropdown.classList.remove('hidden');
            }
            return;
        }
        const data = await res.json();

        if (data.results.length === 0) {
            dropdown.innerHTML = '<div class="db-ref-no-results">No matches</div>';
        } else {
            dropdown.innerHTML = data.results.map(r =>
                `<div class="db-ref-option" data-id="${r.id}">${escapeHtml(r.display)} <span class="db-ref-id">#${r.id}</span></div>`
            ).join('');
        }
        dropdown.classList.remove('hidden');

        // Click handlers for options
        dropdown.querySelectorAll('.db-ref-option').forEach(opt => {
            opt.addEventListener('click', () => {
                hiddenInput.value = opt.dataset.id;
                displayEl.textContent = opt.textContent.replace(/ #\d+$/, '').trim();
                searchInput.value = '';
                clearBtn.style.display = 'inline-flex';
                dropdown.classList.add('hidden');
            });
        });
    } catch (e) {
        if (e.name !== 'AbortError') console.error('Reference search error:', e);
    }
}

function hideRowEditor() {
    const dialog = document.getElementById('row-editor-dialog');
    if (dialog) dialog.classList.add('hidden');
    editingDbRow = null;
    editingDbTableKey = null;
}

async function handleSaveRow() {
    const form = document.getElementById('row-editor-form');
    const errorEl = document.getElementById('row-editor-error');
    if (!form) return;
    if (errorEl) errorEl.textContent = '';
    form.querySelectorAll('.db-row-field--invalid').forEach(el => el.classList.remove('db-row-field--invalid'));

    // Collect data from form + validate. Empty strings are ALLOWED on text/longtext
    // columns (they're nullable). We only flag truly invalid input (unparseable
    // number or date, malformed reference id).
    const data = {};
    const invalidFields = [];
    form.querySelectorAll('[data-col-key]').forEach(input => {
        const key = input.dataset.colKey;
        const type = input.dataset.colType;
        if (Object.prototype.hasOwnProperty.call(data, key)) return;  // skip duplicates (hidden ref pairs)
        const raw = input.value;
        if (type === 'boolean') {
            data[key] = input.checked;
        } else if (type === 'number') {
            if (raw === '' || raw == null) {
                data[key] = null;
            } else {
                const n = parseFloat(raw);
                if (Number.isNaN(n)) {
                    invalidFields.push({ key, label: _fieldLabel(input), reason: 'must be a number' });
                } else {
                    data[key] = n;
                }
            }
        } else if (type === 'reference') {
            if (raw === '' || raw == null) {
                data[key] = null;
            } else {
                const n = parseInt(raw, 10);
                if (Number.isNaN(n)) {
                    invalidFields.push({ key, label: _fieldLabel(input), reason: 'invalid reference' });
                } else {
                    data[key] = n;
                }
            }
        } else if (type === 'date') {
            // Browser date input enforces format; empty is allowed.
            data[key] = raw;
        } else {
            // text / longtext: empty is fine, store as-is
            data[key] = raw;
        }
    });

    if (invalidFields.length) {
        // Mark the offending fields visually and summarize in the error slot.
        invalidFields.forEach(f => {
            const el = form.querySelector(`[data-col-key="${CSS.escape(f.key)}"]`);
            el?.closest('.field-row')?.classList.add('db-row-field--invalid');
        });
        if (errorEl) {
            errorEl.textContent =
                invalidFields.length === 1
                    ? `${invalidFields[0].label}: ${invalidFields[0].reason}`
                    : `${invalidFields.length} fields invalid: ` +
                      invalidFields.map(f => `${f.label} (${f.reason})`).join(', ');
        }
        return;  // Do NOT submit until the user fixes the inputs
    }

    try {
        const headers = getHeaders();
        // The row's own table, which is only usually the one on screen.
        const saveTable = editingDbTableKey || dbSelectedTable;
        const url = editingDbRow
            ? `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(saveTable)}/${editingDbRow.id}`
            : `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(saveTable)}`;
        const method = editingDbRow ? 'PUT' : 'POST';

        const res = await fetch(url, { method, headers, body: JSON.stringify({ data }) });
        if (!res.ok) {
            // Try to surface the actual server-side error rather than a generic message.
            let detail = `${method === 'POST' ? 'Add' : 'Update'} failed (${res.status})`;
            try {
                const body = await res.json();
                if (body && body.detail) detail = String(body.detail);
            } catch (_) {
                try {
                    const text = await res.text();
                    if (text) detail = text.slice(0, 200);
                } catch (__) {}
            }
            throw new Error(detail);
        }

        // Capture intent before hideRowEditor() clears editingDbRow.
        const wasEdit = !!editingDbRow;
        hideRowEditor();
        showToast(wasEdit ? 'Row updated' : 'Row added');
        await selectDbTable(dbSelectedTable);
    } catch (error) {
        // Keep the dialog open on failure so the user can read the message + retry.
        if (errorEl) errorEl.textContent = error.message || 'Save failed';
    }
}

function _fieldLabel(inputEl) {
    // Walk to the enclosing .field-row's <label> for a friendly name; fall back to col_key.
    const row = inputEl.closest('.field-row');
    const label = row?.querySelector('label');
    return (label?.textContent || inputEl.dataset.colKey || 'field').trim();
}

async function handleDeleteRow(rowId) {
    showDbConfirm(
        'Delete Row',
        `Delete row #${rowId}? This cannot be undone.`,
        async () => {
            try {
                const headers = getHeaders(false);
                const res = await fetch(
                    `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(dbSelectedTable)}/${rowId}`,
                    { method: 'DELETE', headers }
                );
                if (!res.ok) throw new Error('Failed to delete row');

                await selectDbTable(dbSelectedTable);
            } catch (error) {
                console.error('Error deleting row:', error);
            }
        }
    );
}

// --- Confirm Dialog ---

function showDbConfirm(title, message, onConfirm) {
    const dialog = document.getElementById('db-confirm-dialog');
    const titleEl = document.getElementById('db-confirm-title');
    const messageEl = document.getElementById('db-confirm-message');
    const okBtn = document.getElementById('db-confirm-ok');
    if (!dialog) return;

    titleEl.textContent = title;
    messageEl.textContent = message;
    dbConfirmCallback = onConfirm;
    dialog.classList.remove('hidden');

    // Replace OK button listener to avoid stacking
    const newOk = okBtn.cloneNode(true);
    okBtn.parentNode.replaceChild(newOk, okBtn);
    newOk.addEventListener('click', async () => {
        const callback = dbConfirmCallback;
        hideDbConfirm();
        if (callback) await callback();
    });
}

function hideDbConfirm() {
    const dialog = document.getElementById('db-confirm-dialog');
    if (dialog) dialog.classList.add('hidden');
    dbConfirmCallback = null;
}

// --- Import/Export ---

function handleExportTable(format) {
    if (!selectedAgent || !dbSelectedTable) return;
    const url = `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(dbSelectedTable)}/export/${format}`;
    const headers = getHeaders(false);

    fetch(url, { headers })
        .then(res => {
            if (!res.ok) throw new Error('Export failed');
            return res.blob();
        })
        .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `${dbSelectedTable}.${format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        })
        .catch(err => console.error('Export error:', err));
}

function setImportFile(file) {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'csv' && ext !== 'xlsx') {
        const errorEl = document.getElementById('import-error');
        if (errorEl) errorEl.textContent = 'Only .csv and .xlsx files are supported';
        return;
    }
    importFile = file;
    document.getElementById('import-file-name').textContent = file.name;
    document.getElementById('import-file-info')?.classList.remove('hidden');
    document.getElementById('import-dropzone')?.classList.add('hidden');
    document.getElementById('import-dialog-submit').disabled = false;
    const errorEl = document.getElementById('import-error');
    if (errorEl) errorEl.textContent = '';
}

function showImportDialog() {
    const dialog = document.getElementById('db-import-dialog');
    if (!dialog) return;

    importFile = null;
    const fileInput = document.getElementById('import-file-input');
    if (fileInput) fileInput.value = '';

    document.getElementById('import-file-info')?.classList.add('hidden');
    document.getElementById('import-dropzone')?.classList.remove('hidden');
    const errorEl = document.getElementById('import-error');
    if (errorEl) errorEl.textContent = '';
    const submitBtn = document.getElementById('import-dialog-submit');
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Import';
    }

    // Reset to append mode
    const appendRadio = dialog.querySelector('input[name="import-mode"][value="append"]');
    if (appendRadio) appendRadio.checked = true;
    document.getElementById('import-replace-warning')?.classList.add('hidden');

    dialog.classList.remove('hidden');
}

function hideImportDialog() {
    const dialog = document.getElementById('db-import-dialog');
    if (dialog) dialog.classList.add('hidden');
    importFile = null;
}

async function handleImportSubmit() {
    if (!importFile || !selectedAgent || !dbSelectedTable) return;

    const errorEl = document.getElementById('import-error');
    const submitBtn = document.getElementById('import-dialog-submit');
    const mode = document.querySelector('input[name="import-mode"]:checked')?.value || 'append';

    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Importing...';
    }
    if (errorEl) errorEl.textContent = '';

    try {
        const formData = new FormData();
        formData.append('file', importFile);
        formData.append('mode', mode);

        const headers = getHeaders(false);

        const res = await fetch(
            `/agents/${encodeURIComponent(selectedAgent)}/db/${encodeURIComponent(dbSelectedTable)}/import`,
            { method: 'POST', headers, body: formData }
        );

        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.detail || 'Import failed');
        }

        hideImportDialog();
        await selectDbTable(dbSelectedTable);
    } catch (error) {
        if (errorEl) errorEl.textContent = error.message;
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Import';
        }
    }
}

// ─── Tool Chains CRUD ────────────────────────────────────────────

const CHAIN_TOOL_OPTIONS = [
    { value: 'rag_search', label: 'Doc Search' },
    { value: 'agent_db', label: 'Database' },
    { value: 'web_search', label: 'Web Search' },
    { value: 'web_fetch', label: 'Web Fetch' },
    { value: 'http_request', label: 'HTTP Request' },
    { value: 'email_send', label: 'Email Send' },
    { value: 'email_fetch', label: 'Email Fetch' },
    { value: 'image_generate', label: 'Image Generate' },
    { value: 'scholar', label: 'Scholar' },
    { value: 'python_exec', label: 'Python' },
    { value: 'shell_exec', label: 'Shell' },
    { value: 'file_ops', label: 'Files' },
    { value: 'calculator', label: 'Calculator' },
];

/**
 * Render the tool chains list from config array
 */
function renderToolChains(chains) {
    const list = elements.toolChainsList;
    if (!list) return;
    list.innerHTML = '';

    if (elements.toolChainsSubtitle) {
        elements.toolChainsSubtitle.textContent = `${chains.length} chain${chains.length !== 1 ? 's' : ''}`;
    }

    if (chains.length === 0) {
        list.innerHTML = '<p class="tool-chains-empty">No chains configured. Add one to create predefined tool sequences.</p>';
        return;
    }

    for (const chain of chains) {
        const row = document.createElement('div');
        row.className = 'chain-row';
        row.dataset.chainId = chain.id;

        const stepsSummary = (chain.steps || []).map(s => {
            const opt = CHAIN_TOOL_OPTIONS.find(o => o.value === s.tool_name);
            return opt ? opt.label : s.tool_name;
        }).join(' → ');

        row.innerHTML = `
            <div class="chain-row-info">
                <div class="chain-row-header">
                    <strong class="chain-row-name">${escapeHtml(chain.name || chain.id)}</strong>
                    <span class="chain-row-steps">${chain.steps?.length || 0} steps</span>
                    <label class="chain-enabled-toggle" title="Enabled">
                        <input type="checkbox" class="chain-enabled-cb" data-chain-id="${chain.id}" ${chain.enabled !== false ? 'checked' : ''}>
                        <span class="toggle-slider small"></span>
                    </label>
                </div>
                <div class="chain-row-flow">${escapeHtml(stepsSummary)}</div>
            </div>
            <div class="chain-row-actions">
                <button class="chain-edit-btn" data-chain-id="${chain.id}" title="Edit">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                </button>
                <button class="chain-delete-btn" data-chain-id="${chain.id}" title="Delete">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                </button>
                <button class="chain-confirm-delete-btn hidden" data-chain-id="${chain.id}">Delete?</button>
            </div>
        `;

        // Event listeners
        row.querySelector('.chain-edit-btn')?.addEventListener('click', () => openChainEditor(chain));
        const deleteBtn = row.querySelector('.chain-delete-btn');
        const confirmDeleteBtn = row.querySelector('.chain-confirm-delete-btn');
        let deleteTimeout = null;
        deleteBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            // First click: show confirm button, hide trash icon
            deleteBtn.classList.add('hidden');
            confirmDeleteBtn.classList.remove('hidden');
            // Auto-revert after 3 seconds if not confirmed
            clearTimeout(deleteTimeout);
            deleteTimeout = setTimeout(() => {
                confirmDeleteBtn.classList.add('hidden');
                deleteBtn.classList.remove('hidden');
            }, 3000);
        });
        confirmDeleteBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            clearTimeout(deleteTimeout);
            deleteChain(chain.id);
        });
        row.querySelector('.chain-enabled-cb')?.addEventListener('change', (e) => toggleChainEnabled(chain.id, e.target.checked));

        list.appendChild(row);
    }
}

/**
 * Open inline chain editor (create or edit)
 */
function openChainEditor(existingChain = null) {
    const list = elements.toolChainsList;
    if (!list) return;

    // Remove any existing editor
    const oldEditor = list.querySelector('.chain-editor');
    if (oldEditor) oldEditor.remove();

    const isEdit = !!existingChain;
    const chain = existingChain || { id: '', name: '', description: '', enabled: true, default_on: true, prerequisite: null, steps: [{ tool_name: 'rag_search', instruction: '' }] };

    const editor = document.createElement('div');
    editor.className = 'chain-editor';

    // Build prerequisite options
    const currentChains = agentSettings.tool_chains || [];
    const enabledTools = getEnabledTools();
    let prereqOptions = '<option value="">None (standalone)</option>';
    for (const t of enabledTools) {
        const label = CHAIN_TOOL_OPTIONS.find(o => o.value === t)?.label || t;
        const selected = chain.prerequisite?.type === 'tool' && chain.prerequisite?.name === t ? 'selected' : '';
        prereqOptions += `<option value="tool:${t}" ${selected}>After tool: ${label}</option>`;
    }
    for (const c of currentChains) {
        if (c.id !== chain.id) {
            const selected = chain.prerequisite?.type === 'chain' && chain.prerequisite?.id === c.id ? 'selected' : '';
            prereqOptions += `<option value="chain:${c.id}" ${selected}>After chain: ${c.name || c.id}</option>`;
        }
    }

    editor.innerHTML = `
        <div class="chain-editor-header">${isEdit ? 'Edit Chain' : 'New Chain'}</div>
        <div class="chain-editor-row">
            <label>ID</label>
            <input type="text" class="chain-id-input" value="${escapeHtml(chain.id)}" placeholder="e.g. character-creation" ${isEdit ? 'disabled' : ''}>
        </div>
        <div class="chain-editor-row">
            <label>Name</label>
            <input type="text" class="chain-name-input" value="${escapeHtml(chain.name || '')}">
        </div>
        <div class="chain-editor-row">
            <label>Description</label>
            <textarea class="chain-desc-input" rows="2" placeholder="When should the LLM use this chain?">${escapeHtml(chain.description || '')}</textarea>
        </div>
        <div class="chain-editor-row-pair">
            <label class="chain-toggle-label">
                <input type="checkbox" class="chain-enabled-input" ${chain.enabled !== false ? 'checked' : ''}>
                Enabled
            </label>
            <label class="chain-toggle-label">
                <input type="checkbox" class="chain-default-on-input" ${chain.default_on !== false ? 'checked' : ''}>
                Default On
            </label>
        </div>
        <div class="chain-editor-row">
            <label>Prerequisite</label>
            <select class="chain-prereq-input">${prereqOptions}</select>
        </div>
        <div class="chain-editor-steps-header">
            <label>Steps</label>
            <button type="button" class="chain-add-step-btn">+ Step</button>
        </div>
        <div class="chain-steps-list"></div>
        <div class="chain-editor-actions">
            <button type="button" class="chain-cancel-btn btn-secondary">Cancel</button>
            <button type="button" class="chain-save-btn kb-create-btn">${isEdit ? 'Update' : 'Create'}</button>
        </div>
        <div class="chain-editor-error"></div>
    `;

    list.appendChild(editor);

    // Populate steps
    const stepsList = editor.querySelector('.chain-steps-list');
    for (const step of chain.steps) {
        addStepRow(stepsList, step);
    }

    // Add step button
    editor.querySelector('.chain-add-step-btn')?.addEventListener('click', () => {
        addStepRow(stepsList, { tool_name: 'rag_search', instruction: '' });
    });

    // Cancel
    editor.querySelector('.chain-cancel-btn')?.addEventListener('click', () => editor.remove());

    // Save
    editor.querySelector('.chain-save-btn')?.addEventListener('click', () => saveChain(editor, isEdit ? chain.id : null));

    // Auto-generate ID from name for new chains
    if (!isEdit) {
        const nameInput = editor.querySelector('.chain-name-input');
        const idInput = editor.querySelector('.chain-id-input');
        nameInput?.addEventListener('input', () => {
            if (!idInput.dataset.manualEdit) {
                idInput.value = nameInput.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            }
        });
        idInput?.addEventListener('input', () => { idInput.dataset.manualEdit = '1'; });
    }

    // Scroll to editor
    editor.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * Add a step row to the steps list container
 */
function addStepRow(container, step) {
    const row = document.createElement('div');
    row.className = 'chain-step-row';

    let toolOptions = '';
    for (const opt of CHAIN_TOOL_OPTIONS) {
        toolOptions += `<option value="${opt.value}" ${step.tool_name === opt.value ? 'selected' : ''}>${opt.label}</option>`;
    }

    row.innerHTML = `
        <span class="chain-step-num">${container.children.length + 1}</span>
        <select class="chain-step-tool">${toolOptions}</select>
        <input type="text" class="chain-step-instruction" value="${escapeHtml(step.instruction || '')}" placeholder="Instruction for this step...">
        <button type="button" class="chain-step-remove" title="Remove step">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
    `;

    row.querySelector('.chain-step-remove')?.addEventListener('click', () => {
        row.remove();
        renumberSteps(container);
    });

    container.appendChild(row);
}

function renumberSteps(container) {
    container.querySelectorAll('.chain-step-row').forEach((row, i) => {
        const numEl = row.querySelector('.chain-step-num');
        if (numEl) numEl.textContent = i + 1;
    });
}

/**
 * Save chain (create or update)
 */
async function saveChain(editorEl, existingId) {
    const errorEl = editorEl.querySelector('.chain-editor-error');
    const saveBtn = editorEl.querySelector('.chain-save-btn');

    const id = editorEl.querySelector('.chain-id-input')?.value?.trim();
    const name = editorEl.querySelector('.chain-name-input')?.value?.trim();
    const description = editorEl.querySelector('.chain-desc-input')?.value?.trim();
    const enabled = editorEl.querySelector('.chain-enabled-input')?.checked ?? true;
    const defaultOn = editorEl.querySelector('.chain-default-on-input')?.checked ?? true;

    // Parse prerequisite
    const prereqValue = editorEl.querySelector('.chain-prereq-input')?.value || '';
    let prerequisite = null;
    if (prereqValue.startsWith('tool:')) {
        prerequisite = { type: 'tool', name: prereqValue.slice(5) };
    } else if (prereqValue.startsWith('chain:')) {
        prerequisite = { type: 'chain', id: prereqValue.slice(6) };
    }

    // Collect steps
    const stepRows = editorEl.querySelectorAll('.chain-step-row');
    const steps = [];
    for (const row of stepRows) {
        const toolName = row.querySelector('.chain-step-tool')?.value;
        const instruction = row.querySelector('.chain-step-instruction')?.value?.trim() || '';
        if (toolName) steps.push({ tool_name: toolName, instruction });
    }

    // Validation
    if (!id || !/^[a-z0-9-]+$/.test(id)) {
        if (errorEl) errorEl.textContent = 'ID must be lowercase with hyphens (e.g. character-creation)';
        return;
    }
    if (steps.length === 0) {
        if (errorEl) errorEl.textContent = 'At least one step is required';
        return;
    }

    const body = { id, name: name || id, description, enabled, default_on: defaultOn, prerequisite, steps };

    try {
        if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving...'; }

        const headers = getHeaders();

        const url = existingId
            ? `/agents/${encodeURIComponent(selectedAgent)}/chains/${encodeURIComponent(existingId)}`
            : `/agents/${encodeURIComponent(selectedAgent)}/chains`;
        const method = existingId ? 'PUT' : 'POST';

        const resp = await fetch(url, { method, headers, body: JSON.stringify(body) });
        if (!resp.ok) {
            const data = await resp.json();
            throw new Error(data.detail || 'Failed to save chain');
        }

        // Refresh chains from settings
        const settingsResp = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/settings`, { headers });
        const settingsData = await settingsResp.json();
        agentSettings.tool_chains = settingsData.settings?.tool_chains || [];
        renderToolChains(agentSettings.tool_chains);

    } catch (error) {
        if (errorEl) errorEl.textContent = error.message;
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = existingId ? 'Update' : 'Create'; }
    }
}

/**
 * Delete a chain
 */
async function deleteChain(chainId) {
    try {
        const headers = getHeaders();

        const resp = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/chains/${encodeURIComponent(chainId)}`, {
            method: 'DELETE', headers
        });
        if (!resp.ok) {
            const data = await resp.json();
            throw new Error(data.detail || 'Failed to delete chain');
        }

        // Refresh
        agentSettings.tool_chains = (agentSettings.tool_chains || []).filter(c => c.id !== chainId);
        renderToolChains(agentSettings.tool_chains);
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Toggle chain enabled state
 */
async function toggleChainEnabled(chainId, enabled) {
    try {
        const headers = getHeaders();

        const resp = await fetch(`/agents/${encodeURIComponent(selectedAgent)}/chains/${encodeURIComponent(chainId)}`, {
            method: 'PUT', headers, body: JSON.stringify({ enabled })
        });
        if (!resp.ok) throw new Error('Failed to update chain');

        // Update local state
        const chain = (agentSettings.tool_chains || []).find(c => c.id === chainId);
        if (chain) chain.enabled = enabled;
        if (elements.toolChainsSubtitle) {
            const total = (agentSettings.tool_chains || []).length;
            elements.toolChainsSubtitle.textContent = `${total} chain${total !== 1 ? 's' : ''}`;
        }
    } catch (error) {
        alert(error.message);
    }
}
